import "@fontsource/fira-code/latin-400.css";
import "@fontsource/fira-code/latin-700.css";
import "@fontsource/ibm-plex-mono/latin-400.css";
import "@fontsource/ibm-plex-mono/latin-400-italic.css";
import "@fontsource/ibm-plex-mono/latin-700.css";
import "@fontsource/jetbrains-mono/latin-400.css";
import "@fontsource/jetbrains-mono/latin-400-italic.css";
import "@fontsource/jetbrains-mono/latin-700.css";
import "@fontsource/source-code-pro/latin-400.css";
import "@fontsource/source-code-pro/latin-400-italic.css";
import "@fontsource/source-code-pro/latin-700.css";
import "@webtui/css/components/spinner.css";
import "@webtui/css/components/table.css";
import "@webtui/css/utils/box.css";
import guideMarkdown from "./Guide.md?raw";
import { fileTreeRows, type FileTreeRow } from "./file-tree";
import {
  MarkdownEditor,
  isEditorMode,
  setSpellChecker,
  type EditorMode,
  type EditorPlace,
} from "./markdown-editor";
import type { SpellChecker, SpellingWord } from "./spelling-words";
import { filterAppCommands, type AppCommandId } from "./app-commands";
import {
  AI_REASONING,
  AI_REASONING_LABELS,
  DEFAULT_AI_APPLY,
  DEFAULT_AI_MODEL,
  DEFAULT_AI_REASONING,
  MAX_AI_TEXT_LENGTH,
  aiSelectionContext,
  cleanAiAnswer,
  cleanAiInsertion,
  filterAiModels,
  isAiApplyMode,
  isAiModelId,
  isAiReasoning,
  type AiModel,
  type AiScope,
  type AiStreamEvent,
} from "./ai";
import { aiChanges } from "./ai-changes";
import { type MarkdownFormat } from "./editor-formatting";
import { dailyNoteName, formatToday, isDateFormat, isTimeFormat, timedNoteName } from "./date-time";
import {
  monthGrid,
  monthLabel,
  sameDay,
  shiftDay,
  shiftMonth,
  WEEKDAY_LABELS,
} from "./calendar";
import { calculate } from "./calculator";
import { findHeading, headingAtLine, headingSection, outlineHeadings, shiftSection } from "./outline";
import { closeContextMenu, inContextMenu, showContextMenu, type MenuEntry } from "./context-menu";
import {
  DEFAULT_TIMER_MINUTES,
  TIMER_PRESETS,
  formatRemaining,
  remainingAt,
  timerBar,
} from "./timer";
import {
  SIDEBAR_WIDGETS,
  moveWidgetBefore,
  sidebarWidget,
  widgetCloseId,
  widgetDropTarget,
  widgetHandleId,
  widgetList,
  widgetOrderFrom,
  widgetPanelId,
  widgetPreferenceKey,
  widgetToggleId,
  type SidebarWidgetId,
  type WidgetBox,
} from "./sidebar-widgets";
import { notePathFromLink, parseEditorLink, type EditorLink } from "./editor-links";
import { filterWikiLinkTitles } from "./wiki-link-completion";
import { filterMentions, parseMentions, rememberMentions } from "./mentions";
import {
  FOLDER_ICONS,
  MAX_CUSTOM_ICONS,
  PENCIL_ROWS,
  customIconId,
  customIconRef,
  emojiIconRef,
  emojiOf,
  iconElement,
  isEmoji,
  iconLabel,
  newCustomIconId,
  pixelIcon,
  rasterizeIcon,
} from "./folder-icons";
import {
  clampPanePercentage,
  closeLayout,
  layoutPanes,
  minimumPaneShare,
  nextPane,
  paneBeside,
  parsePaneLayout,
  resizeSplit,
  snapShare,
  splitLayout,
  type PaneLayout,
  type PaneSplit,
  type SplitDirection,
} from "./editor-panes";
import {
  loadAppearance,
  isThemeId,
  isFontId,
  THEME_STORAGE_KEY,
  FONT_STORAGE_KEY,
  THEME_COLORS,
  DEFAULT_FONT,
  type ThemeId,
} from "./appearance";
import {
  loadMarkdownBodySize,
  loadMarkdownHeadingSizes,
  normalizeMarkdownBodySize,
  setMarkdownHeadingSize,
  isMarkdownHeadingLevel,
  MARKDOWN_BODY_SIZE_STORAGE_KEY,
  MARKDOWN_HEADING_SIZE_STORAGE_KEY,
  defaultMarkdownHeadingSizes,
  DEFAULT_MARKDOWN_BODY_SIZE,
} from "./markdown-heading-sizes";
import { DocumentStore, draftPending } from "./vault/store";
import { ApiClient } from "./vault/api";
import { AccountDataSync } from "./vault/account-data";
import { VaultSync } from "./vault/sync";
import { guardOf, pathRevision, differences, filesOf, untitledNotePath } from "./vault/database";
import { defaultMetadata } from "./vault/metadata";
import {
  titleOf,
  folderOf,
  folderColor,
  resolveWikiLink,
  notePath,
  relativePath,
  assertDestination,
  under,
  isNote,
  foldersFromPaths,
  encryptedFolderOf,
  keyFolders,
  noteExtension,
  noteStem,
  moveProblem,
  folderProblem,
  movedInto,
} from "./vault/paths";
import {
  absent,
  present,
  ARCHIVE_ROOT,
  FOLDER_KEY_NAME,
  MAX_VAULT_BYTES,
  MAX_VAULT_FILES,
  fileByteLimit,
  fileByteSize,
  isAgeFile,
  isAttachment,
  isEncryptedAttachment,
  isEncryptedFile,
  isEncryptedNote,
  isFolderKey,
  sameTarget,
  VaultError,
  type Conflict,
  type Connection,
  type FileState,
  type HistoryEntry,
  type RepositoryChoice,
  type Snapshot,
  type Guard,
} from "./vault/model";
import {
  attachmentMime,
  attachmentReference,
  encodeAttachment,
  newAttachmentPath,
  resolveAttachmentPath,
} from "./vault/attachments";
import { imageMarkdown, imageMarkdownForUrl } from "./markdown-image";
import { FIGLET_FONTS, FIGLET_SAMPLE, figletBlock, type FigletFontId } from "./figlet-fonts";
import { readEditorDiagramPalette, renderMermaidSvg } from "./mermaid-render";
import { setMermaidSvg } from "./mermaid-layout";
import {
  loadMermaidSettings,
  isMermaidAlign,
  isMermaidBorder,
  isMermaidTheme,
  isMermaidWidth,
  MERMAID_ALIGN_STORAGE_KEY,
  MERMAID_BORDER_STORAGE_KEY,
  MERMAID_THEME_STORAGE_KEY,
  MERMAID_WIDTH_STORAGE_KEY,
} from "./mermaid-settings";
import {
  loadImageSettings,
  isImageAlign,
  isImageBorder,
  IMAGE_ALIGN_STORAGE_KEY,
  IMAGE_BORDER_STORAGE_KEY,
} from "./image-settings";
import {
  loadTableSettings,
  isTableAlign,
  isTablePadding,
  isTablePosition,
  isTableWidth,
  TABLE_ALIGN_STORAGE_KEY,
  TABLE_PADDING_STORAGE_KEY,
  TABLE_POSITION_STORAGE_KEY,
  TABLE_WIDTH_STORAGE_KEY,
} from "./table-settings";
import { loadCodeBlockSettings, CODE_WORD_WRAP_STORAGE_KEY } from "./code-block-settings";
import { createFolderKey, MIN_PASSPHRASE_LENGTH } from "./vault/encryption";
import {
  ENCRYPTION_BLUR_LOCK_STORAGE_KEY,
  ENCRYPTION_LOCK_STORAGE_KEY,
  isLockMinutes,
  loadLockMinutes,
  loadLockOnBlur,
  lockDue,
} from "./encryption-settings";

const el = <T extends HTMLElement = HTMLElement>(id: string): T => {
  const e = document.getElementById(id);
  if (!e) throw new Error(`Missing #${id}`);
  return e as T;
};
const themePickerGroups = [
  {
    label: "Light",
    themes: [
      ["ms-edit-light", "MS Edit Light"],
      ["catppuccin-latte", "Catppuccin Latte"],
      ["tokyo-night-day", "Tokyo Night Day"],
      ["gruvbox-light", "Gruvbox Light"],
      ["solarized-light", "Solarized Light"],
    ],
  },
  {
    label: "Dark & retro",
    themes: [
      ["dark", "Dark Mode"],
      ["catppuccin-mocha", "Catppuccin Mocha"],
      ["tokyo-night-moon", "Tokyo Night Moon"],
      ["solarized", "Solarized Dark"],
      ["retro-blue", "Retro Blue"],
      ["amber", "Amber Terminal"],
      ["green", "Green Phosphor"],
    ],
  },
] as const satisfies readonly { label: string; themes: readonly (readonly [ThemeId, string])[] }[];
let accountData: AccountDataSync | undefined;
const pref = {
  getItem(key: string) {
    try {
      return localStorage.getItem(key);
    } catch {
      return null;
    }
  },
  setItem(key: string, value: string) {
    try {
      localStorage.setItem(key, value);
      accountData?.record(key, value);
    } catch {}
  },
};
const read = (key: string) => pref.getItem("turbonotes2-" + key);
const write = (key: string, value: string) =>
  pref.setItem("turbonotes2-" + key, value);
// The open notes and the pane split are remembered only for the tab, in sessionStorage,
// so a second window never inherits another one's view. Clear what older versions stored.
for (const key of ["pane-layout", "last-note"]) {
  try {
    localStorage.removeItem("turbonotes2-" + key);
  } catch {}
}
function slashTodayText(): string {
  const dateFormat = read("slash-date-format");
  const timeFormat = read("slash-time-format");
  return formatToday(
    new Date(),
    isDateFormat(dateFormat) ? dateFormat : "mdy",
    isTimeFormat(timeFormat) ? timeFormat : "none",
  );
}
/** The people you have @mentioned, most recent first. They sync with the account, not the vault. */
const mentions = () => parseMentions(read("mentions"));
function remember(names: readonly string[]) {
  const list = mentions();
  const next = rememberMentions(list, names);
  if (JSON.stringify(next) !== JSON.stringify(list)) write("mentions", JSON.stringify(next));
}
function clearMentions() {
  const count = mentions().length;
  if (!count) {
    toast("No @mentions are remembered.");
    return;
  }
  write("mentions", "[]");
  toast(`Forgot ${count} @mention${count === 1 ? "" : "s"}.`);
}
const store = new DocumentStore(),
  api = new ApiClient(),
  sync = new VaultSync(store, api);
const screen = el("screen");
// Folder paths cannot start with /, so this cannot collide with a real folder.
const NEW_FOLDER_OPTION = "/";
const DAILY_NOTE_FOLDER = "daily";
const SCRATCH_FOLDER = "scratch";
const JOURNAL_FOLDER = "journal";
let mode: EditorMode = isEditorMode(read("editor-mode"))
  ? (read("editor-mode") as EditorMode)
  : "source";
let lineNumbers = read("line-numbers") === "true";
let wordWrap = read("word-wrap") !== "false";
let spellCheck = read("spell-check") !== "false";
let panesReady = false;
let activePane = 0,
  modalPane = 0,
  editingPath: string | null = null,
  selection = 0,
  palettePaths: string[] | null = null;
let repositories: RepositoryChoice[] = [];
let conflict: Conflict | null = null;
let conflictGuard: Guard | undefined;
let historical: { path: string; file: FileState; pane: number } | null = null;
let toastTimer: ReturnType<typeof setTimeout> | undefined;
let folderBusy = false,
  folderCompleted = false,
  fileSaveBusy = false;
let treeSelection = "",
  treePane = 0,
  treeRows: FileTreeRow[] = [];
const collapsedFolders = new Set<string>();
// The Library opens on the folder tree unless this browser last chose the note list.
let libraryTreeVisible = read("library-tree") !== "false",
  libraryTreeSelection = "",
  libraryTreeRows: FileTreeRow[] = [];
// The tree opens with every folder closed; these are the ones opened since.
const libraryOpenFolders = new Set<string>([""]);
// Folders a search opened to show its hits: the Library's own search and Find (⌘K). They
// close again when the search changes or ends, leaving the tree as it was.
const searchOpenFolders = { library: new Set<string>(), palette: new Set<string>() };
const libraryFolderOpen = (path: string) =>
  libraryOpenFolders.has(path) || searchOpenFolders.library.has(path) || searchOpenFolders.palette.has(path);
/** The folders that hold these paths, all the way up. */
function foldersAbove(paths: Iterable<string>): Set<string> {
  const above = new Set<string>();
  for (const path of paths) for (let folder = folderOf(path); folder; folder = folderOf(folder)) above.add(folder);
  return above;
}
// The icon each folder form has chosen, and an image uploaded there that isn't saved yet.
const iconChoices = new Map<string, { icon: string | null; upload?: { id: string; data: string } }>();
let iconUploadPicker = "",
  editingFolder = "";
// The month on show and the day the keyboard is on. Neither is remembered: the calendar
// opens on the current month, the way the editor opens on a blank note.
let calendarCursor = new Date();
let calendarView = {
  year: calendarCursor.getFullYear(),
  month: calendarCursor.getMonth(),
};
// How the stack is arranged. The order and which widgets are folded belong to this
// browser, like the rest of the view state.
let widgetOrder = widgetOrderFrom(read("sidebar-order"));
const widgetFolded = new Set<SidebarWidgetId>(widgetList(read("sidebar-folded")));
let widgetDragEnded = false;
let outlineLine = 1;
let outlineDrawn = "";
let timerMinutes = DEFAULT_TIMER_MINUTES;
let timerRemaining = DEFAULT_TIMER_MINUTES * 60;
let timerEndsAt: number | null = null;
let timerTicker: ReturnType<typeof setInterval> | undefined;
const CALCULATOR_TAPE_LIMIT = 20;
let calculatorAnswer: number | undefined;
let calculatorTape: { expression: string; text: string }[] = [];
let bodySize = loadMarkdownBodySize(pref),
  headingSizes = loadMarkdownHeadingSizes(pref);
let tableSettings = loadTableSettings(pref);
let codeBlockSettings = loadCodeBlockSettings(pref);
let imageSettings = loadImageSettings(pref);
let mermaidSettings = loadMermaidSettings(pref);
let mermaidPreviewVersion = 0;
let guideEditor: MarkdownEditor | null = null;
let lockMinutes = loadLockMinutes(pref);
let lockOnBlur = loadLockOnBlur(pref);
// The app's own confirm() takes focus for a moment; that is not leaving the tab.
let ownDialog = false;
// The Library tree note being dragged, kept here since a drag's data is unreadable until the drop.
let treeDrag: { path: string; folder: boolean } | null = null;
// Typing, clicking, and scrolling count; a tab left open does not keep folders unlocked.
let lastActivity = Date.now();
let unlockRequest: { folder: string; resolve: (unlocked: boolean) => void } | null = null;
let lastForeground = Date.now();
let previousFocus: HTMLElement | null = null;
// The page holds the first two panes. Splitting makes the rest, up to nine, from the
// second one as it is before any editor goes in.
const paneTemplate = el("editor-pane-secondary").outerHTML;
const paneId = (index: number) => (index === 0 ? "primary" : index === 1 ? "secondary" : `pane-${index + 1}`);
function createPane(index: number) {
  const id = paneId(index);
  if (index > 1) {
    const template = document.createElement("template");
    template.innerHTML = paneTemplate.replaceAll("secondary", id);
    const section = template.content.firstElementChild as HTMLElement;
    section.setAttribute("aria-label", `Editor pane ${index + 1}`);
    section.querySelector(".editor-area")!.setAttribute("aria-label", `Markdown note editor in pane ${index + 1}`);
    el("editor-panes").append(section);
  }
  const pane = {
    id,
    path: null as string | null,
    open: index === 0,
    // Preview mode: the note shows rendered and read-only, whatever F4 has the rest showing.
    preview: false,
    editor: null as unknown as MarkdownEditor,
  };
  pane.editor = new MarkdownEditor(el(`editor-${id}`), {
    initialMode: mode,
    onChange(text) {
      if (pane.path) store.edit(pane.path, text);
    },
    onCursorChange(line, column) {
      if (activePane !== index) return;
      el("cursorpos").textContent = `${line}:${column}`;
      outlineLine = line;
      renderOutline();
    },
    onActivateLink: (text) => activateLink(text, index),
    async onImageFile(file, placeholder) {
      const notePath = pane.path;
      if (!notePath) throw new Error("Open a note before inserting an image.");
      try {
        // An encrypted note keeps its images encrypted, in its folder's attachments/.
        const assetPath = newAttachmentPath(file, store.encryptedFolder(notePath));
        const data = await encodeAttachment(file);
        store.edit(assetPath, data);
        await store.flush(assetPath);
        const markdown = imageMarkdown(file.name, attachmentReference(notePath, assetPath));
        if (pane.path !== notePath) {
          const text = textOf(notePath);
          if (text.includes(placeholder)) store.edit(notePath, text.replace(placeholder, markdown));
          return null;
        }
        return markdown;
      } catch (error) {
        if (pane.path !== notePath) {
          const text = textOf(notePath);
          if (text.includes(placeholder)) store.edit(notePath, text.replace(placeholder, ""));
        }
        throw error;
      }
    },
    onImageError(message) {
      toast(message, true);
    },
    resolveImage(source) {
      const path = pane.path ? resolveAttachmentPath(pane.path, source) : null;
      const data = path ? textOf(path) : "";
      const mime = path ? attachmentMime(path) : null;
      return data && mime ? `data:${mime};base64,${data}` : null;
    },
    getImageBorder: () => imageSettings.border,
    getMermaidSettings: () => mermaidSettings,
    getWikiLinkSuggestions(query) {
      return filterWikiLinkTitles(
        paths()
          .filter((p) => !under(p, ARCHIVE_ROOT) && p !== pane.path)
          .map(noteStem),
        query,
      );
    },
    getMentionSuggestions: (query) => filterMentions(mentions(), query),
    onMentions(names) {
      // The list is kept with the account, so names typed in an encrypted note stay out of it.
      if (pane.path && !isEncryptedFile(pane.path)) remember(names);
    },
    getTodayText() {
      return slashTodayText();
    },
    onAskAi(about) {
      activate(index);
      openAi(about === "cursor");
    },
  });
  pane.editor.setLineNumbers(lineNumbers);
  pane.editor.setLineWrapping(wordWrap);
  pane.editor.setSpellCheck(spellCheck);
  el(`editor-pane-${id}`).addEventListener("focusin", () => activate(index));
  el(`editor-pane-${id}`).addEventListener("pointerdown", () =>
    activate(index),
  );
  el(`filename-${id}`).onclick = () => openNoteSettings(index);
  el(`pane-action-${id}`).onclick = () => toggleSplit(index);
  el(`pane-empty-${id}`).querySelector("button")!.onclick = () => openPalette(index);
  return pane;
}
const panes = [createPane(0), createPane(1)];
/** How the open panes are arranged. Like the notes in them, only a reload brings it back. */
let paneLayout: PaneLayout = { pane: 0 };
const openPanes = () => layoutPanes(paneLayout);
const settings = () => store.state?.metadata.value ?? defaultMetadata();
// A blank note that has never been created is not in the vault, so it stays out of
// the Library, the palettes and the size accounting until it has content.
const draftPaths = () => [...store.drafts.values()]
  .filter((draft) => draft.status !== "new")
  .map((draft) => draft.path);
const paths = () => [
  ...new Set([
    ...(store.state?.documents.map((d) => d.path).filter(isNote) ?? []),
    ...draftPaths().filter(isNote),
  ]),
];
const vaultPaths = () => [
  ...new Set([
    ...(store.state?.documents.map((d) => d.path) ?? []),
    ...draftPaths(),
  ]),
];
const current = () => panes[activePane]!;
/** A note a pane is showing that the vault has never stored. */
const isBlankNote = (path: string | null) =>
  !!path && store.drafts.get(path)?.status === "new";
const folders = () =>
  foldersFromPaths(
    [
      ...(store.state?.documents.map((d) => d.path) ?? []),
      ...store.drafts.keys(),
    ],
    store.state?.vault.repositoryFolders,
  );
const inFolder = (path: string, folder: string) =>
  !folder || folderOf(path) === (folder === "." ? "" : folder);
/** A note's text as you read it: decrypted while its folder is unlocked, else empty. */
const textOf = (path: string) => store.text(path);
const folderIcon = (folder: string) =>
  iconElement(settings().icons[folder], settings().custom_icons);
const keys = () => keyFolders(vaultPaths());
/** The encrypted folder a new note in this folder would belong to. */
const encryptedFolderFor = (folder: string) => encryptedFolderOf(folder, keys());
/** Asks for the passphrase of a locked folder; resolves once it opens or is dismissed. */
function requestUnlock(folder: string): Promise<boolean> {
  if (store.keyring.unlocked(folder)) return Promise.resolve(true);
  return new Promise((resolve) => {
    el("unlock-folder-name").textContent = folder;
    el("unlock-hint").textContent = `It locks again after ${lockMinutes} ${lockMinutes === 1 ? "minute" : "minutes"} without activity${lockOnBlur ? " or as soon as this tab loses focus" : ""}.`;
    el<HTMLInputElement>("unlock-passphrase").value = "";
    el("unlock-error").textContent = "";
    el("unlock-progress").hidden = true;
    el<HTMLButtonElement>("unlock-submit").disabled = false;
    // Opening it displaces any earlier request, which then stays locked.
    openModal("unlock-modal");
    unlockRequest = { folder, resolve };
  });
}
/** A padlock: shut while its folder is locked, open once it is unlocked. */
function lockIcon(locked: boolean): HTMLSpanElement {
  const icon = document.createElement("span");
  icon.className = locked ? "lock-icon" : "lock-icon unlocked";
  icon.setAttribute("aria-hidden", "true");
  icon.title = locked ? "Encrypted · locked" : "Encrypted · unlocked";
  return icon;
}
/** Whether a note or folder is encrypted, and if so whether it is locked right now. */
function encryption(path: string, folder = false): "locked" | "unlocked" | null {
  if (folder) {
    const key = encryptedFolderFor(path);
    return key === null ? null : store.keyring.unlocked(key) ? "unlocked" : "locked";
  }
  return isEncryptedFile(path) ? (store.isLocked(path) ? "locked" : "unlocked") : null;
}
/** The padlock to show before a note or folder name, if it is encrypted. */
function lockIcons(path: string, folder = false): HTMLSpanElement[] {
  const state = encryption(path, folder);
  return state ? [lockIcon(state === "locked")] : [];
}
/** The eye before a pane's title while the pane is in Preview mode. */
function previewIcon(): HTMLSpanElement {
  const icon = document.createElement("span");
  icon.className = "preview-icon";
  icon.setAttribute("role", "img");
  icon.setAttribute("aria-label", "Preview, read-only");
  icon.title = "Preview · read-only. F3 edits again.";
  return icon;
}
/**
 * A pane's title: the note's path, after a padlock when the note is encrypted, and after
 * an eye while the pane is in Preview mode.
 */
function setPaneTitle(pane: (typeof panes)[number], path: string | null) {
  el(`filename-${pane.id}`).replaceChildren(
    ...(pane.preview ? [previewIcon()] : []),
    ...(path ? lockIcons(path) : []),
    path ?? "SELECT NOTE",
  );
}
/** Unlocks the folder a new or existing note belongs to, if it is encrypted. */
async function unlockedFor(path: string): Promise<boolean> {
  const folder = isEncryptedFile(path) ? encryptedFolderFor(folderOf(path)) : null;
  return folder === null || requestUnlock(folder);
}
async function submitUnlock() {
  const request = unlockRequest;
  if (!request) return;
  el("unlock-error").textContent = "";
  el("unlock-progress").hidden = false;
  el<HTMLButtonElement>("unlock-submit").disabled = true;
  try {
    await store.unlock(request.folder, el<HTMLInputElement>("unlock-passphrase").value);
  } finally {
    el("unlock-progress").hidden = true;
    el<HTMLButtonElement>("unlock-submit").disabled = false;
  }
  lastActivity = Date.now();
  unlockRequest = null;
  el<HTMLInputElement>("unlock-passphrase").value = "";
  closeModal("unlock-modal");
  request.resolve(true);
  render();
  // A refresh held back for this folder can go ahead now.
  if (sync.error && store.state?.vault.connection && api.session.user) run(() => sync.refresh());
}
/**
 * Forgets folder keys and everything decrypted with them: drafts, the panes showing
 * them, their undo history, and any dialog reading them.
 */
let locking: Promise<void> | null = null;
function lockFolders(folders = store.keyring.folders): Promise<void> {
  locking ??= lockNow(folders).finally(() => (locking = null));
  return locking;
}
async function lockNow(folders: string[]) {
  if (!folders.length) return;
  const showing = panes.filter((pane) => pane.path && isEncryptedFile(pane.path));
  const unsaved: string[] = [];
  for (const folder of folders) unsaved.push(...(await store.lock(folder)));
  for (const pane of showing) if (pane.path && store.isLocked(pane.path)) pane.path = null;
  if (el("conflict-modal").classList.contains("active") && conflict?.paths.some(isEncryptedFile))
    closeModal("conflict-modal");
  if (historical && isEncryptedFile(historical.path)) {
    historical = null;
    if (el("revision-modal").classList.contains("active")) closeModal("revision-modal");
  }
  render();
  if (el("palette-modal").classList.contains("active")) renderPalette();
  showing.forEach((pane) => pane.editor.clearHistory());
  renderEncryptionSettings();
  toast(
    unsaved.length
      ? `Locked. These edits could not be saved first: ${unsaved.join(", ")}.`
      : folders.length === 1
        ? `Locked “${folders[0]}”.`
        : "Locked encrypted folders.",
    unsaved.length > 0,
  );
}
/** Locks every folder when the tab is hidden or the window loses focus, if that is on. */
function lockOnFocusLoss() {
  if (lockOnBlur && !ownDialog && store.keyring.folders.length) run(() => lockFolders());
}
function checkAutoLock() {
  if (!locking && store.keyring.folders.length && lockDue(lastActivity, Date.now(), lockMinutes))
    run(() => lockFolders());
}
function renderEncryptionSettings() {
  el<HTMLSelectElement>("encryption-lock-minutes").value = String(lockMinutes);
  el<HTMLInputElement>("encryption-lock-on-blur").checked = lockOnBlur;
  const open = store.keyring.folders;
  el("encryption-unlocked").textContent = open.length ? open.join(", ") : "None";
  el<HTMLButtonElement>("lock-folders-button").disabled = !open.length;
}
function toast(message: string, error = false) {
  el("toast").textContent = message;
  el("toast").classList.toggle("error", error);
  el("toast").classList.add("visible");
  clearTimeout(toastTimer);
  toastTimer = setTimeout(() => el("toast").classList.remove("visible"), 5000);
}
const run = (action: () => Promise<unknown>) => {
  void action().catch((e) => {
    toast(e instanceof Error ? e.message : "Operation failed.", true);
    render();
  });
};
function activate(index: number) {
  activePane = index;
  panes.forEach((p, i) =>
    el(`editor-pane-${p.id}`).classList.toggle("active", i === index),
  );
  renderPreviewToggle();
  renderStats();
}
function guideIsOpen(): boolean {
  return !el("guide-pane").hidden;
}
/** The editor in front of you: the Guide while it is open, otherwise the active pane. */
const activeEditor = () => (guideIsOpen() && guideEditor ? guideEditor : current().editor);
function renderPreviewToggle(): void {
  const live = guideIsOpen() && guideEditor
    ? guideEditor.getMode() === "live"
    : mode === "live";
  el("editor-mode-toggle").setAttribute("aria-pressed", String(live));
  // Preview mode belongs to a pane, so the menu shows the active pane's.
  const preview = !guideIsOpen() && current().preview;
  el("preview-mode-toggle").setAttribute("aria-pressed", String(preview));
  screen.classList.toggle("preview-mode-active", preview);
  screen.classList.toggle("live-preview-active", live);
}
function closeGuide(focus = true): void {
  if (!guideIsOpen()) return;
  el("guide-pane").hidden = true;
  el("editor-panes").hidden = false;
  renderPreviewToggle();
  render();
  if (focus) current().editor.focus();
}
function toggleGuideMode(): void {
  if (!guideEditor) return;
  const next = guideEditor.getMode() === "live" ? "source" : "live";
  guideEditor.setMode(next);
  el("guide-mode-button").textContent = next === "live" ? "VIEW SOURCE" : "LIVE PREVIEW";
  renderPreviewToggle();
  guideEditor.focus();
}
function openGuide(): void {
  closeMenus();
  if (!guideEditor) {
    guideEditor = new MarkdownEditor(el("guide-editor"), {
      initialMode: "live",
      readOnly: true,
      onChange() {},
      onCursorChange(line) {
        outlineLine = line;
        renderOutline();
      },
      onActivateLink(text) {
        const link = parseEditorLink(text, location.href);
        if (link?.kind === "url") window.open(link.url, "_blank", "noopener,noreferrer");
        else if (link?.kind === "heading" && guideEditor) goToHeading(guideEditor, link.heading);
      },
      resolveImage: (source) => source.startsWith("/guide/") ? source : null,
      getImageBorder: () => imageSettings.border,
      getMermaidSettings: () => mermaidSettings,
    });
    guideEditor.setValue(guideMarkdown);
  }
  guideEditor.setMode("live");
  el("guide-mode-button").textContent = "VIEW SOURCE";
  el("editor-panes").hidden = true;
  el("guide-pane").hidden = false;
  renderPreviewToggle();
  render();
  guideEditor.requestMeasure();
  guideEditor.focus();
}
function loadPaneNote(path: string, index: number) {
  const pane = panes[index]!;
  const left = pane.path;
  // An answer on its way to the note that was here has nowhere to go now.
  if (aiJob?.pane === pane && left !== path) cancelAi();
  pane.path = path;
  // The tree opens the folders above an opened note, so it shows where the note is.
  for (const folder of foldersAbove([path])) libraryOpenFolders.add(folder);
  if (libraryTreeVisible) libraryTreeSelection = path;
  store.get(path);
  // Leaving a blank note that was never created discards it; nothing was lost.
  if (left && left !== path && !panes.some((p) => p.path === left))
    store.discardNewDraft(left);
  pane.editor.setValue(textOf(path));
  setPaneTitle(pane, path);
  el(`pane-empty-${pane.id}`).hidden = true;
  el(`editor-wrapper-${pane.id}`).hidden = false;
}
/** Opens a note in a pane; without `focus` it only shows there, and the cursor stays put. */
function openNote(path: string, index = activePane, focus = true) {
  // A pane chosen before it closed, by the Library or a dialog, hands over to the active one.
  if (!panes[index]?.open) index = activePane;
  if (store.isLocked(path)) {
    const folder = store.encryptedFolder(path);
    if (folder === null) toast(`No folder key can open “${path}”.`, true);
    else void requestUnlock(folder).then((unlocked) => unlocked && openNote(path, index, focus));
    return;
  }
  if (store.damaged.has(path)) {
    toast(`“${path}” could not be decrypted with its folder's key.`, true);
    return;
  }
  closeGuide(false);
  loadPaneNote(path, index);
  if (focus) activate(index);
  render();
  if (focus) {
    panes[index]!.editor.focus();
    if (matchMedia("(max-width:760px)").matches) setSidebar(false);
  }
  if (index === activePane && screen.classList.contains("revisions-open")) run(loadHistory);
}
/**
 * Shows the note the Library has selected in the pane last used, leaving the cursor in
 * the Library. A locked note waits for Enter, so arrowing past it asks for nothing.
 */
function previewNote(path: string) {
  if (current().path === path || store.isLocked(path) || store.damaged.has(path)) return;
  openNote(path, activePane, false);
}
/**
 * Shows a note beside the one being read: in a new split pane, or in place of the pane
 * after the active one.
 */
function showBeside(path: string) {
  const show = () => {
    const index = openPanes().length > 1 ? nextPane(paneLayout, activePane) : addPane("row");
    if (index !== null) openNote(path, index, false);
  };
  const locked = store.isLocked(path),
    folder = locked ? store.encryptedFolder(path) : null;
  // A note nothing can open says why, without opening a pane for it.
  if (store.damaged.has(path) || (locked && folder === null)) openNote(path, activePane, false);
  // The pane opens once the note can be read, not for a passphrase that is dismissed.
  else if (folder === null) show();
  else void requestUnlock(folder).then((unlocked) => unlocked && show());
}
/** Opens a note in the pane after `sourcePane`, splitting it first when it is the only one; resolves that pane. */
function openNoteInSplit(path: string, sourcePane: number): number | null {
  const index = openPanes().length > 1 ? nextPane(paneLayout, sourcePane) : addPane("row", sourcePane);
  if (index !== null) openNote(path, index);
  return index;
}
/** Puts the cursor on the heading a link names, at the top of the view, or says there is none. */
function goToHeading(editor: MarkdownEditor, name: string) {
  const heading = findHeading(outlineHeadings(editor.getValue()), name);
  if (heading) editor.goToLineAtTop(heading.line);
  else toast(`No heading “${name}” in this note.`);
}
/** The notes a [[wiki link]] or a note path links to, from the note at `from`. */
function linkedNotes(link: EditorLink & { kind: "wiki" | "note" }, from: string | null): string[] {
  if (link.kind === "wiki") return resolveWikiLink(link.title, paths());
  const path = notePathFromLink(from ?? "", link.path);
  // An encrypted note's file is its Markdown name with .age after it.
  return path ? [path, `${path}.age`].filter((candidate) => paths().includes(candidate)) : [];
}
/**
 * Follows a link in the note in pane `index`: a web address opens in a new tab, a heading
 * in the note comes to the top of it, and a link to another note opens it in the pane, or
 * `beside` it, at the heading it names if it names one.
 */
function activateLink(text: string, index: number, beside = false) {
  const link = parseEditorLink(text, location.href);
  const pane = panes[index];
  if (!link || !pane) return;
  if (link.kind === "url") {
    window.open(link.url, "_blank", "noopener,noreferrer");
    return;
  }
  if (link.kind === "heading") {
    goToHeading(pane.editor, link.heading);
    return;
  }
  const matches = linkedNotes(link, pane.path);
  if (matches.length > 1) {
    openPalette(index, matches);
    return;
  }
  const path = matches[0];
  if (!path) {
    toast(link.kind === "wiki" ? `No active note matches “${link.title}”.` : `No note at “${link.path}”.`);
    return;
  }
  const shown = beside ? openNoteInSplit(path, index) : (openNote(path, index), index);
  // A note that has to be unlocked first opens without going to the heading.
  const target = shown === null ? null : panes[shown];
  if (link.heading && target?.path === path) goToHeading(target.editor, link.heading);
}
function renderStats() {
  const text = guideIsOpen() ? guideMarkdown : current().path ? textOf(current().path!) : "";
  const count = text.trim() ? text.trim().split(/\s+/).length : 0;
  el("wordcount").textContent = `${count} words`;
  el("charcount").textContent = `${text.length} chars`;
  const path = guideIsOpen() ? null : current().path;
  el("status-folder").hidden = !path;
  if (path) {
    const folder = folderOf(path);
    el("status-folder-name").textContent = folder.split("/").at(-1) || "/";
    el("status-folder-icon").replaceChildren(...[folderIcon(folder)].filter((icon) => icon !== null));
    el("status-folder").title = folder || "/";
    const color = folderColor(path);
    el("status-folder-color").hidden = color === "transparent";
    el("status-folder-color").style.backgroundColor = color;
  }
}
function sortedPaths(query = "", source = paths()): string[] {
  const terms = query.toLocaleLowerCase().trim().split(/\s+/);
  return source
    .filter((p) =>
      terms.every((t) => `${p}\n${textOf(p)}`.toLocaleLowerCase().includes(t)),
    )
    .sort((a, b) => {
      const pinned = settings().pinned;
      const pa = pinned.includes(a),
        pb = pinned.includes(b);
      if (pa !== pb) return pa ? -1 : 1;
      const av = `${folderOf(a)}\n${titleOf(a)}`.toLocaleLowerCase(),
        bv = `${folderOf(b)}\n${titleOf(b)}`.toLocaleLowerCase();
      return av < bv ? -1 : av > bv ? 1 : a < b ? -1 : a > b ? 1 : 0;
    });
}
function row(path: string, onClick: () => void, showPath = false): HTMLButtonElement {
  const button = document.createElement("button");
  button.type = "button";
  button.className = "note-row";
  button.dataset.path = path;
  button.setAttribute("role", "option");
  button.setAttribute("aria-selected", String(current().path === path));
  button.classList.toggle("active", current().path === path);
  button.title = path;
  const marker = document.createElement("span");
  marker.className = "note-index-mark";
  marker.style.backgroundColor = folderColor(path);
  marker.setAttribute("aria-hidden", "true");
  const title = document.createElement("span");
  title.className = "note-row-title";
  title.replaceChildren(...lockIcons(path), showPath ? path : titleOf(path));
  const snippet = document.createElement("span");
  snippet.className = "note-row-snippet";
  snippet.textContent = store.isLocked(path)
    ? "Encrypted · unlock to read"
    : store.damaged.has(path)
      ? "Encrypted · can't be decrypted"
      : textOf(path).trimStart().slice(0, 240).replace(/\s+/g, " ") || "Empty note";
  const body = document.createElement("span");
  body.className = "note-row-body";
  body.append(title, snippet);
  const pin = document.createElement("span");
  pin.className = "pin-mark";
  pin.textContent = settings().pinned.includes(path) ? "◆" : "";
  button.append(marker, body, pin);
  button.onclick = onClick;
  return button;
}
function renderLibrary() {
  const folderList = folders();
  const options: [string, string][] = [
    ["", "All"],
    [".", "/ (repository root)"],
    ...folderList.map((folder): [string, string] => [folder, folder]),
  ];
  const optionsKey = JSON.stringify(options);
  for (const id of ["library-folder", "palette-folder"]) {
    const filter = el<HTMLSelectElement>(id);
    if (filter.dataset.options === optionsKey) continue;
    const selected = filter.value;
    filter.replaceChildren(
      ...options.map(([value, label]) => {
        const option = document.createElement("option");
        option.value = value;
        option.textContent = label;
        return option;
      }),
    );
    filter.value = options.some(([value]) => value === selected)
      ? selected
      : "";
    filter.dataset.options = optionsKey;
  }
  const selectedFolder = el<HTMLSelectElement>("library-folder").value;
  el("library-folder-current").textContent = libraryTreeVisible
    ? "Full tree"
    : selectedFolder === ""
    ? "All folders"
    : selectedFolder === "."
      ? "/ (repository root)"
      : selectedFolder;
  const list = el("note-list");
  list.hidden = libraryTreeVisible;
  el("library-tree").hidden = !libraryTreeVisible;
  // Drawing the list again keeps the cursor on the row it was on.
  const focused = list.contains(document.activeElement)
    ? (document.activeElement as HTMLElement).dataset.path
    : undefined;
  list.replaceChildren();
  const found = sortedPaths(
    el<HTMLInputElement>("library-search").value,
  ).filter((path) =>
    inFolder(path, el<HTMLSelectElement>("library-folder").value),
  );
  let group = "";
  for (const path of found) {
    const folder = settings().pinned.includes(path)
      ? "PINNED"
      : folderOf(path) || "/";
    if (folder !== group) {
      group = folder;
      const header = document.createElement("div");
      header.className = "folder-heading";
      const icon = settings().pinned.includes(path) ? null : folderIcon(folderOf(path));
      header.replaceChildren(...[icon].filter((item) => item !== null), folder);
      if (!settings().pinned.includes(path)) header.dataset.folder = folderOf(path);
      list.append(header);
    }
    list.append(row(path, () => openNote(path)));
  }
  if (!found.length) {
    const p = document.createElement("p");
    p.className = "panel-empty";
    p.textContent = "No notes here. File → New starts a local note.";
    list.append(p);
  }
  if (focused !== undefined)
    [...list.querySelectorAll<HTMLElement>(".note-row")].find((row) => row.dataset.path === focused)?.focus();
  const backlinks = el("backlink-list");
  backlinks.replaceChildren();
  let count = 0;
  if (current().path)
    for (const path of paths()) {
      if (path === current().path || under(path, ARCHIVE_ROOT)) continue;
      const links = [...textOf(path).matchAll(/\[\[([^\]\n]+)\]\]/g)];
      if (
        links.some((m) => {
          const found = resolveWikiLink(m[1]!, paths());
          return found.length === 1 && found[0] === current().path;
        })
      ) {
        backlinks.append(row(path, () => openNote(path)));
        count++;
      }
    }
  el("backlink-count").textContent = String(count);
  renderNoteFolders(folderList);
  if (libraryTreeVisible) renderLibraryTree();
}
function renderLibraryTree() {
  const tree = el("library-tree");
  libraryTreeRows = fileTreeRows(
    sortedPaths(el<HTMLInputElement>("library-search").value),
    folders(),
    { has: (path) => !libraryFolderOpen(path) },
  );
  while (!libraryTreeRows.some((row) => row.path === libraryTreeSelection))
    libraryTreeSelection = folderOf(libraryTreeSelection);
  tree.replaceChildren(
    ...libraryTreeRows.map((row, index) => {
      const button = document.createElement("button");
      button.type = "button";
      button.className = "file-tree-row";
      button.id = `library-tree-row-${index}`;
      button.tabIndex = -1;
      button.setAttribute("role", "treeitem");
      button.setAttribute("aria-label", treeLabel(row));
      button.setAttribute("aria-level", String(row.depth));
      button.setAttribute("aria-posinset", String(row.position));
      button.setAttribute("aria-setsize", String(row.siblings));
      if (row.folder) button.setAttribute("aria-expanded", String(row.expanded));
      const branch = document.createElement("span");
      branch.className = "tree-branch";
      branch.setAttribute("aria-hidden", "true");
      branch.textContent = `${row.prefix}${row.folder ? (row.expanded ? "[-] " : "[+] ") : ""}`;
      const name = document.createElement("span");
      name.className = row.folder ? "tree-folder" : "tree-note";
      name.textContent = row.name;
      const icon = row.folder ? folderIcon(row.path) : null;
      button.append(branch, ...lockIcons(row.path, row.folder), ...[icon].filter((item) => item !== null), name);
      // The pencil opens Edit folder; F2 does the same from the keyboard.
      if (row.folder && row.path) button.append(pixelIcon(PENCIL_ROWS, "tree-edit"));
      // Alt-click starts a note in the folder: the one clicked, or the clicked note's.
      const folder = row.folder ? row.path : folderOf(row.path);
      button.title = row.folder
        ? row.path
          ? `${row.path} · Drag onto a folder to move it · Alt-click for a new note here · F2 or the pencil edits the folder`
          : "/ · Alt-click for a new note here"
        : `${row.path} · Drag onto a folder to move it`;
      button.onclick = (event) => {
        libraryTreeSelection = row.path;
        if (row.folder && (event.target as Element).closest(".tree-edit")) {
          renderLibraryTree();
          openFolderEdit(row.path);
        } else if (event.altKey) {
          event.preventDefault();
          openForm(false, folder);
        } else if (row.folder) {
          toggleLibraryTreeFolder(row.path);
          tree.focus();
        } else openNote(row.path);
      };
      if (row.folder) button.dataset.folder = row.path;
      // Notes and folders drag onto a folder; the root stays where it is.
      if (row.path) {
        button.draggable = true;
        button.ondragstart = (event) => {
          treeDrag = { path: row.path, folder: row.folder };
          event.dataTransfer?.setData("text/plain", row.path);
          if (event.dataTransfer) event.dataTransfer.effectAllowed = "move";
          button.classList.add("dragging");
        };
        button.ondragend = () => {
          treeDrag = null;
          showDropTarget(null);
          button.classList.remove("dragging");
        };
      }
      // Dropping on a note drops into its folder.
      button.ondragover = (event) => {
        if (!dropMoves(treeDrag, folder)) {
          showDropTarget(null);
          return;
        }
        event.preventDefault();
        if (event.dataTransfer) event.dataTransfer.dropEffect = "move";
        showDropTarget(folder);
      };
      button.ondrop = (event) => {
        event.preventDefault();
        const drag = treeDrag;
        treeDrag = null;
        showDropTarget(null);
        if (!drag || !dropMoves(drag, folder)) return;
        run(() => (drag.folder ? moveFolderInto(drag.path, folder) : moveNoteToFolder(drag.path, folder)));
      };
      return button;
    }),
  );
  const selectedIndex = libraryTreeRows.findIndex((row) => row.path === libraryTreeSelection);
  [...tree.children].forEach((button, index) =>
    button.setAttribute("aria-selected", String(index === selectedIndex)),
  );
  tree.setAttribute("aria-activedescendant", `library-tree-row-${selectedIndex}`);
}
/** A tree row's accessible name, saying whether it is encrypted and locked. */
function treeLabel(row: FileTreeRow): string {
  const state = encryption(row.path, row.folder);
  return state ? `${row.name}, encrypted, ${state}` : row.name;
}
/**
 * Whether dropping a dragged note or folder into `folder` moves it: not into the folder
 * it is already in, and a folder not into itself or a folder inside it.
 */
function dropMoves(drag: { path: string; folder: boolean } | null, folder: string): boolean {
  if (!drag || folderOf(drag.path) === folder) return false;
  return !drag.folder || (folder !== drag.path && !under(folder, drag.path));
}
/** Outlines the folder a dragged note or folder would land in. */
function showDropTarget(folder: string | null) {
  for (const button of el("library-tree").querySelectorAll<HTMLElement>(".file-tree-row"))
    button.classList.toggle("drop-target", folder !== null && button.dataset.folder === folder);
}
/**
 * Moves a stored note, encrypting it when it moves into an encrypted folder. An
 * encrypted source must be readable too, so its image links can be rebased.
 * Resolves false when an unlock it needed was dismissed.
 */
async function moveNote(source: string, destination: string): Promise<boolean> {
  const problem = moveProblem(source, destination, keys());
  if (problem) throw new Error(problem);
  for (const path of [source, destination])
    if (isEncryptedFile(path) && !(await unlockedFor(path))) return false;
  await store.flush(source);
  const doc = store.state?.documents.find((d) => d.path === source);
  if (!doc) throw new Error("This note changed. Reopen its details.");
  await store.database.move(source, destination, doc.revision, await store.movedText(source, destination));
  store.discardResolvedDraft([source]);
  panes.filter((p) => p.path === source).forEach((p) => (p.path = destination));
  if (libraryTreeSelection === source) libraryTreeSelection = destination;
  return true;
}
/** Moves a note into a folder, keeping its title; resolves where it went, or null. */
async function moveNoteToFolder(source: string, folder: string): Promise<string | null> {
  const extension = isEncryptedNote(source) ? ".md.age" : noteExtension(folder, keys());
  const destination = notePath(`${folder ? `${folder}/` : ""}${titleOf(source)}${extension}`);
  assertDestination(destination, vaultPaths(), source);
  if (!(await moveNote(source, destination))) return null;
  await store.changed();
  render();
  toast(
    isEncryptedNote(destination) && !isEncryptedNote(source)
      ? `Moved “${titleOf(destination)}” to ${folder}. It is encrypted from now on.`
      : `Moved “${titleOf(destination)}” to ${folder || "/"}.`,
  );
  return destination;
}
/** Moves a folder into another, keeping its name and everything in it; resolves where it went. */
async function moveFolderInto(source: string, folder: string): Promise<string> {
  const name = source.split("/").at(-1)!;
  const destination = folder ? `${folder}/${name}` : name;
  await renameFolder(source, destination);
  toast(`Moved “${name}” to ${folder || "/"}.`);
  return destination;
}
/** What Move to folder moves, and whether the cursor goes back to the Library after. */
let moving: { path: string; folder: boolean; fromLibrary: boolean } | null = null;
/** Opens Move to folder on a note, or on a folder with everything in it. */
function openMove(path: string, folder: boolean) {
  if (folder && !path) return;
  moving = { path, folder, fromLibrary: el("library-panel").contains(document.activeElement) };
  selection = 0;
  el("move-title").textContent = `MOVE “${folder ? path.split("/").at(-1) : titleOf(path)}” TO`;
  el<HTMLInputElement>("move-search").value = "";
  openModal("move-modal");
  renderMoveTargets();
}
/**
 * The folders Move to folder offers: every one but where the note or folder is now, and a
 * folder's own, matching what is typed; a typed folder that doesn't exist yet comes last.
 */
function renderMoveTargets() {
  if (!moving) return;
  const { path, folder } = moving;
  const query = el<HTMLInputElement>("move-search").value.trim().replace(/^\/+|\/+$/g, "");
  const offered = (target: string) =>
    target !== folderOf(path) && !(folder && (target === path || under(target, path)));
  const targets = ["", ...folders()].filter(
    (target) => offered(target) && (target || "/").toLowerCase().includes(query.toLowerCase()),
  );
  let fresh = false;
  try {
    fresh = !!query && !folders().includes(query) && offered(relativePath(query));
  } catch {}
  const rows = fresh ? [...targets, query] : targets;
  selection = Math.max(0, Math.min(selection, rows.length - 1));
  el("move-results").replaceChildren(
    ...rows.map((target, i) => {
      const button = document.createElement("button");
      button.type = "button";
      button.className = "note-row move-row" + (i === selection ? " selected" : "");
      button.id = `move-option-${i}`;
      button.setAttribute("role", "option");
      button.setAttribute("aria-selected", String(i === selection));
      const created = fresh && i === rows.length - 1;
      const icon = created ? null : folderIcon(target);
      button.append(
        ...[icon].filter((item) => item !== null),
        created ? `New folder “${target}”` : target || "/ (repository root)",
      );
      button.onclick = () => run(() => moveInto(target));
      return button;
    }),
  );
  updatePaletteSelection("move-search", "move-results", "No other folder matches. Type a new one.");
}
/** Moves what Move to folder holds into `folder`, then puts the cursor back where it was. */
async function moveInto(folder: string) {
  const item = moving;
  if (!item) return;
  moving = null;
  closeModal("move-modal");
  const moved = item.folder ? await moveFolderInto(item.path, folder) : await moveNoteToFolder(item.path, folder);
  if (moved === null || !item.fromLibrary) return;
  // The tree opens where it went, so the selection can follow it there.
  for (const folder of foldersAbove([moved])) libraryOpenFolders.add(folder);
  libraryTreeSelection = moved;
  renderLibraryTree();
  focusLibraryRow(moved);
}
/** Shows a Library note in the pane used last, unlocking it first if need be, then runs `then`. */
function showThen(path: string, then: () => void) {
  const folder = store.isLocked(path) ? store.encryptedFolder(path) : null;
  const show = () => {
    if (current().path !== path) openNote(path, activePane, false);
    if (current().path === path) then();
  };
  if (folder === null) show();
  else void requestUnlock(folder).then((unlocked) => unlocked && show());
}
/** Pins a Library note above the rest, or unpins it. */
async function togglePin(path: string) {
  if (under(path, ARCHIVE_ROOT)) throw new Error("Archived notes can't be pinned.");
  const pinned = !settings().pinned.includes(path);
  await store.database.pin(path, pinned);
  await store.changed();
  render();
  toast(`${pinned ? "Pinned" : "Unpinned"} “${titleOf(path)}”.`);
}
/** Opens New folder, or New encrypted folder, with the path started inside `parent`. */
function openFolderFormIn(parent: string, encrypt = false) {
  openFolderForm(encrypt);
  const input = el<HTMLInputElement>("folder-path");
  if (folderBusy || folderCompleted || !parent) return;
  input.value = `${parent}/`;
  // After the dialog focuses the field, the cursor goes to the end of the path.
  requestAnimationFrame(() => input.setSelectionRange(input.value.length, input.value.length));
}
function toggleLibraryTreeFolder(path: string) {
  if (libraryFolderOpen(path)) {
    libraryOpenFolders.delete(path);
    searchOpenFolders.library.delete(path);
    searchOpenFolders.palette.delete(path);
  } else libraryOpenFolders.add(path);
  renderLibraryTree();
}
function libraryTreeKeydown(event: KeyboardEvent) {
  let index = libraryTreeRows.findIndex((row) => row.path === libraryTreeSelection);
  const row = libraryTreeRows[index];
  if (!row) return;
  const after = libraryTreeRows[index + 1] ?? libraryTreeRows[index - 1];
  if (libraryShortcut(event, row.path, row.folder, after?.path)) return;
  switch (event.key) {
    case "ArrowDown":
    case "j": index = Math.min(libraryTreeRows.length - 1, index + 1); break;
    case "ArrowUp":
    case "k": index = Math.max(0, index - 1); break;
    case "Home": index = 0; break;
    case "End": index = libraryTreeRows.length - 1; break;
    case "ArrowRight":
      if (row.folder && !row.expanded) toggleLibraryTreeFolder(row.path);
      else if (row.folder && libraryTreeRows[index + 1]?.parent === row.path) index++;
      break;
    case "ArrowLeft":
      if (row.folder && row.expanded) toggleLibraryTreeFolder(row.path);
      else if (row.parent !== null)
        index = libraryTreeRows.findIndex((item) => item.path === row.parent);
      break;
    case "Enter":
    case " ":
      event.preventDefault();
      if (event.key === "Enter" && event.altKey) openForm(false, row.folder ? row.path : folderOf(row.path));
      else if (row.folder) toggleLibraryTreeFolder(row.path);
      else openNote(row.path);
      return;
    case "Delete":
    case "Backspace":
      if (event.altKey || event.ctrlKey) return;
      event.preventDefault();
      run(() => (row.folder ? deleteTreeFolder(row.path) : deleteTreeNote(index)));
      return;
    default: return;
  }
  event.preventDefault();
  const selected = libraryTreeRows[index];
  libraryTreeSelection = selected?.path ?? "";
  // A note shows as soon as it is selected; Enter puts the cursor in it.
  if (selected && !selected.folder) previewNote(selected.path);
  renderLibraryTree();
  treeScrollSelection("library-tree");
}
/** Deletes the Library tree's note at `index`, once confirmed, and selects the row after it. */
async function deleteTreeNote(index: number) {
  const row = libraryTreeRows[index]!;
  const next = libraryTreeRows[index + 1] ?? libraryTreeRows[index - 1];
  if (!(await deleteNote(row.path))) return;
  if (next) libraryTreeSelection = next.path;
  renderLibraryTree();
  el("library-tree").focus();
  treeScrollSelection("library-tree");
}
/** Deletes a folder from the Library tree, once confirmed, and selects the folder above it. */
async function deleteTreeFolder(folder: string) {
  if (!(await deleteFolder(folder))) return;
  libraryTreeSelection = folderOf(folder);
  renderLibraryTree();
  el("library-tree").focus();
  treeScrollSelection("library-tree");
}
function treeScrollSelection(id: string) {
  el(id).querySelector('[aria-selected="true"]')?.scrollIntoView({ block: "nearest" });
}
function showLibraryFolderPicker(visible: boolean) {
  el("library-folder").hidden = !visible;
  el("library-folder-current").hidden = visible;
  el("library-folder-toggle").setAttribute("aria-expanded", String(visible));
  if (visible) el("library-folder").focus();
}
function showLibraryTree(visible: boolean, focus = true) {
  libraryTreeVisible = visible;
  write("library-tree", String(visible));
  if (visible) libraryTreeSelection = current().path ?? "";
  el("library-tree-toggle").setAttribute("aria-pressed", String(visible));
  el("library-tree-toggle").setAttribute(
    "aria-label",
    visible ? "Show note list" : "Show full folder tree",
  );
  renderLibrary();
  if (visible && focus) {
    el("library-tree").focus();
    treeScrollSelection("library-tree");
  }
}
let lastAssetSignature = "";
/**
 * The month on show, with a mark against every day that already has a note. It is built
 * from the vault on each render, so a day earns its mark as soon as its note exists, in
 * this window or another one.
 */
function renderCalendar() {
  if (el(widgetPanelId("calendar")).hidden) return;
  el("calendar-month").textContent = monthLabel(calendarView.year, calendarView.month);
  const notes = new Set(paths());
  const now = new Date();
  const weekdays = document.createElement("div");
  weekdays.className = "calendar-week";
  weekdays.setAttribute("role", "row");
  weekdays.append(
    ...WEEKDAY_LABELS.map((label) => {
      const heading = document.createElement("span");
      heading.className = "calendar-weekday";
      heading.setAttribute("role", "columnheader");
      heading.textContent = label;
      return heading;
    }),
  );
  let position = 0;
  const weeks = monthGrid(calendarView.year, calendarView.month).map((week) => {
    const row = document.createElement("div");
    row.className = "calendar-week";
    row.setAttribute("role", "row");
    row.append(
      ...week.map((date) => {
        const day = document.createElement("button");
        const written = notes.has(dailyNotePath(date));
        day.type = "button";
        day.className = "calendar-day";
        day.id = `calendar-day-${position++}`;
        day.tabIndex = -1;
        day.setAttribute("role", "gridcell");
        day.textContent = String(date.getDate());
        day.classList.toggle("written", written);
        day.classList.toggle("outside", date.getMonth() !== calendarView.month);
        day.classList.toggle("today", sameDay(date, now));
        day.setAttribute("aria-selected", String(sameDay(date, calendarCursor)));
        day.setAttribute(
          "aria-label",
          `${formatToday(date, "long")}${written ? ", has a note" : ""}`,
        );
        day.onclick = () => openCalendarDay(date);
        return day;
      }),
    );
    return row;
  });
  const grid = el("calendar-grid");
  grid.replaceChildren(weekdays, ...weeks);
  grid.setAttribute(
    "aria-activedescendant",
    grid.querySelector('[aria-selected="true"]')?.id ?? "",
  );
}
function openCalendarDay(date: Date) {
  calendarCursor = date;
  // A day borrowed from the month either side brings its own month into view.
  calendarView = { year: date.getFullYear(), month: date.getMonth() };
  renderCalendar();
  run(() => openDailyNote(date));
}
function moveCalendar(months: number) {
  const moved = shiftMonth(
    calendarView.year,
    calendarView.month,
    months,
    calendarCursor.getDate(),
  );
  calendarCursor = new Date(moved.year, moved.month, moved.day);
  calendarView = { year: moved.year, month: moved.month };
  renderCalendar();
}
function showThisMonth() {
  calendarCursor = new Date();
  calendarView = {
    year: calendarCursor.getFullYear(),
    month: calendarCursor.getMonth(),
  };
  renderCalendar();
}
function calendarKeydown(event: KeyboardEvent) {
  const step = (days: number) => {
    calendarCursor = shiftDay(calendarCursor, days);
    calendarView = {
      year: calendarCursor.getFullYear(),
      month: calendarCursor.getMonth(),
    };
  };
  switch (event.key) {
    case "ArrowLeft": step(-1); break;
    case "ArrowRight": step(1); break;
    case "ArrowUp": step(-7); break;
    case "ArrowDown": step(7); break;
    case "Home": step(-calendarCursor.getDay()); break;
    case "End": step(6 - calendarCursor.getDay()); break;
    case "PageUp":
    case "PageDown":
      event.preventDefault();
      moveCalendar(event.key === "PageUp" ? -1 : 1);
      return;
    case "Enter":
    case " ":
      event.preventDefault();
      openCalendarDay(calendarCursor);
      return;
    default: return;
  }
  event.preventDefault();
  renderCalendar();
}
/** The answer is a button: it is the thing you came for, so it is the thing you can copy. */
function showAnswer(text: string | null, problem = "") {
  const answer = el("calculator-answer");
  answer.textContent = text === null ? "" : `= ${text}`;
  answer.hidden = text === null;
  el("calculator-problem").textContent = problem;
  el("calculator-result").classList.toggle("error", !!problem);
}
/** The answer follows the typing; half an expression says nothing rather than complaining. */
function showCalculation() {
  const done = calculate(el<HTMLInputElement>("calculator-input").value, calculatorAnswer);
  showAnswer(done.ok ? done.text : null);
}
function copyAnswer() {
  const answer = el("calculator-answer").textContent?.replace(/^=\s*/, "");
  if (!answer) return;
  run(async () => {
    await navigator.clipboard.writeText(answer);
    toast("Answer copied.");
  });
}
function submitCalculation() {
  const input = el<HTMLInputElement>("calculator-input");
  const expression = input.value.trim();
  const done = calculate(expression, calculatorAnswer);
  if (!done.ok) {
    showAnswer(null, done.error);
    return;
  }
  showAnswer(done.text);
  calculatorAnswer = done.value;
  // Newest first, and only so many: the tape is a reminder, not a record.
  calculatorTape = [{ expression, text: done.text }, ...calculatorTape]
    .slice(0, CALCULATOR_TAPE_LIMIT);
  input.value = "";
  renderCalculatorTape();
}
function renderCalculatorTape() {
  el("calculator-tape").replaceChildren(
    ...calculatorTape.map((entry) => {
      const row = document.createElement("button");
      row.type = "button";
      row.className = "calculator-tape-row";
      row.title = `${entry.expression} = ${entry.text}`;
      const expression = document.createElement("span");
      expression.className = "calculator-tape-expression";
      expression.textContent = entry.expression;
      const value = document.createElement("span");
      value.className = "calculator-tape-value";
      value.textContent = `= ${entry.text}`;
      row.append(expression, value);
      // A line goes back in the box, to run again or to change a number first.
      row.onclick = () => {
        el<HTMLInputElement>("calculator-input").value = entry.expression;
        el("calculator-input").focus();
        showCalculation();
      };
      return row;
    }),
  );
}
/**
 * The headings of whatever is in front of you, read from the editor rather than the vault
 * so an unsaved note and the read-only Guide both have an outline. The heading the cursor
 * sits under is marked, which is the one thing a list of headings cannot say by itself.
 */
function renderOutline() {
  if (el(widgetPanelId("outline")).hidden) return;
  const headings = outlineHeadings(activeEditor().getValue());
  const here = headingAtLine(headings, outlineLine);
  // Typing moves the cursor as well as the text, so this is asked twice a keystroke.
  // Rebuilding the list only when it would read differently also keeps the focus on a
  // row somebody is using.
  const drawn = `${here?.line ?? 0}|${headings.map((h) => `${h.level} ${h.line} ${h.text}`).join("\n")}`;
  if (drawn === outlineDrawn) return;
  outlineDrawn = drawn;
  const list = el("outline-list");
  list.replaceChildren(
    ...headings.map((heading) => {
      const row = document.createElement("button");
      row.type = "button";
      row.className = "outline-row";
      row.dataset.line = String(heading.line);
      row.style.setProperty("--level", String(heading.level));
      row.classList.toggle("here", heading === here);
      row.setAttribute("aria-label", `${heading.text}, heading ${heading.level}`);
      const level = document.createElement("span");
      level.className = "outline-level";
      level.setAttribute("aria-hidden", "true");
      level.textContent = "#".repeat(heading.level);
      const title = document.createElement("span");
      title.className = "outline-title";
      title.textContent = heading.text;
      row.append(level, title);
      row.onclick = () => {
        activeEditor().goToLine(heading.line);
        activeEditor().focus();
      };
      return row;
    }),
  );
  if (!headings.length) {
    const empty = document.createElement("p");
    empty.className = "panel-empty";
    empty.textContent = "No headings yet. Start a line with #.";
    list.append(empty);
  }
}
/**
 * Turbo Pascal kept its clock in a box of block characters, so this one does too. What is
 * stored is the moment it runs out, not a count of ticks: a tab the browser froze comes
 * back with the right time left rather than however many ticks it was allowed.
 */
function renderTimer() {
  const widget = el(widgetPanelId("timer"));
  if (widget.hidden) return;
  const total = timerMinutes * 60;
  const left = timerEndsAt === null ? timerRemaining : remainingAt(timerEndsAt, Date.now());
  const running = timerEndsAt !== null;
  el("timer-clock").textContent = formatRemaining(left);
  el("timer-bar").textContent = timerBar(left, total);
  el("timer-run").textContent = running
    ? "[ PAUSE ]"
    : left > 0 && left < total
      ? "[ RESUME ]"
      : "[ START ]";
  el("timer-run").setAttribute("aria-label", running ? "Pause timer" : "Start timer");
  widget.classList.toggle("running", running);
  widget.classList.toggle("rung", !running && left === 0);
  for (const preset of el("timer-presets").querySelectorAll<HTMLElement>("[data-minutes]"))
    preset.setAttribute(
      "aria-pressed",
      String(Number(preset.dataset.minutes) === timerMinutes),
    );
}
function stopTimerClock() {
  timerEndsAt = null;
  clearInterval(timerTicker);
  timerTicker = undefined;
}
function timerTick() {
  if (timerEndsAt === null) return;
  if (remainingAt(timerEndsAt, Date.now()) > 0) {
    renderTimer();
    return;
  }
  stopTimerClock();
  timerRemaining = 0;
  renderTimer();
  // A timer finishes whether or not its widget is open, so the toast does the telling.
  toast(`Timer finished: ${timerMinutes} minutes.`);
}
function startTimer() {
  if (timerRemaining <= 0) timerRemaining = timerMinutes * 60;
  timerEndsAt = Date.now() + timerRemaining * 1000;
  timerTicker ??= setInterval(timerTick, 500);
  renderTimer();
}
function pauseTimer() {
  if (timerEndsAt === null) return;
  timerRemaining = remainingAt(timerEndsAt, Date.now());
  stopTimerClock();
  renderTimer();
}
function resetTimer() {
  stopTimerClock();
  timerRemaining = timerMinutes * 60;
  renderTimer();
}
/** Folding leaves the heading in place, so a widget can be parked without closing it. */
function setWidgetFolded(id: SidebarWidgetId, folded: boolean) {
  if (folded) widgetFolded.add(id);
  else widgetFolded.delete(id);
  el(widgetPanelId(id)).classList.toggle("folded", folded);
  el(widgetHandleId(id)).setAttribute("aria-expanded", String(!folded));
  labelWidgetHandle(id);
  write("sidebar-folded", [...widgetFolded].join(","));
  if (!folded) renderWidgets();
}
/**
 * The sidebar a widget is in. One that belongs on the right comes back to the left stack
 * on a phone, where a sidebar takes the whole screen and the left one is the one you open.
 */
const widgetSidebar = (id: SidebarWidgetId) =>
  sidebarWidget(id).side === "right" && !matchMedia("(max-width:760px)").matches
    ? "right-sidebar"
    : "left-sidebar";
/** Only the left stack has an order to change; the right sidebar holds the outline alone. */
const widgetRestacks = (id: SidebarWidgetId) => widgetSidebar(id) === "left-sidebar";
function labelWidgetHandle(id: SidebarWidgetId) {
  const handle = el(widgetHandleId(id));
  const verb = widgetFolded.has(id) ? "Expand" : "Collapse";
  const caret = handle.querySelector(".widget-caret");
  if (caret) caret.textContent = widgetFolded.has(id) ? "►" : "▼";
  if (widgetRestacks(id)) {
    handle.title = `${verb} · drag or ⌥↑↓ to reorder`;
    handle.setAttribute("aria-keyshortcuts", "Alt+ArrowUp Alt+ArrowDown");
  } else {
    handle.title = verb;
    handle.removeAttribute("aria-keyshortcuts");
  }
}
/**
 * Lays the panels out in the stored order, each in its own sidebar. The Library stays at
 * the foot of the left stack and the Time Machine at the foot of the right one.
 */
function applyWidgetOrder() {
  for (const id of widgetOrder) {
    const sidebar = widgetSidebar(id);
    el(sidebar).insertBefore(
      el(widgetPanelId(id)),
      el(sidebar === "left-sidebar" ? "library-panel" : "revisions-panel"),
    );
    labelWidgetHandle(id);
  }
  renderRightSidebar();
}
function setWidgetOrder(order: readonly SidebarWidgetId[]) {
  widgetOrder = [...order];
  write("sidebar-order", widgetOrder.join(","));
  applyWidgetOrder();
}
/** The open widgets that share a sidebar with this one, top to bottom. */
const openWidgets = (beside: SidebarWidgetId) =>
  widgetOrder.filter(
    (id) => !el(widgetPanelId(id)).hidden && widgetSidebar(id) === widgetSidebar(beside),
  );
/** Where the open panels are on screen, top to bottom, for working out a drop. */
const widgetBoxes = (beside: SidebarWidgetId): WidgetBox[] =>
  openWidgets(beside).map((id) => {
    const box = el(widgetPanelId(id)).getBoundingClientRect();
    return { id, top: box.top, bottom: box.bottom };
  });
/**
 * ⌥↑ and ⌥↓ restack a widget without a pointer. It passes the next widget that is open,
 * and lands immediately after it, so the closed ones keep the places they had.
 */
function nudgeWidget(id: SidebarWidgetId, direction: -1 | 1) {
  if (!widgetRestacks(id)) return;
  const shown = openWidgets(id);
  const at = shown.indexOf(id);
  if (at < 0) return;
  if (direction < 0 ? at === 0 : at === shown.length - 1) return;
  const passed = shown[at + direction]!;
  const before =
    direction < 0 ? passed : (widgetOrder[widgetOrder.indexOf(passed) + 1] ?? null);
  setWidgetOrder(moveWidgetBefore(widgetOrder, id, before));
  el(widgetHandleId(id)).focus();
}
/**
 * Dragging a heading restacks its widget. The listeners sit on the window rather than
 * capturing the pointer, because reordering moves the handle itself through the DOM and a
 * captured pointer would be lost on the way.
 */
function startWidgetDrag(id: SidebarWidgetId, event: PointerEvent) {
  if (event.button !== 0 || !widgetRestacks(id)) return;
  const panel = el(widgetPanelId(id));
  const startY = event.clientY;
  let moved = false;
  const move = (e: PointerEvent) => {
    // A click wanders a pixel or two; a drag does not.
    if (!moved && Math.abs(e.clientY - startY) < 4) return;
    moved = true;
    panel.classList.add("dragging");
    const before = widgetDropTarget(widgetBoxes(id), id, e.clientY);
    const next = moveWidgetBefore(widgetOrder, id, before);
    if (next.join() !== widgetOrder.join()) setWidgetOrder(next);
  };
  const end = () => {
    window.removeEventListener("pointermove", move);
    window.removeEventListener("pointerup", end);
    window.removeEventListener("pointercancel", end);
    panel.classList.remove("dragging");
    if (!moved) return;
    // The click this drag ends with must not also fold the widget it just moved.
    widgetDragEnded = true;
    el(widgetHandleId(id)).focus();
  };
  window.addEventListener("pointermove", move);
  window.addEventListener("pointerup", end);
  window.addEventListener("pointercancel", end);
}
/** Repaints what the open widgets show. Each one bows out when it is closed. */
function renderWidgets() {
  renderOutline();
  renderCalendar();
  renderTimer();
}
function render() {
  // Another window or GitHub can bring a real note to a blank note's name. The note
  // keeps the name and the blank page moves aside, so neither takes the other over.
  panes.forEach((pane, index) => {
    if (isBlankNote(pane.path) && store.state?.documents.some((d) => d.path === pane.path))
      loadPaneNote(untitledNotePath(store.state), index);
  });
  for (const pane of panes) {
    el(`editor-pane-${pane.id}`).hidden = !pane.open;
    if (!pane.path && !panesReady) continue;
    const draft = pane.path ? store.drafts.get(pane.path) : undefined;
    const doc = store.state?.documents.find((d) => d.path === pane.path);
    // A note whose folder locked leaves its pane, along with its decrypted text.
    if (!pane.path || (!draft && !doc) || store.isLocked(pane.path)) {
      pane.path = null;
      pane.editor.setValue("");
      setPaneTitle(pane, null);
      el(`pane-empty-${pane.id}`).hidden = false;
      el(`editor-wrapper-${pane.id}`).hidden = true;
      continue;
    }
    pane.editor.setValue(textOf(pane.path));
    setPaneTitle(pane, pane.path);
  }
  const assetSignature = vaultPaths().filter((path) => isAttachment(path) || isEncryptedAttachment(path)).map((path) =>
    `${path}:${textOf(path).length}:${store.state?.documents.find((d) => d.path === path)?.revision ?? 0}`,
  ).join("|");
  if (assetSignature !== lastAssetSignature) {
    lastAssetSignature = assetSignature;
    panes.forEach((pane) => pane.editor.refreshPreview());
  }
  const pending = store.state?.attempts.some((a) => a.state === "submitted");
  const conflicts = store.state?.recovery.some((r) => !r.resolved);
  const drafts = [...store.drafts.values()];
  const failed =
    !!store.storageError || drafts.some((d) => d.status === "storage-error");
  const unsaved = drafts.some((d) => d.status === "unsaved");
  const draftConflict = drafts.some((d) => d.status === "conflict");
  const dirty = store.state
    ? differences(filesOf(store.state, true), filesOf(store.state)).length > 0
    : false;
  const files = vaultPaths();
  let total = 0;
  let oversized = files.length > MAX_VAULT_FILES;
  for (const path of files) {
    try {
      // An age file is measured as stored, the way GitHub receives it.
      const text = isAgeFile(path)
        ? store.state?.documents.find((d) => d.path === path)?.text
        : textOf(path);
      if (text === undefined) continue;
      const size = fileByteSize(path, text);
      total += size;
      if (size > fileByteLimit(path)) oversized = true;
    } catch {
      oversized = true;
    }
  }
  oversized ||= total > MAX_VAULT_BYTES;
  const syncActive = [
    "waiting",
    "refreshing",
    "checking",
    "checkpointing",
  ].includes(sync.status);
  const oversizedNotice = "Too big to checkpoint. Editing and export still work.";
  const storageNotice = failed
    ? "Can’t save locally — copy your draft before closing."
    : conflicts || draftConflict
      ? "A note changed in two places. Nothing is lost."
      : oversized
        ? oversizedNotice
        : null;
  const syncNotice = storageNotice || syncActive
    ? null
    : (sync.error ?? (pending ? "Still confirming the last checkpoint." : null));
  const notice = storageNotice ?? syncNotice;
  // The limits only matter once they bite, so they wait on the hover rather than
  // spending a line of the bar.
  const noticeTitle = notice === oversizedNotice
    ? `${notice} Limits: 1 MiB per note, 5 MiB per image, 10 MiB per encrypted file, 2,000 files, 20 MiB per vault.`
    : notice;
  const status = failed
    ? "SAVE FAILED"
    : unsaved
      ? "UNSAVED"
      : draftConflict || conflicts
        ? "CONFLICT"
        : oversized
          ? "TOO BIG"
        : sync.status === "waiting"
          ? "QUEUED…"
          : sync.status === "refreshing"
            ? "REFRESHING…"
            : sync.status === "checking" || sync.status === "checkpointing" || pending
              ? "CHECKPOINTING…"
              : sync.error
                ? "SYNC FAILED"
              : !store.state?.vault.connection
                ? "LOCAL"
                : !navigator.onLine || sync.status === "offline"
                  ? "OFFLINE"
                  : dirty
                    ? "LOCAL CHANGES"
                    : "UP TO DATE";
  el("save-status").textContent = status;
  el("save-status").setAttribute("title", noticeTitle ?? status);
  const statusNotice = el("status-notice");
  statusNotice.hidden = !notice;
  statusNotice.setAttribute("role", storageNotice || sync.error ? "alert" : "status");
  statusNotice.setAttribute("aria-live", storageNotice || sync.error ? "assertive" : "polite");
  const statusMessage = el("status-message");
  if (statusMessage.textContent !== notice) statusMessage.textContent = notice;
  statusMessage.setAttribute("title", noticeTitle ?? "");
  el("retry-save").hidden = !failed;
  el("copy-draft").hidden = !storageNotice;
  el("download-draft").hidden = !storageNotice;
  el("resolve-conflicts").hidden = !conflicts;
  el("retry-sync").hidden = !syncNotice;
  el("retry-sync").textContent = pending ? "Check status" : "Retry sync";
  el("help-sync-display").textContent = sync.error ?? status;
  for (const pane of panes) {
    el(`status-${pane.id}`).textContent =
      status === "LOCAL" || status === "UP TO DATE" ? "■" : "◆";
    el(`sync-tooltip-${pane.id}`).textContent = status;
    el(`sync-status-control-${pane.id}`).setAttribute("aria-label", status);
  }
  const authLabel = el("auth-label");
  authLabel.replaceChildren();
  if (api.session.user) {
    const icon = document.createElement("span");
    icon.className = "github-icon";
    icon.setAttribute("aria-hidden", "true");
    authLabel.append(icon, api.session.user.login);
  } else {
    const initial = document.createElement("span");
    initial.className = "red-168-text";
    initial.textContent = "S";
    authLabel.append(initial, "ave to GitHub");
  }
  const c = store.state?.vault.connection;
  el("repository-status").textContent = c
    ? `${c.owner}/${c.repository} · ${c.branch}`
    : "Local vault";
  renderStats();
  renderLibrary();
  renderWidgets();
  el<HTMLButtonElement>("save-note-button").disabled =
    fileSaveBusy || !current().path || guideIsOpen();
  for (const id of ["note-settings-button", "mobile-note-settings", "export-note-button", "delete-note-button"])
    el<HTMLButtonElement>(id).disabled = guideIsOpen();
  el<HTMLButtonElement>("save-all-button").disabled = fileSaveBusy;
  renderFolderProgress();
  if (el("open-modal").classList.contains("active")) renderFileTree();
}
function closeMenus() {
  document.querySelectorAll(".menu-open").forEach((e) => {
    e.classList.remove("menu-open");
    e.querySelector(".nav-menu-label")?.setAttribute("aria-expanded", "false");
  });
}
function openModal(id: string) {
  // A right-click menu gives the cursor back first, so the dialog returns it there too.
  closeContextMenu();
  const trigger = document.activeElement as HTMLElement;
  const returnFocus = trigger.closest<HTMLElement>(".tui-dropdown")
    ?.querySelector<HTMLElement>(".nav-menu-label") ?? trigger;
  closeMenus();
  document
    .querySelectorAll<HTMLElement>(".tui-modal.active")
    .forEach((modal) => closeModal(modal.id));
  previousFocus = returnFocus;
  el(id).classList.add("active");
  if (id === "mermaid-settings-modal") void renderMermaidSettingsPreview();
  requestAnimationFrame(() => {
    const modal = el(id);
    if (!modal.classList.contains("active")) return;
    const input =
      modal.querySelector<HTMLElement>("[data-initial-focus]") ??
      modal.querySelector<HTMLElement>(
        "input:not(:disabled), select:not(:disabled), textarea:not(:disabled)",
      );
    (input ?? modal.querySelector<HTMLElement>("button, a"))?.focus();
  });
}
function closeModal(id: string) {
  el(id).classList.remove("active");
  if (id === "palette-modal" && searchOpenFolders.palette.size) {
    searchOpenFolders.palette = new Set();
    if (libraryTreeVisible) renderLibraryTree();
  }
  // Closing Ask AI drops a previewed answer; a streaming one carries on in the note.
  if (id === "ai-modal" && !aiJob?.stream) {
    cancelAi();
    aiAsk = null;
  }
  if (id === "unlock-modal" && unlockRequest) {
    // Dismissed, or displaced by another dialog: whatever asked stays locked.
    const request = unlockRequest;
    unlockRequest = null;
    el<HTMLInputElement>("unlock-passphrase").value = "";
    request.resolve(false);
  }
  previousFocus?.focus();
}
/** The whole left stack, Library and widgets together. */
function setSidebar(open: boolean) {
  const mobile = matchMedia("(max-width:760px)").matches;
  screen.classList.toggle("sidebar-open", mobile && open);
  screen.classList.toggle("sidebar-collapsed", !mobile && !open);
  screen.classList.toggle("sidebar-visible", open);
  document
    .querySelectorAll("[data-sidebar-toggle]")
    .forEach((e) => e.setAttribute("aria-expanded", String(open)));
  // Stored under its old name, so a browser keeps the choice it has already made.
  write("library-collapsed", String(!open));
}
function toggleSidebar() {
  setSidebar(!screen.classList.contains("sidebar-visible"));
}
/**
 * One widget in the stack. `byHand` tells a click from restoring the view at launch: asking
 * for a widget opens the sidebar it lives in and puts the cursor in it, while a launch
 * leaves the sidebar as this browser last had it.
 */
function setWidget(id: SidebarWidgetId, visible: boolean, byHand = false) {
  el(widgetPanelId(id)).hidden = !visible;
  el(widgetToggleId(id)).setAttribute("aria-pressed", String(visible));
  write(widgetPreferenceKey(id), String(visible));
  renderRightSidebar();
  if (!visible) return;
  if (byHand && widgetSidebar(id) === "left-sidebar") setSidebar(true);
  renderWidgets();
  if (byHand) el(sidebarWidget(id).focus).focus();
}
const toggleWidget = (id: SidebarWidgetId) =>
  setWidget(id, !!el(widgetPanelId(id)).hidden, true);
/** The right sidebar takes a column only while it holds something open. */
function renderRightSidebar() {
  const open =
    screen.classList.contains("revisions-open") ||
    [...el("right-sidebar").querySelectorAll<HTMLElement>(".sidebar-widget")].some(
      (panel) => !panel.hidden,
    );
  screen.classList.toggle("right-sidebar-open", open);
}
function setHistory(open: boolean) {
  screen.classList.toggle("revisions-open", open);
  el("revisions-toggle").setAttribute("aria-expanded", String(open));
  renderRightSidebar();
  if (open) run(loadHistory);
}
const toggleHistory = () => setHistory(!screen.classList.contains("revisions-open"));
function updateNewFolderField() {
  const creating =
    el<HTMLSelectElement>("note-folder-input").value === NEW_FOLDER_OPTION;
  el("note-new-folder-field").hidden = !creating;
  const input = el<HTMLInputElement>("note-new-folder-input");
  input.disabled = !creating;
  input.required = creating;
}
function renderNoteFolders(folderList = folders()) {
  const picker = el<HTMLSelectElement>("note-folder-input");
  const optionsKey = JSON.stringify(folderList);
  if (picker.dataset.options === optionsKey) return;
  const selected = picker.value;
  const options: [string, string][] = [
    ["", "/ (repository root)"],
    ...folderList.map((folder): [string, string] => [folder, folder]),
    [NEW_FOLDER_OPTION, "New folder…"],
  ];
  picker.replaceChildren(
    ...options.map(([value, label]) => {
      const option = document.createElement("option");
      option.value = value;
      option.textContent = label;
      return option;
    }),
  );
  // Preserve the entered destination if another tab removes it while this form is open.
  picker.value =
    !selected || selected === NEW_FOLDER_OPTION || folderList.includes(selected)
      ? selected
      : NEW_FOLDER_OPTION;
  if (picker.value === NEW_FOLDER_OPTION && selected !== NEW_FOLDER_OPTION)
    el<HTMLInputElement>("note-new-folder-input").value = selected;
  picker.dataset.options = optionsKey;
  updateNewFolderField();
}
function newNoteFolder(): string {
  const selected = el<HTMLSelectElement>("library-folder").value;
  return selected === "." ? "" : selected || (current().path ? folderOf(current().path!) : "");
}
const dailyNotePath = (date: Date) =>
  notePath(`${DAILY_NOTE_FOLDER}/${dailyNoteName(date)}${noteExtension(DAILY_NOTE_FOLDER, keys())}`);
/** Opens a day's note under daily/, creating it the first time it is asked for. */
async function openDailyNote(date = new Date()) {
  const path = dailyNotePath(date);
  if (!paths().includes(path)) {
    if (isEncryptedFile(path) && !(await unlockedFor(path))) return;
    // The folder needs no .gitkeep: a folder exists once a note names it.
    assertDestination(path, paths());
    store.edit(path, "");
    openNote(path);
    await store.flush(path);
    await store.changed();
  }
  openNote(path);
}
/** A folder at the top of the vault, by name in any case: "Journal" serves as journal/. */
const topFolder = (name: string) =>
  folders().find((folder) => folder === name) ??
  folders().find((folder) => !folder.includes("/") && folder.toLocaleLowerCase() === name);
/**
 * Opens a blank note in `folder` named for when it was started. Like any blank note it is
 * stored once it has text, so one left empty leaves nothing behind.
 */
async function openTimedNote(folder: string) {
  const stem = `${folder}/${timedNoteName(new Date())}`;
  const extension = noteExtension(folder, keys());
  const taken = new Set(vaultPaths());
  let path = notePath(`${stem}${extension}`);
  for (let number = 2; taken.has(path); number++) path = notePath(`${stem} ${number}${extension}`);
  if (isEncryptedFile(path) && !(await unlockedFor(path))) return;
  assertDestination(path, vaultPaths());
  openNote(path);
}
const openScratchNote = () => openTimedNote(topFolder(SCRATCH_FOLDER) ?? SCRATCH_FOLDER);
async function openJournalNote() {
  const folder = topFolder(JOURNAL_FOLDER);
  // The journal is yours to set up, encrypted or not, so it is never made for you.
  if (folder === undefined)
    throw new Error("There is no journal folder. Create one named “journal” with New folder… or New encrypted folder… first.");
  await openTimedNote(folder);
}
async function createUntitledNote() {
  const index = activePane;
  const folder = newNoteFolder();
  await store.flush();
  if (store.state && encryptedFolderFor(folder) !== null) {
    // Even an empty encrypted note is stored encrypted, so the store creates it.
    const path = untitledNotePath(store.state, folder);
    if (isEncryptedFile(path) && !(await unlockedFor(path))) return;
    store.edit(path, "");
    await store.flush(path);
    await store.changed();
    openNote(path, index);
    return;
  }
  const note = await store.database.createUntitledNote(folder);
  await store.changed();
  openNote(note.path, index);
}
/** The note title is a button: pressing it is how you rename, move, pin, or archive it. */
function openNoteSettings(index: number) {
  activate(index);
  // An empty pane has its own OPEN NOTE button; there is nothing here to rename.
  if (current().path) openForm(true);
}
/** `folder` places a new note, as Alt-click in the Library tree does. */
function openForm(edit = false, folderChoice?: string) {
  modalPane = activePane;
  editingPath = edit ? current().path : null;
  el("note-modal-title").textContent = editingPath ? "Edit note" : "New note";
  el<HTMLInputElement>("note-title-input").value = editingPath
    ? titleOf(editingPath)
    : "";
  const folder = editingPath
    ? folderOf(editingPath)
    : folderChoice ?? newNoteFolder();
  renderNoteFolders();
  el<HTMLSelectElement>("note-folder-input").value =
    !folder || folders().includes(folder) ? folder : NEW_FOLDER_OPTION;
  el<HTMLInputElement>("note-new-folder-input").value =
    el<HTMLSelectElement>("note-folder-input").value === NEW_FOLDER_OPTION
      ? folder
      : "";
  updateNewFolderField();
  el<HTMLInputElement>("note-pinned-input").checked =
    !!editingPath && settings().pinned.includes(editingPath);
  el("archive-note-button").classList.toggle("hide", !editingPath);
  el("archive-note-button").textContent =
    editingPath && under(editingPath, ARCHIVE_ROOT) ? "UNARCHIVE" : "ARCHIVE";
  el("note-form-error").textContent = "";
  openModal("note-modal");
  el<HTMLInputElement>("note-title-input").focus();
}
async function submitForm() {
  const title = el<HTMLInputElement>("note-title-input").value.trim();
  const selected = el<HTMLSelectElement>("note-folder-input").value;
  const folder =
    selected === NEW_FOLDER_OPTION
      ? el<HTMLInputElement>("note-new-folder-input").value.trim()
      : selected;
  if (selected === NEW_FOLDER_OPTION && !folder)
    throw new Error("Enter a folder path or choose the repository root.");
  if (title.includes("/"))
    throw new Error("Put nested folders in the Folder field.");
  const moving = !!editingPath && !isBlankNote(editingPath);
  // An encrypted note stays encrypted; any other takes its folder's kind, so a plain
  // note moved into an encrypted folder is encrypted from then on.
  const extension = moving && isEncryptedNote(editingPath!) ? ".md.age" : noteExtension(folder, keys());
  const path = notePath(`${folder ? folder + "/" : ""}${title}${extension}`);
  if (editingPath && moving) {
    if (path !== editingPath && !(await moveNote(editingPath, path))) return;
    if (path === editingPath) await store.flush(editingPath);
  } else {
    assertDestination(path, paths());
    if (isEncryptedFile(path) && !(await unlockedFor(path))) return;
    store.edit(path, "");
    openNote(path, modalPane);
    await store.flush(path);
  }
  await store.database.pin(
    path,
    el<HTMLInputElement>("note-pinned-input").checked,
  );
  await store.changed();
  openNote(path, modalPane);
  closeModal("note-modal");
}
/** Where archiving takes a note: into the archive, or back out of it. */
const archivedPath = (path: string): string =>
  under(path, ARCHIVE_ROOT) ? path.slice(ARCHIVE_ROOT.length + 1) : `${ARCHIVE_ROOT}/${path}`;
async function archiveNote() {
  const source = editingPath;
  if (!source) return;
  await storeBlankNote(source);
  const path = archivedPath(source);
  if (!(await moveNote(source, path))) return;
  await store.changed();
  openNote(path, modalPane);
  closeModal("note-modal");
}
/** Archives the Library's selected note, or unarchives one, and selects `next`, the row after it. */
async function archiveFromLibrary(path: string, next: string | undefined) {
  await storeBlankNote(path);
  if (!(await moveNote(path, archivedPath(path)))) return;
  await store.changed();
  if (next !== undefined) libraryTreeSelection = next;
  render();
  focusLibraryRow(next);
  toast(`${under(path, ARCHIVE_ROOT) ? "Unarchived" : "Archived"} “${titleOf(path)}”.`);
}
/** Puts the cursor in the Library on the note being read, opening the sidebar if it is closed. */
function focusLibrary() {
  closeGuide(false);
  setSidebar(true);
  const path = current().path ?? undefined;
  if (path) {
    for (const folder of foldersAbove([path])) libraryOpenFolders.add(folder);
    libraryTreeSelection = path;
  }
  renderLibrary();
  focusLibraryRow(path);
}
/** Puts the cursor back in the Library, on the note list's row for `path` when the list shows. */
function focusLibraryRow(path: string | undefined) {
  if (libraryTreeVisible) {
    el("library-tree").focus();
    treeScrollSelection("library-tree");
    return;
  }
  const rows = [...el("note-list").querySelectorAll<HTMLElement>(".note-row")];
  (rows.find((row) => row.dataset.path === path) ?? rows[0])?.focus();
}
/**
 * The Library's single keys on a selected row. On a note: x archives it (or unarchives it),
 * s shows it beside the one being read, p pins it, and r or F2 renames it. On a folder r
 * or F2 edits it. On either: n starts a note in the row's folder, N a folder in it, m
 * moves the row, / finds in the Library, and Escape goes back to the note. Resolves
 * whether it took the key; `next` is the row to select once a note is archived.
 */
function libraryShortcut(event: KeyboardEvent, path: string, folder: boolean, next?: string): boolean {
  if (event.metaKey || event.ctrlKey || event.altKey || event.isComposing) return false;
  const here = folder ? path : folderOf(path);
  const rename = folder ? () => openFolderEdit(path) : () => showThen(path, () => openForm(true));
  const actions: Record<string, (() => void) | undefined> = {
    n: () => openForm(false, here),
    N: () => openFolderFormIn(here),
    x: folder ? undefined : () => run(() => archiveFromLibrary(path, next)),
    s: folder ? undefined : () => showBeside(path),
    p: folder ? undefined : () => run(() => togglePin(path)),
    // The root can't be renamed or moved.
    r: path ? rename : undefined,
    F2: path ? rename : undefined,
    m: path ? () => openMove(path, folder) : undefined,
    "/": () => {
      setLibraryFind(true);
      el("library-search").focus();
    },
    Escape: focusCurrentPane,
  };
  const action = actions[event.key];
  if (!action) return false;
  event.preventDefault();
  // Holding a key down acts once.
  if (!event.repeat) action();
  return true;
}
/** Archiving needs a stored note, so a blank one is written out first. */
async function storeBlankNote(path: string) {
  if (!isBlankNote(path)) return;
  store.edit(path, "");
  await store.flush(path);
  await store.changed();
}
/** Whether keys pressed in `target` type text: a field, a menu of choices, or an editor. */
const typingIn = (target: EventTarget | null): boolean =>
  target instanceof Element &&
  target.closest('input, textarea, select, [contenteditable]:not([contenteditable="false"])') !== null;
/** The browser's confirm dialog, which doesn't count as leaving the tab for the focus lock. */
function ask(message: string): boolean {
  ownDialog = true;
  try {
    return confirm(message);
  } finally {
    // Focus events queued while the dialog was open run first; this clears after them.
    setTimeout(() => (ownDialog = false));
  }
}
/** Asks first, then deletes; resolves whether the note went. */
async function deleteNote(path = current().path): Promise<boolean> {
  if (!path) return false;
  // A blank note was never stored, so there is nothing to delete or recover.
  if (isBlankNote(path)) {
    store.discardNewDraft(path);
    render();
    return true;
  }
  if (!ask(`Delete ${path}? A local recovery snapshot will be kept; the next checkpoint deletes the GitHub file.`))
    return false;
  await store.flush(path);
  const doc = store.state!.documents.find((d) => d.path === path)!;
  await store.database.delete(path, doc.revision);
  store.discardResolvedDraft([path]);
  await store.changed();
  render();
  return true;
}
function download(path: string, text: string) {
  const url = URL.createObjectURL(
    new Blob([text], { type: "text/markdown;charset=utf-8" }),
  );
  const a = document.createElement("a");
  a.href = url;
  a.download = path.split("/").at(-1)!;
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
function exportDraft() {
  // The export is the decrypted Markdown, so it is named as plain Markdown.
  if (current().path) download(current().path!.replace(/\.age$/, ""), textOf(current().path!));
}
async function copyDraft() {
  if (current().path) await copyText(textOf(current().path!), "Markdown");
}
/** Puts text on the clipboard and says what it was. */
async function copyText(text: string, what: string) {
  await navigator.clipboard.writeText(text);
  toast(`${what} copied.`);
}
function formatDocument() {
  const formatted = current().editor.formatDocument();
  if (formatted === null) toast("This note could not be formatted safely. Nothing changed.", true);
  else toast(formatted ? "Note formatted." : "Note is already formatted.");
}
async function copyTable() {
  const markdown = activeEditor().tableMarkdownAtCursor();
  if (!markdown) {
    toast("Put the cursor in a table first.");
    return;
  }
  await navigator.clipboard.writeText(markdown);
  toast("Table copied.");
}
// Ask AI rewrites a selection or a note through the Worker, which calls AI Gateway with
// the account's own key. Its model, thinking effort, and results mode sync like any setting.
type Pane = (typeof panes)[number];
const aiModel = () => {
  const model = read("ai-model");
  return isAiModelId(model) ? model : DEFAULT_AI_MODEL;
};
const aiReasoning = () => {
  const reasoning = read("ai-reasoning");
  return isAiReasoning(reasoning) ? reasoning : DEFAULT_AI_REASONING;
};
const aiApply = () => {
  const apply = read("ai-apply");
  return isAiApplyMode(apply) ? apply : DEFAULT_AI_APPLY;
};
/** The saved key's status for the signed-in account, once asked. */
let aiKey: { owner: number; configured: boolean; hint: string | null } | null = null;
/** The gateway's models for the key they were listed with. */
let aiModels: { key: string; models: AiModel[] } | null = null;
let aiModelChoices: AiModel[] = [];
let aiModelSelection = 0;
/** The note and selection Ask AI was opened on. */
let aiAsk: {
  pane: Pane;
  path: string;
  /** The note when the dialog opened. */
  doc: string;
  selection: { from: number; to: number; text: string } | null;
  /** Where the cursor was, when nothing was selected. */
  cursor: number | null;
} | null = null;
interface AiJob {
  pane: Pane;
  path: string;
  scope: AiScope;
  original: string;
  answer: string;
  thinking: boolean;
  /** Why the answer ended, once it has. */
  finish: string | null;
  error: string | null;
  /** Written into the note as it arrives, rather than previewed first. */
  stream: boolean;
  stop: AbortController;
}
let aiJob: AiJob | null = null;
const aiOwner = () => api.session.user?.id ?? null;
const aiKeyFor = () => (aiKey && aiKey.owner === aiOwner() ? aiKey : null);
const aiModelsFor = () => {
  const key = aiKeyFor();
  return key?.configured && aiModels?.key === `${key.owner}:${key.hint}` ? aiModels.models : null;
};
function finishProblem(finish: string): string | null {
  if (finish === "stop") return null;
  if (finish === "length") return "The answer was cut off at the model's output limit.";
  if (finish === "content-filter") return "The provider's content filter stopped the answer.";
  return `The answer stopped early (${finish}).`;
}
function openAiSettings() {
  openModal("ai-settings-modal");
  renderAiSettings();
  void loadAiKey();
}
async function loadAiKey() {
  const owner = aiOwner();
  if (owner === null) return;
  try {
    const status = await api.request<{ configured: boolean; hint: string | null }>("/ai/key");
    if (aiOwner() === owner) aiKey = { owner, ...status };
    el("ai-key-error").textContent = "";
  } catch (error) {
    el("ai-key-error").textContent = error instanceof Error ? error.message : "Couldn't check for a saved key.";
  }
  renderAiSettings();
}
function renderAiSettings() {
  const signedIn = aiOwner() !== null;
  const key = aiKeyFor();
  const busy = el("ai-key-form").classList.contains("busy");
  el<HTMLInputElement>("ai-key-input").disabled = !signedIn || busy;
  el<HTMLButtonElement>("ai-key-save").disabled = !signedIn || busy;
  el<HTMLButtonElement>("ai-key-remove").disabled = !signedIn || busy || !key?.configured;
  el("ai-key-status").textContent = !signedIn
    ? "Log in with Save to GitHub to keep a key with your account."
    : busy
      ? "Checking the key with AI Gateway…"
      : !key
        ? "Checking for a saved key…"
        : key.configured
          ? `A key ending in …${key.hint} is saved with your account. Paste another to replace it.`
          : "No key yet. Create one in your Vercel dashboard under AI Gateway → API Keys.";
  const model = el<HTMLInputElement>("ai-model");
  if (document.activeElement !== model) model.value = aiModel();
  const known = aiModelsFor()?.find((candidate) => candidate.id === aiModel());
  el("ai-model-hint").textContent = known
    ? known.name
    : key?.configured
      ? "Type to search the gateway's models, or any creator/model id."
      : "Save a key to search the gateway's models.";
  el<HTMLSelectElement>("ai-reasoning").value = aiReasoning();
  el<HTMLSelectElement>("ai-apply").value = aiApply();
}
async function saveAiKey() {
  const input = el<HTMLInputElement>("ai-key-input");
  const apiKey = input.value.trim();
  if (!apiKey) {
    el("ai-key-error").textContent = "Paste a key first.";
    return;
  }
  const owner = aiOwner();
  el("ai-key-error").textContent = "";
  el("ai-key-form").classList.add("busy");
  renderAiSettings();
  try {
    const status = await api.request<{ configured: boolean; hint: string | null }>("/ai/key", "POST", { apiKey });
    if (owner !== null && aiOwner() === owner) aiKey = { owner, ...status };
    input.value = "";
  } catch (error) {
    el("ai-key-error").textContent = error instanceof Error ? error.message : "The key couldn't be saved.";
  } finally {
    el("ai-key-form").classList.remove("busy");
  }
  renderAiSettings();
}
async function removeAiKey() {
  const owner = aiOwner();
  if (owner === null || !ask("Remove the AI Gateway key from your account? Ask AI stops working until you add one.")) return;
  try {
    const status = await api.request<{ configured: boolean; hint: string | null }>("/ai/key", "DELETE");
    if (aiOwner() === owner) aiKey = { owner, ...status };
    el("ai-key-error").textContent = "";
  } catch (error) {
    el("ai-key-error").textContent = error instanceof Error ? error.message : "The key couldn't be removed.";
  }
  renderAiSettings();
}
async function loadAiModels() {
  const key = aiKeyFor();
  if (!key?.configured || aiModelsFor()) return;
  try {
    const { models } = await api.request<{ models: AiModel[] }>("/ai/models");
    if (aiKeyFor() === key) aiModels = { key: `${key.owner}:${key.hint}`, models };
  } catch (error) {
    el("ai-model-hint").textContent = `The model list didn't load: ${error instanceof Error ? error.message : "try again"}`;
    return;
  }
  if (document.activeElement === el("ai-model")) renderAiModelOptions(el<HTMLInputElement>("ai-model").dataset.query ?? "");
  renderAiSettings();
}
/** The models matching `query` under the model field; null closes the list. */
function renderAiModelOptions(query: string | null) {
  const input = el<HTMLInputElement>("ai-model");
  const list = el("ai-model-options");
  const models = aiModelsFor();
  aiModelChoices = query === null || !models ? [] : filterAiModels(models, query);
  aiModelSelection = Math.max(0, Math.min(aiModelSelection, aiModelChoices.length - 1));
  list.hidden = !aiModelChoices.length;
  input.setAttribute("aria-expanded", String(!list.hidden));
  list.replaceChildren(
    ...aiModelChoices.map((model, index) => {
      const option = document.createElement("button");
      option.type = "button";
      option.tabIndex = -1;
      option.id = `ai-model-option-${index}`;
      option.className = "ai-model-option" + (index === aiModelSelection ? " selected" : "");
      option.setAttribute("role", "option");
      option.setAttribute("aria-selected", String(index === aiModelSelection));
      const name = document.createElement("span");
      name.className = "ai-model-name";
      name.textContent = model.name;
      option.append(model.id, name);
      // Keep the field focused, so choosing doesn't count as leaving it.
      option.onpointerdown = (event) => event.preventDefault();
      option.onclick = () => chooseAiModel(model.id);
      return option;
    }),
  );
  if (list.hidden) input.removeAttribute("aria-activedescendant");
  else {
    input.setAttribute("aria-activedescendant", `ai-model-option-${aiModelSelection}`);
    list.querySelector(".selected")?.scrollIntoView({ block: "nearest" });
  }
}
function chooseAiModel(id: string) {
  write("ai-model", id);
  el<HTMLInputElement>("ai-model").value = id;
  renderAiModelOptions(null);
  renderAiSettings();
}
/** Keeps a typed model id once you leave the field, or puts back the saved one. */
function commitAiModel() {
  const input = el<HTMLInputElement>("ai-model");
  const value = input.value.trim();
  renderAiModelOptions(null);
  if (value === aiModel()) return;
  if (isAiModelId(value)) write("ai-model", value);
  else input.value = aiModel();
  renderAiSettings();
  if (value && !isAiModelId(value))
    el("ai-model-hint").textContent = "A model id looks like creator/model, such as anthropic/claude-sonnet-5.5.";
}
function aiModelKeydown(event: KeyboardEvent) {
  const open = !el("ai-model-options").hidden;
  if (event.key === "ArrowDown" || event.key === "ArrowUp") {
    event.preventDefault();
    if (!open) {
      el("ai-model").dataset.query = "";
      renderAiModelOptions("");
      return;
    }
    const step = event.key === "ArrowDown" ? 1 : -1;
    aiModelSelection = (aiModelSelection + step + aiModelChoices.length) % aiModelChoices.length;
    renderAiModelOptions(el<HTMLInputElement>("ai-model").dataset.query ?? "");
  } else if (event.key === "Enter") {
    event.preventDefault();
    const choice = open ? aiModelChoices[aiModelSelection] : undefined;
    if (choice) chooseAiModel(choice.id);
    else commitAiModel();
  } else if (event.key === "Escape" && open) {
    // Close the list, not the dialog.
    event.preventDefault();
    event.stopPropagation();
    renderAiModelOptions(null);
  }
}
/**
 * Opens Ask AI on the active pane's note: on its selection if it has one, otherwise to write
 * at the cursor when `write` (from /ai), or about the whole note (from the panel).
 */
function openAi(write = false) {
  const pane = current();
  const path = pane.path;
  if (!path) {
    toast("Open a note first.");
    return;
  }
  if (isEncryptedFile(path)) {
    toast("Ask AI is off in encrypted notes, so their text never leaves this browser.", true);
    return;
  }
  if (aiOwner() === null) {
    toast("Log in with Save to GitHub to use Ask AI.");
    openAiSettings();
    return;
  }
  if (aiJob?.stream) {
    toast("AI is still writing into a note. Esc stops it.");
    return;
  }
  cancelAi();
  const selection = pane.editor.getSelection();
  aiAsk = { pane, path, doc: pane.editor.getValue(), selection, cursor: selection ? null : pane.editor.getCursor() };
  const words = selection?.text.trim().split(/\s+/).filter(Boolean).length ?? 0;
  el("ai-selection-size").textContent = selection ? ` · ${words} WORD${words === 1 ? "" : "S"}` : "";
  el<HTMLInputElement>(selection ? "ai-scope-selection" : write ? "ai-scope-cursor" : "ai-scope-note").checked = true;
  el<HTMLInputElement>("ai-instruction").value = "";
  el("ai-title").textContent = `ASK AI · ${titleOf(path).toUpperCase()}`;
  markAiScope();
  renderAi();
  openModal("ai-modal");
}
const aiScope = (): AiScope =>
  el<HTMLInputElement>("ai-scope-selection").checked ? "selection" : el<HTMLInputElement>("ai-scope-cursor").checked ? "cursor" : "note";
/** Marks what the chosen scope rewrites, or where it writes, and words the prompt to suit. */
function markAiScope() {
  if (!aiAsk) return;
  const { pane, selection, cursor } = aiAsk;
  const scope = aiScope();
  if (scope === "selection" && selection) pane.editor.showAiTarget(selection.from, selection.to);
  else if (scope === "cursor" && cursor !== null) pane.editor.showAiTarget(cursor, cursor);
  else pane.editor.clearAiTarget();
  el<HTMLInputElement>("ai-instruction").placeholder =
    scope === "cursor" ? "Write a summary, list next steps, draft an intro…" : "Fix this Markdown, make it professional…";
}
/** The answer ready for the note: new text at the cursor, or in place of the text it rewrote. */
const aiAnswer = (job: AiJob, below = false) =>
  job.scope === "cursor" ? cleanAiInsertion(job.answer) : cleanAiAnswer(job.answer, below ? job.original.trim() : job.original);
/** A previewed answer has arrived in full and can go into the note. */
const aiReady = () => !!aiJob && !aiJob.stream && aiJob.finish !== null && !aiJob.error;
function renderAi() {
  const job = aiJob && !aiJob.stream ? aiJob : null;
  const running = !!job && job.finish === null && !job.error;
  const ready = aiReady();
  const writing = job?.scope === "cursor";
  el("ai-model-button").textContent = `${aiModel()} · ${AI_REASONING_LABELS[aiReasoning()].toUpperCase()} ►`;
  el<HTMLInputElement>("ai-scope-selection").disabled = running || !aiAsk?.selection;
  el<HTMLInputElement>("ai-scope-cursor").disabled = running || aiAsk?.cursor == null;
  el<HTMLInputElement>("ai-scope-note").disabled = running;
  el("ai-stop").hidden = !running;
  el("ai-discard").hidden = !job || running;
  el("ai-replace").hidden = !ready;
  el("ai-replace").textContent = writing ? "INSERT" : "REPLACE";
  el("ai-below").hidden = !ready || writing;
  const result = el("ai-result");
  result.hidden = !job || (!job.answer && !ready);
  if (job && ready) {
    const changes = aiChanges(job.original, aiAnswer(job));
    result.replaceChildren(
      ...(changes.length ? changes : [{ kind: "skip" as const, lines: 0 }]).map((change) => {
        const line = document.createElement("div");
        line.className = `ai-line ai-line-${change.kind}`;
        line.textContent =
          change.kind === "skip"
            ? change.lines
              ? `⋯ ${change.lines} unchanged line${change.lines === 1 ? "" : "s"}`
              : writing ? "The answer was empty." : "No changes."
            : `${change.kind === "add" ? "+" : change.kind === "remove" ? "-" : " "} ${change.text}`;
        return line;
      }),
    );
    result.scrollTop = 0;
  } else if (job) {
    result.textContent = job.answer;
    result.scrollTop = result.scrollHeight;
  }
  const status = el("ai-status");
  status.classList.toggle("error", !!job?.error);
  status.textContent = !job
    ? ""
    : job.error
      ? job.error
      : running
        ? job.answer ? "Writing…" : job.thinking ? "Thinking…" : "Asking…"
        : finishProblem(job.finish!) ?? "";
  const keys = ready
    ? [...(writing ? ["⇧ENTER INSERT"] : ["⌘ENTER REPLACE", "⇧ENTER INSERT BELOW"]), "ENTER ASK AGAIN", "ESC DISCARD"]
    : running
      ? ["ESC CANCEL"]
      : ["ENTER ASK", "ESC CLOSE"];
  el("ai-footer").replaceChildren(
    ...keys.map((text) => {
      const span = document.createElement("span");
      span.textContent = text;
      return span;
    }),
  );
}
let aiFrame = 0;
/** Shows the answer so far: in the note while it streams there, otherwise in the dialog. */
function drawAiAnswer() {
  aiFrame = 0;
  const job = aiJob;
  if (!job) return;
  if (!job.stream) renderAi();
  else if (!job.pane.editor.streamAiDraft(job.answer)) {
    cancelAi();
    toast("The text changed while AI was writing, so its answer was dropped.", true);
  }
}
/** Stops the answer in progress, if any, and forgets where it was going. */
function cancelAi() {
  const job = aiJob;
  aiJob = null;
  job?.stop.abort();
  job?.pane.editor.clearAiTarget();
  if (aiAsk && aiAsk.pane !== job?.pane) aiAsk.pane.editor.clearAiTarget();
}
async function askAi() {
  const ask = aiAsk;
  if (!ask || (aiJob && !aiJob.stream && aiJob.finish === null && !aiJob.error)) return;
  const instruction = el<HTMLInputElement>("ai-instruction").value.trim();
  const fail = (message: string) => {
    el("ai-status").textContent = message;
    el("ai-status").classList.add("error");
  };
  if (!instruction) return fail("Say what AI should do, like “fix this Markdown”.");
  const { pane, path, selection, cursor } = ask;
  if (pane.path !== path) return fail("The note changed. Close this and ask again.");
  const scope = aiScope();
  const doc = pane.editor.getValue();
  if (scope === "selection" && selection && doc.slice(selection.from, selection.to) !== selection.text)
    return fail("The selection changed. Close this and select the text again.");
  // The spot to write at is only an offset, so any edit since the dialog opened may have moved it.
  if (scope === "cursor" && doc !== ask.doc) return fail("The note changed. Close this and ask again.");
  const original = scope === "selection" ? selection!.text : scope === "note" ? doc : "";
  if (scope !== "cursor" && !original.trim()) return fail("There's nothing to rewrite yet.");
  if (original.length > MAX_AI_TEXT_LENGTH)
    return fail(`That's too long to send. Select up to ${MAX_AI_TEXT_LENGTH.toLocaleString()} characters.`);
  cancelAi();
  const [from, to] =
    scope === "selection" ? [selection!.from, selection!.to] : scope === "cursor" ? [cursor!, cursor!] : [0, doc.length];
  pane.editor.showAiTarget(from, to);
  const job: AiJob = {
    pane,
    path,
    scope,
    original,
    answer: "",
    thinking: false,
    finish: null,
    error: null,
    stream: aiApply() === "stream",
    stop: new AbortController(),
  };
  aiJob = job;
  if (job.stream) {
    aiAsk = null;
    closeModal("ai-modal");
    pane.editor.streamAiDraft("");
    pane.editor.focus();
    toast("AI is writing into the note. Esc stops it.");
  } else renderAi();
  const onEvent = (event: AiStreamEvent) => {
    if (aiJob !== job) return;
    if (event.type === "thinking") job.thinking = true;
    else if (event.type === "error") job.error = event.message;
    else if (event.type === "done") job.finish = event.finish;
    else if (event.type === "text") {
      job.answer += event.text;
      // Answers arrive a few characters at a time; draw them once a frame.
      if (!aiFrame) aiFrame = requestAnimationFrame(drawAiAnswer);
      return;
    }
    if (!job.stream) renderAi();
  };
  try {
    await api.stream<AiStreamEvent>(
      "/ai/edit",
      {
        model: aiModel(),
        reasoning: aiReasoning(),
        instruction,
        scope,
        text: original,
        ...(scope === "note" ? {} : aiSelectionContext(doc, from, to)),
      },
      onEvent,
      job.stop.signal,
    );
  } catch (error) {
    if (job.stop.signal.aborted) return;
    job.error = error instanceof Error ? error.message : "Ask AI failed.";
  }
  if (aiJob !== job) return;
  if (!job.error && job.finish === null) job.error = "The connection closed before the answer finished.";
  if (!job.stream) {
    renderAi();
    return;
  }
  // Streaming writes the answer for good only if it all arrived.
  const problem = job.error ?? finishProblem(job.finish!) ?? (scope === "cursor" && !aiAnswer(job) ? "The answer was empty." : null);
  aiJob = null;
  if (problem) {
    pane.editor.clearAiTarget();
    toast(`${problem} The note was left as it was.`, true);
  } else if (pane.path !== path || !pane.editor.applyAiAnswer(aiAnswer(job)))
    toast("The text changed while AI was writing, so its answer was dropped.", true);
  else toast(`${scope === "cursor" ? "AI wrote at the cursor" : `AI rewrote the ${scope}`}. ⌘Z undoes it.`);
}
/** Stops a previewed answer part way; what arrived stays to read but can't be applied. */
function stopAi() {
  if (!aiJob || aiJob.stream || aiJob.finish !== null || aiJob.error) return;
  aiJob.error = "Stopped. Ask again, or close this.";
  aiJob.stop.abort();
  renderAi();
}
/** Puts a previewed answer in place of the text it rewrote, below it, or in at the cursor. */
function applyAi(below: boolean) {
  const job = aiJob;
  if (!job || !aiReady()) return;
  const writing = job.scope === "cursor";
  const answer = aiAnswer(job, below);
  if ((below || writing) && !answer) return;
  if (job.pane.path !== job.path || !job.pane.editor.applyAiAnswer(answer, below && !writing)) {
    job.error = "The note changed while AI was working. Ask again.";
    renderAi();
    return;
  }
  aiJob = null;
  aiAsk = null;
  closeModal("ai-modal");
  job.pane.editor.focus();
  toast(
    `${writing ? "Inserted the answer" : below ? "Added the answer below" : `Replaced the ${job.scope}`}. ⌘Z undoes it.`,
  );
}
/** Where Insert image puts the picture, and the selected words that become its alt text. */
let imageTarget: { editor: MarkdownEditor; alt: string } | null = null;
/**
 * Insert image: a selected web address becomes the picture at once; otherwise the dialog
 * asks for one, and selected words on one line become its alt text.
 */
function insertImage(editor: MarkdownEditor) {
  const selected = editor.getSelection()?.text ?? "";
  const markdown = imageMarkdownForUrl(selected);
  if (markdown) {
    editor.insertText(markdown);
    return;
  }
  imageTarget = { editor, alt: selected.includes("\n") ? "" : selected };
  el<HTMLInputElement>("image-url").value = "";
  el("image-error").textContent = "";
  openModal("image-modal");
}
function submitImage() {
  const markdown = imageMarkdownForUrl(el<HTMLInputElement>("image-url").value, imageTarget?.alt);
  if (!markdown) {
    el("image-error").textContent = "Enter the image's URL, starting with https://.";
    return;
  }
  const target = imageTarget;
  imageTarget = null;
  // Closing gives the note back the cursor, where the picture goes.
  closeModal("image-modal");
  target?.editor.insertText(markdown);
}
// Insert FIGlet text draws what is typed in each of six fonts, to pick one by sight.
/** The font picked last, offered first next time. */
let figletFont: FigletFontId = FIGLET_FONTS[0].id;
let figletTarget: MarkdownEditor | null = null;
/** The renderer and its fonts, loaded the first time the dialog opens. */
let figletRender: ((text: string, font: FigletFontId) => string) | null = null;
/** Opens Insert FIGlet text for `editor`, with the words selected there to start from. */
function openFiglet(editor: MarkdownEditor) {
  figletTarget = editor;
  const selected = editor.getSelection()?.text ?? "";
  el<HTMLInputElement>("figlet-input").value = selected.includes("\n") ? "" : selected.trim().slice(0, 60);
  el("figlet-error").textContent = "";
  if (!el("figlet-fonts").childElementCount) buildFigletFonts();
  openModal("figlet-modal");
  renderFiglet();
  if (!figletRender)
    void import("./figlet-text")
      .then(({ figletText }) => {
        figletRender = figletText;
        renderFiglet();
      })
      .catch(() => {
        el("figlet-error").textContent = "The fonts didn't load. Close this and try again.";
      });
}
/** One choice per font: its name over the text drawn in it. */
function buildFigletFonts() {
  const group = el("figlet-fonts");
  group.replaceChildren(
    ...FIGLET_FONTS.map(({ id, label }) => {
      const card = document.createElement("button");
      card.type = "button";
      card.className = "figlet-font";
      card.dataset.font = id;
      card.setAttribute("role", "radio");
      card.setAttribute("aria-label", label);
      const name = document.createElement("span");
      name.className = "figlet-font-name";
      name.textContent = label;
      const preview = document.createElement("pre");
      preview.className = "figlet-preview";
      preview.setAttribute("aria-hidden", "true");
      card.append(name, preview);
      card.onclick = () => chooseFigletFont(id);
      card.ondblclick = submitFiglet;
      return card;
    }),
  );
  group.onkeydown = (event) => {
    const step = { ArrowRight: 1, ArrowDown: 1, ArrowLeft: -1, ArrowUp: -1 }[event.key];
    if (event.key === "Enter") submitFiglet();
    else if (step !== undefined) stepFigletFont(step, true);
    else if (event.key === "Home" || event.key === "End")
      chooseFigletFont(FIGLET_FONTS.at(event.key === "Home" ? 0 : -1)!.id, true);
    else return;
    event.preventDefault();
  };
}
function chooseFigletFont(font: FigletFontId, focus = false) {
  figletFont = font;
  for (const card of el("figlet-fonts").querySelectorAll<HTMLElement>(".figlet-font")) {
    const chosen = card.dataset.font === font;
    card.setAttribute("aria-checked", String(chosen));
    card.tabIndex = chosen ? 0 : -1;
    if (chosen && focus) card.focus();
  }
}
function stepFigletFont(step: number, focus = false) {
  const at = FIGLET_FONTS.findIndex(({ id }) => id === figletFont);
  chooseFigletFont(FIGLET_FONTS[(at + step + FIGLET_FONTS.length) % FIGLET_FONTS.length]!.id, focus);
}
/** Draws the text, or a sample until there is some, in every font, each shrunk to fit its box. */
function renderFiglet() {
  const text = el<HTMLInputElement>("figlet-input").value.trim() || FIGLET_SAMPLE;
  for (const card of el("figlet-fonts").querySelectorAll<HTMLElement>(".figlet-font")) {
    const preview = card.querySelector<HTMLElement>(".figlet-preview")!;
    preview.textContent = figletRender ? figletRender(text, card.dataset.font as FigletFontId) : "…";
    preview.style.fontSize = "";
    const { clientWidth, scrollWidth } = preview;
    if (scrollWidth > clientWidth && clientWidth > 0) {
      const size = parseFloat(getComputedStyle(preview).fontSize);
      preview.style.fontSize = `${Math.max(3, Math.floor((size * clientWidth * 10) / scrollWidth) / 10)}px`;
    }
  }
  chooseFigletFont(figletFont);
}
function submitFiglet() {
  if (!figletRender) return;
  const text = el<HTMLInputElement>("figlet-input").value.trim();
  if (!text) {
    el("figlet-error").textContent = "Type the text to draw.";
    el("figlet-input").focus();
    return;
  }
  const art = figletRender(text, figletFont);
  if (!art.trim()) {
    el("figlet-error").textContent = "This font has none of those characters. Try another.";
    return;
  }
  const target = figletTarget;
  figletTarget = null;
  // Closing gives the note back the cursor, where the lettering goes.
  closeModal("figlet-modal");
  target?.insertBlock(figletBlock(art));
}
function openCheckpoint() {
  el<HTMLInputElement>("checkpoint-label").value =
    `Checkpoint · ${new Date().toLocaleString()}`;
  el("checkpoint-error").textContent = "";
  openModal("checkpoint-modal");
}
async function saveNotes(all = false) {
  if (fileSaveBusy) return;
  const path = current().path;
  if (!all && !path) return;
  fileSaveBusy = true;
  render();
  try {
    await store.flush();
    const vault = store.state?.vault;
    if (
      !vault?.connection ||
      !navigator.onLine ||
      (!vault.baseCommitSha && !vault.remoteLoaded)
    ) {
      toast(
        !vault?.connection
          ? "Saved locally. Connect GitHub to checkpoint your notes."
          : !navigator.onLine
            ? "Saved locally. GitHub is unavailable while offline."
            : "Saved locally. Refresh the repository before checkpointing.",
      );
      return;
    }
    await sync.save(
      all ? "Save all notes" : `Save ${path}`,
      all ? undefined : [path!],
    );
    toast(
      all
        ? "All changes checkpointed to GitHub."
        : `“${path}” checkpointed to GitHub.`,
    );
  } finally {
    fileSaveBusy = false;
    render();
  }
}
function showFolderEncryption(encrypt: boolean) {
  el<HTMLInputElement>("folder-encrypt").checked = encrypt;
  el("folder-encryption-fields").hidden = !encrypt;
  for (const id of ["folder-passphrase", "folder-passphrase-confirm"]) {
    el<HTMLInputElement>(id).required = encrypt;
    el<HTMLInputElement>(id).disabled = !encrypt || folderBusy || folderCompleted;
  }
}
function openFolderForm(encrypt = false) {
  if (!folderBusy) {
    folderCompleted = false;
    el<HTMLInputElement>("folder-path").value = "";
    el<HTMLInputElement>("folder-passphrase").value = "";
    el<HTMLInputElement>("folder-passphrase-confirm").value = "";
    showFolderEncryption(encrypt);
    iconChoices.set("folder-icon-picker", { icon: null });
    renderIconPicker("folder-icon-picker");
    el("folder-progress").hidden = true;
    el("folder-error").textContent = "";
    el("folder-submit").hidden = false;
    el("folder-submit").textContent = "CREATE";
    el("folder-close").textContent = "CANCEL";
  }
  renderFolderProgress();
  openModal("folder-modal");
}
function renderFolderProgress() {
  el<HTMLInputElement>("folder-path").disabled = folderBusy || folderCompleted;
  el<HTMLInputElement>("folder-encrypt").disabled = folderBusy || folderCompleted;
  showFolderEncryption(el<HTMLInputElement>("folder-encrypt").checked);
  el<HTMLButtonElement>("folder-submit").disabled = folderBusy;
  el("folder-spinner").hidden = !folderBusy;
  el("folder-form").setAttribute("aria-busy", String(folderBusy));
  if (!folderBusy) return;
  el("folder-progress").hidden = false;
  el("folder-close").textContent = "HIDE";
  const folder = el<HTMLInputElement>("folder-path").value.trim();
  const created = store.state?.documents.some((d) =>
    [`${folder}/.gitkeep`, `${folder}/${FOLDER_KEY_NAME}`].includes(d.path),
  );
  el("folder-progress-message").textContent = !created
    ? "Creating folder locally…"
    : sync.status === "waiting"
      ? "Folder created locally. Waiting for GitHub sync…"
      : sync.status === "checking" || sync.status === "checkpointing"
        ? "Committing folder to GitHub… You can keep editing."
        : "Folder created locally. Preparing its GitHub checkpoint…";
}
async function submitFolder() {
  if (folderBusy || folderCompleted) return;
  const path = relativePath(el<HTMLInputElement>("folder-path").value.trim());
  el<HTMLInputElement>("folder-path").value = path;
  const encrypt = el<HTMLInputElement>("folder-encrypt").checked;
  const passphrase = el<HTMLInputElement>("folder-passphrase").value;
  if (encrypt) {
    if (passphrase.length < MIN_PASSPHRASE_LENGTH)
      throw new Error(`Use a passphrase of at least ${MIN_PASSPHRASE_LENGTH} characters.`);
    if (passphrase !== el<HTMLInputElement>("folder-passphrase-confirm").value)
      throw new Error("The passphrases don't match.");
    // Checked again when the folder is stored; this spares a key nobody will use.
    if (folders().includes(path))
      throw new Error(`“${path}” already exists. Only a new folder can be encrypted.`);
  }
  folderBusy = true;
  el("folder-error").textContent = "";
  renderFolderProgress();
  let key: string | undefined;
  try {
    if (encrypt) {
      el("folder-progress-message").textContent = "Creating the folder key…";
      const created = await createFolderKey(passphrase);
      // The folder that holds the new key starts unlocked.
      await store.keyring.add(path, created.identity);
      key = created.file;
      lastActivity = Date.now();
    }
    const result = await sync.createFolder(path, key);
    await saveFolderIcon(path, "folder-icon-picker");
    el<HTMLInputElement>("folder-passphrase").value = "";
    el<HTMLInputElement>("folder-passphrase-confirm").value = "";
    el<HTMLSelectElement>("library-folder").value = path;
    renderLibrary();
    folderCompleted = true;
    const kind = encrypt ? "Encrypted folder" : "Folder";
    const message =
      result === "checkpointed"
        ? `${kind} “${path}” committed to GitHub.`
        : result === "existing"
          ? `Folder “${path}” already exists.`
          : `${kind} “${path}” created locally. It will checkpoint when GitHub is connected and online.`;
    el("folder-progress-message").textContent = message;
    el("folder-submit").hidden = true;
    if (!el("folder-modal").classList.contains("active")) toast(message);
  } catch (error) {
    // Keep the new key only if it is the one the folder stored.
    if (encrypt && store.state?.documents.find((d) => d.path === `${path}/${FOLDER_KEY_NAME}`)?.text !== key)
      store.keyring.lock(path);
    el("folder-progress").hidden = true;
    el("folder-error").textContent =
      error instanceof Error
        ? error.message
        : "Folder creation failed. Please retry.";
    el("folder-submit").textContent = "TRY AGAIN";
    if (!el("folder-modal").classList.contains("active"))
      toast(el("folder-error").textContent!, true);
  } finally {
    folderBusy = false;
    el("folder-close").textContent = "CLOSE";
    renderFolderProgress();
  }
}
/** The icon tiles of a folder form: none, the built-in set, emoji in use, then uploaded icons. */
function renderIconPicker(id: string) {
  const choice = iconChoices.get(id) ?? { icon: null };
  const uploads = {
    ...settings().custom_icons,
    ...(choice.upload ? { [choice.upload.id]: choice.upload.data } : {}),
  };
  // Emoji other folders show are offered again, along with the one just typed.
  const emoji = [...new Set([...Object.values(settings().icons), choice.icon].map(emojiOf))].filter(
    (glyph) => glyph !== null,
  );
  const refs = [
    null,
    ...FOLDER_ICONS.map((icon) => icon.id),
    ...emoji.map(emojiIconRef),
    ...Object.keys(uploads).map(customIconRef),
  ];
  // A chosen icon that was forgotten in another tab is no choice at all.
  if (!refs.includes(choice.icon)) choice.icon = null;
  el(id).replaceChildren(
    ...refs.map((ref) => {
      const tile = document.createElement("button");
      tile.type = "button";
      tile.className = "icon-choice";
      tile.setAttribute("role", "radio");
      tile.setAttribute("aria-checked", String(ref === choice.icon));
      // Arrow keys move between the tiles, so only the chosen one is a Tab stop.
      tile.tabIndex = ref === choice.icon ? 0 : -1;
      const label = ref === null ? "No icon" : iconLabel(ref);
      tile.setAttribute("aria-label", label);
      tile.title = label;
      tile.dataset.icon = ref ?? "";
      const icon = ref === null ? null : iconElement(ref, uploads);
      if (icon) tile.append(icon);
      else tile.textContent = "—";
      tile.onclick = () => chooseIcon(id, ref);
      if (ref === null || customIconId(ref) === null) return tile;
      // An uploaded icon shows an × on hover that deletes it.
      const remove = document.createElement("button");
      remove.type = "button";
      remove.className = "icon-delete";
      // Delete or Backspace on the tile does the same from the keyboard.
      remove.tabIndex = -1;
      remove.textContent = "×";
      remove.setAttribute("aria-label", "Delete this uploaded icon");
      remove.title = "Delete this uploaded icon";
      remove.onclick = () => void deleteUploadedIcon(id, ref).catch(showIconError(id));
      const wrap = document.createElement("span");
      wrap.className = "icon-choice-wrap";
      wrap.append(tile, remove);
      return wrap;
    }),
  );
  const typed = el<HTMLInputElement>(`${id}-emoji`);
  if (document.activeElement !== typed) typed.value = emojiOf(choice.icon) ?? "";
}
function chooseIcon(id: string, icon: string | null, focus = true) {
  iconChoices.set(id, { ...iconChoices.get(id), icon });
  renderIconPicker(id);
  if (focus) el(id).querySelector<HTMLElement>('[aria-checked="true"]')?.focus();
}
/** Picks the emoji typed or pasted into a picker's Emoji field, once it is exactly one. */
function typeEmoji(id: string) {
  const emoji = el<HTMLInputElement>(`${id}-emoji`).value.trim();
  const error = el(el(id).dataset.error!);
  error.textContent = emoji && !isEmoji(emoji) ? "Type or paste one emoji." : "";
  if (isEmoji(emoji)) chooseIcon(id, emojiIconRef(emoji), false);
}
function iconPickerKeydown(id: string, event: KeyboardEvent) {
  const tiles = [...el(id).querySelectorAll<HTMLElement>(".icon-choice")];
  const index = tiles.indexOf(document.activeElement as HTMLElement);
  if (index < 0) return;
  const ref = tiles[index]!.dataset.icon || null;
  if ((event.key === "Delete" || event.key === "Backspace") && ref && customIconId(ref) !== null) {
    event.preventDefault();
    void deleteUploadedIcon(id, ref).catch(showIconError(id));
    return;
  }
  // Up and down move a row of the grid as it is laid out right now.
  const top = (tile: HTMLElement) => tile.getBoundingClientRect().top;
  const columns = Math.max(1, tiles.filter((tile) => top(tile) === top(tiles[0]!)).length);
  const step = { ArrowLeft: -1, ArrowRight: 1, ArrowUp: -columns, ArrowDown: columns }[event.key];
  const next =
    event.key === "Home" ? 0 : event.key === "End" ? tiles.length - 1 : step === undefined ? -1 : index + step;
  if (next < 0 || next >= tiles.length) return;
  event.preventDefault();
  chooseIcon(id, tiles[next]!.dataset.icon || null);
}
/** Reads an image chosen for a picker as a new uploaded icon; it is kept once the form saves. */
async function uploadIcon() {
  const input = el<HTMLInputElement>("folder-icon-upload");
  const file = input.files?.[0];
  input.value = "";
  if (!file) return;
  const id = iconUploadPicker;
  if (Object.keys(settings().custom_icons).length >= MAX_CUSTOM_ICONS)
    throw new Error(`Keep up to ${MAX_CUSTOM_ICONS} uploaded icons. Forget one you no longer use first.`);
  const upload = { id: newCustomIconId(), data: await rasterizeIcon(file) };
  iconChoices.set(id, { icon: customIconRef(upload.id), upload });
  renderIconPicker(id);
  el(id).querySelector<HTMLElement>('[aria-checked="true"]')?.focus();
}
/** Shows a picker's error in the form around it. */
const showIconError = (id: string) => (error: Error) => {
  el(el(id).dataset.error!).textContent = error.message;
};
/**
 * Deletes an uploaded icon from a picker, once confirmed, taking it off every folder that
 * shows it. One uploaded in this form and not saved yet just leaves it.
 */
async function deleteUploadedIcon(id: string, ref: string) {
  const choice = iconChoices.get(id) ?? { icon: null };
  const upload = customIconId(ref)!;
  const chosen = choice.icon === ref ? null : choice.icon;
  if (upload in settings().custom_icons) {
    const users = Object.values(settings().icons).filter((icon) => icon === ref).length;
    if (
      !ask(
        users
          ? `Delete this uploaded icon? ${users === 1 ? "The folder showing it goes" : `The ${users} folders showing it go`} back to no icon.`
          : "Delete this uploaded icon?",
      )
    )
      return;
    await store.database.forgetUploadedIcon(upload);
    await store.changed();
  }
  iconChoices.set(id, { icon: chosen, ...(choice.upload && choice.upload.id !== upload ? { upload: choice.upload } : {}) });
  renderIconPicker(id);
  el(id).querySelector<HTMLElement>('[aria-checked="true"]')?.focus();
}
/** Saves the icon a picker chose for a folder; resolves whether anything changed. */
async function saveFolderIcon(folder: string, id: string): Promise<boolean> {
  const choice = iconChoices.get(id) ?? { icon: null };
  const upload = choice.upload && choice.icon === customIconRef(choice.upload.id) ? choice.upload : undefined;
  if ((settings().icons[folder] ?? null) === choice.icon && !upload) return false;
  await store.database.setFolderIcon(folder, choice.icon, upload);
  await store.changed();
  return true;
}
/** Opens Edit folder on a folder: the one given, else the Library tree's, else the open note's. */
function openFolderEdit(folder?: string) {
  const list = folders();
  if (!list.length) {
    toast("There are no folders yet. Create one with File → New folder…");
    return;
  }
  const selected = libraryTreeRows.find((row) => row.path === libraryTreeSelection);
  const preferred =
    folder ?? (libraryTreeVisible && selected?.folder ? selected.path : current().path ? folderOf(current().path!) : "");
  const picker = el<HTMLSelectElement>("folder-edit-source");
  picker.replaceChildren(
    ...list.map((path) => {
      const option = document.createElement("option");
      option.value = option.textContent = path;
      return option;
    }),
  );
  showFolderEdit(list.includes(preferred) ? preferred : list[0]!);
  openModal("folder-edit-modal");
  el<HTMLInputElement>("folder-edit-path").select();
}
function showFolderEdit(folder: string) {
  editingFolder = folder;
  el<HTMLSelectElement>("folder-edit-source").value = folder;
  el<HTMLInputElement>("folder-edit-path").value = folder;
  el("folder-edit-error").textContent = "";
  iconChoices.set("folder-edit-icon-picker", { icon: settings().icons[folder] ?? null });
  renderIconPicker("folder-edit-icon-picker");
}
async function submitFolderEdit() {
  const source = editingFolder;
  const destination = relativePath(el<HTMLInputElement>("folder-edit-path").value.trim().replace(/\/+$/, ""));
  const renamed = destination !== source;
  if (renamed) await renameFolder(source, destination);
  const iconChanged = await saveFolderIcon(destination, "folder-edit-icon-picker");
  closeModal("folder-edit-modal");
  if (renamed) toast(`Renamed “${source}” to “${destination}”.`);
  else if (iconChanged) toast(`Changed the icon of “${source}”.`);
}
/**
 * Moves a folder and everything in it, then follows it: panes, the open folders and
 * the selection in the Library tree, its folder filter, and unlocked keys.
 */
async function renameFolder(source: string, destination: string) {
  const problem = folderProblem(source, destination, vaultPaths(), folders());
  if (problem) throw new Error(problem);
  const follow = (path: string) => movedInto(path, source, destination);
  // Edits still waiting to be saved move with their notes.
  for (const path of [...store.drafts.keys()]) if (under(path, source)) await store.flush(path);
  const filter = el<HTMLSelectElement>("library-folder").value;
  const blanks = new Set([...store.drafts.keys()].filter((path) => under(path, source) && isBlankNote(path)));
  await store.database.moveFolder(source, destination);
  store.keyring.move(source, destination);
  store.discardResolvedDraft([...store.drafts.keys()].filter((path) => under(path, source)));
  // Panes point at the new paths before the reload renders them, or they would empty.
  for (const pane of panes) {
    if (!pane.path || !under(pane.path, source)) continue;
    // A blank note was never stored, so it is started again in its new place.
    if (blanks.has(pane.path)) store.get(follow(pane.path));
    pane.path = follow(pane.path);
  }
  for (const folder of [...libraryOpenFolders])
    if (follow(folder) !== folder) {
      libraryOpenFolders.delete(folder);
      libraryOpenFolders.add(follow(folder));
    }
  libraryTreeSelection = follow(libraryTreeSelection);
  // Open the folders above its new place, so the tree shows where it went.
  for (const folder of foldersAbove([destination])) libraryOpenFolders.add(folder);
  await store.changed();
  render();
  if (follow(filter) !== filter) {
    el<HTMLSelectElement>("library-folder").value = follow(filter);
    renderLibrary();
  }
}
/** Asks first, then deletes a folder and everything in it; resolves whether it went. */
async function deleteFolder(folder: string): Promise<boolean> {
  const problem = folderProblem(folder, null, vaultPaths(), folders());
  if (problem) throw new Error(problem);
  const inside = vaultPaths().filter((path) => under(path, folder));
  if (!inside.length)
    throw new Error(`“${folder}” holds nothing TurboNotes keeps, so delete it on GitHub instead.`);
  const count = (n: number, noun: string) => `${n} ${noun}${n === 1 ? "" : "s"}`;
  const notes = inside.filter(isNote).length,
    images = inside.filter((path) => isAttachment(path) || isEncryptedAttachment(path)).length;
  const contents = [notes ? count(notes, "note") : "", images ? count(images, "image") : ""].filter(Boolean);
  if (
    !ask(
      `${contents.length ? `Delete “${folder}” and the ${contents.join(" and ")} in it?` : `Delete the empty folder “${folder}”?`}` +
        `${inside.some(isFolderKey) ? " Its encryption key goes too." : ""}` +
        " Local recovery keeps a copy of each note; the next checkpoint deletes them on GitHub.",
    )
  )
    return false;
  for (const path of [...store.drafts.keys()]) if (under(path, folder)) await store.flush(path);
  await store.database.deleteFolder(folder);
  store.discardResolvedDraft([...store.drafts.keys()].filter((path) => under(path, folder)));
  for (const key of store.keyring.folders) if (key === folder || under(key, folder)) store.keyring.lock(key);
  for (const open of [...libraryOpenFolders]) if (under(open, folder) || open === folder) libraryOpenFolders.delete(open);
  await store.changed();
  render();
  toast(`Deleted “${folder}”.`);
  return true;
}
function openFileTree() {
  treePane = activePane;
  treeSelection = current().path ?? "";
  collapsedFolders.clear();
  el("file-tree").removeAttribute("data-tree");
  openModal("open-modal");
  renderFileTree();
  el("file-tree")
    .querySelector('[aria-selected="true"]')
    ?.scrollIntoView({ block: "nearest" });
}
function renderFileTree() {
  const tree = el("file-tree");
  const notes = paths(),
    directories = folders();
  const signature = JSON.stringify([
    notes,
    directories,
    [...collapsedFolders],
    store.keyring.folders,
    settings().icons,
    Object.keys(settings().custom_icons),
  ]);
  if (tree.dataset.tree !== signature) {
    treeRows = fileTreeRows(notes, directories, collapsedFolders);
    while (!treeRows.some((row) => row.path === treeSelection))
      treeSelection = folderOf(treeSelection);
    tree.replaceChildren(
      ...treeRows.map((row, index) => {
        const button = document.createElement("button");
        button.type = "button";
        button.className = "file-tree-row";
        button.id = `file-tree-row-${index}`;
        button.tabIndex = -1;
        button.setAttribute("role", "treeitem");
        button.setAttribute("aria-label", treeLabel(row));
        button.setAttribute("aria-level", String(row.depth));
        button.setAttribute("aria-posinset", String(row.position));
        button.setAttribute("aria-setsize", String(row.siblings));
        if (row.folder)
          button.setAttribute("aria-expanded", String(row.expanded));
        const branch = document.createElement("span");
        branch.className = "tree-branch";
        branch.setAttribute("aria-hidden", "true");
        branch.textContent = `${row.prefix}${row.folder ? (row.expanded ? "[-] " : "[+] ") : ""}`;
        const name = document.createElement("span");
        name.className = row.folder ? "tree-folder" : "tree-note";
        name.textContent = row.name;
        const icon = row.folder ? folderIcon(row.path) : null;
        button.append(branch, ...lockIcons(row.path, row.folder), ...[icon].filter((item) => item !== null), name);
        button.onclick = () => {
          treeSelection = row.path;
          if (row.folder) toggleTreeFolder(row.path);
          else renderFileTree();
          tree.focus();
        };
        button.ondblclick = () => {
          if (!row.folder) openSelectedNote();
        };
        return button;
      }),
    );
    tree.dataset.tree = signature;
  }
  const selectedIndex = treeRows.findIndex((row) => row.path === treeSelection);
  [...tree.children].forEach((button, index) =>
    button.setAttribute("aria-selected", String(index === selectedIndex)),
  );
  tree.setAttribute("aria-activedescendant", `file-tree-row-${selectedIndex}`);
  const selected = treeRows[selectedIndex];
  el<HTMLInputElement>("open-note-path").value = selected?.path || "/";
  el<HTMLButtonElement>("open-selected-note").disabled =
    !selected || selected.folder;
  el("file-tree-count").textContent =
    `${notes.length} ${notes.length === 1 ? "note" : "notes"}`;
}
function toggleTreeFolder(path: string) {
  if (collapsedFolders.has(path)) collapsedFolders.delete(path);
  else collapsedFolders.add(path);
  renderFileTree();
}
function openSelectedNote() {
  const selected = treeRows.find((row) => row.path === treeSelection);
  if (!selected || selected.folder) return;
  closeModal("open-modal");
  openNote(selected.path, treePane);
}
function treeKeydown(event: KeyboardEvent) {
  let index = treeRows.findIndex((row) => row.path === treeSelection);
  const row = treeRows[index];
  if (!row) return;
  switch (event.key) {
    case "ArrowDown":
      index = Math.min(treeRows.length - 1, index + 1);
      break;
    case "ArrowUp":
      index = Math.max(0, index - 1);
      break;
    case "Home":
      index = 0;
      break;
    case "End":
      index = treeRows.length - 1;
      break;
    case "ArrowRight":
      if (row.folder && !row.expanded) toggleTreeFolder(row.path);
      else if (row.folder && treeRows[index + 1]?.parent === row.path) index++;
      break;
    case "ArrowLeft":
      if (row.folder && row.expanded) toggleTreeFolder(row.path);
      else if (row.parent !== null)
        index = treeRows.findIndex((item) => item.path === row.parent);
      break;
    case "Enter":
    case " ":
      if (row.folder) toggleTreeFolder(row.path);
      else openSelectedNote();
      break;
    default:
      return;
  }
  event.preventDefault();
  treeSelection = treeRows[index]?.path ?? "";
  renderFileTree();
  el("file-tree")
    .querySelector('[aria-selected="true"]')
    ?.scrollIntoView({ block: "nearest" });
}
function openAutoCheckpointSettings() {
  el<HTMLInputElement>("auto-checkpoint-minutes").value = String(
    settings().checkpoint.auto_minutes,
  );
  el("auto-checkpoint-error").textContent = "";
  openModal("auto-checkpoint-modal");
}
function setLibraryFind(visible: boolean) {
  el("library-search-controls").hidden = !visible;
  el("library-find-toggle").setAttribute("aria-pressed", String(visible));
  write("library-find", String(visible));
}
function openPalette(pane = activePane, restricted: string[] | null = null) {
  modalPane = pane;
  selection = 0;
  palettePaths = restricted;
  el<HTMLSelectElement>("palette-folder").value = "";
  el("palette-folder-controls").hidden = !!restricted;
  el<HTMLInputElement>("palette-search").value = "";
  openModal("palette-modal");
  renderPalette();
  el("palette-search").focus();
}
function renderPalette() {
  const list = el("palette-results");
  const query = el<HTMLInputElement>("palette-search").value;
  const found = store.sortByRecentEdit(
    palettePaths
      ? palettePaths.filter((p) => p.toLowerCase().includes(query.toLowerCase()))
      : sortedPaths(query).filter((path) =>
          inFolder(path, el<HTMLSelectElement>("palette-folder").value),
        ),
  );
  selection = Math.max(0, Math.min(selection, found.length - 1));
  searchOpenFolders.palette = query.trim() ? foldersAbove(found) : new Set();
  if (libraryTreeVisible) renderLibraryTree();
  list.replaceChildren(
    ...found.map((p, i) => {
      const button = row(p, () => {
        closeModal("palette-modal");
        openNote(p, modalPane);
      }, true);
      button.classList.remove("active");
      button.classList.toggle("selected", i === selection);
      button.id = `palette-option-${i}`;
      button.setAttribute("aria-selected", String(i === selection));
      button.dataset.path = p;
      return button;
    }),
  );
  updatePaletteSelection(
    "palette-search",
    "palette-results",
    "No notes match your search.",
  );
}
async function createPaletteNote() {
  if (palettePaths) return;
  const title = el<HTMLInputElement>("palette-search").value.trim();
  if (!title) {
    closeModal("palette-modal");
    openForm();
    return;
  }
  const selectedFolder = el<HTMLSelectElement>("palette-folder").value;
  const folder = selectedFolder === "." ? "" : selectedFolder;
  const name = title.replace(/\.md$/, "");
  const stem = title.includes("/") ? name : `${folder ? `${folder}/` : ""}${name}`;
  const path = notePath(`${stem}${noteExtension(folderOf(stem), keys())}`);
  if (titleOf(path).length > 80) throw new Error("Use a note title of 80 characters or fewer.");
  assertDestination(path, paths());
  if (isEncryptedFile(path) && !(await unlockedFor(path))) return;
  store.edit(path, "");
  closeModal("palette-modal");
  openNote(path, modalPane);
  await store.flush(path);
}
function openCommands() {
  modalPane = activePane;
  selection = 0;
  el<HTMLInputElement>("command-palette-search").value = "";
  openModal("command-palette-modal");
  renderCommands();
  el("command-palette-search").focus();
}
function renderCommands() {
  const commands = filterAppCommands(
    el<HTMLInputElement>("command-palette-search").value,
  );
  selection = Math.max(0, Math.min(selection, commands.length - 1));
  el("command-palette-results").replaceChildren(
    ...commands.map((c, i) => {
      const b = document.createElement("button");
      b.type = "button";
      b.className =
        "note-row command-row" + (i === selection ? " selected" : "");
      b.textContent = `${c.label}  ${c.shortcut}`;
      b.id = `command-option-${i}`;
      b.setAttribute("role", "option");
      b.setAttribute("aria-selected", String(i === selection));
      b.onclick = () => runCommand(c.id);
      return b;
    }),
  );
  updatePaletteSelection(
    "command-palette-search",
    "command-palette-results",
    "No commands match your search.",
  );
}
function updatePaletteSelection(
  input: string,
  results: string,
  emptyMessage: string,
) {
  const list = el(results);
  const selected = list.querySelector<HTMLElement>('[aria-selected="true"]');
  if (selected) {
    el(input).setAttribute("aria-activedescendant", selected.id);
  } else {
    el(input).removeAttribute("aria-activedescendant");
    const empty = document.createElement("p");
    empty.className = "panel-empty";
    empty.setAttribute("role", "status");
    empty.textContent = emptyMessage;
    list.append(empty);
  }
}
function runCommand(id: AppCommandId) {
  closeModal("command-palette-modal");
  activate(modalPane);
  if (guideIsOpen() && (id.startsWith("format-") || id.startsWith("insert-")
    || ["save-note", "edit-note", "move-note", "export-note", "copy-markdown", "delete-note", "ask-ai"].includes(id))) {
    toast("Guide.md is read-only. Open a note to use this command.");
    return;
  }
  // Preview mode keeps the text as it is; the note can still be renamed, moved, or saved.
  if (current().preview && (id.startsWith("format-") || id.startsWith("insert-") || id === "ask-ai")) {
    toast("This pane is in Preview mode, which is read-only. Press F3 to edit.");
    return;
  }
  if (id === "format-document") {
    formatDocument();
    return;
  }
  if (id === "format-table-selection" || id === "format-table-document") {
    current().editor.formatTables(id === "format-table-selection" ? "selection" : "document");
    return;
  }
  if (id.startsWith("format-")) {
    current().editor.formatSelection(
      (id === "format-code" ? "inline-code" : id.slice(7)) as MarkdownFormat,
    );
    return;
  }
  switch (id) {
    case "insert-table":
      current().editor.insertTable();
      break;
    case "insert-image":
      if (current().path) insertImage(current().editor);
      break;
    case "insert-figlet":
      if (current().path) openFiglet(current().editor);
      break;
    case "new-note":
      openForm();
      break;
    case "daily-note":
      run(openDailyNote);
      break;
    case "scratch-note":
      run(openScratchNote);
      break;
    case "journal-note":
      run(openJournalNote);
      break;
    case "clear-mentions":
      clearMentions();
      break;
    case "edit-folder":
      openFolderEdit();
      break;
    case "new-folder":
      openFolderForm();
      break;
    case "new-encrypted-folder":
      openFolderForm(true);
      break;
    case "lock-folders":
      if (store.keyring.folders.length) run(() => lockFolders());
      else toast("No encrypted folders are unlocked.");
      break;
    case "open-note":
      openFileTree();
      break;
    case "open-guide":
      openGuide();
      break;
    case "save-note":
      run(() => saveNotes());
      break;
    case "save-all":
      run(() => saveNotes(true));
      break;
    case "find-note":
      openPalette();
      break;
    case "edit-note":
      openForm(true);
      break;
    case "move-note":
      if (current().path) openMove(current().path!, false);
      break;
    case "focus-library":
      focusLibrary();
      break;
    case "checkpoint":
      openCheckpoint();
      break;
    case "export-note":
      exportDraft();
      break;
    case "copy-markdown":
      run(copyDraft);
      break;
    case "copy-table":
      run(copyTable);
      break;
    case "delete-note":
      run(() => deleteNote());
      break;
    case "toggle-preview":
      toggleMode();
      break;
    case "toggle-preview-mode":
      togglePreviewMode();
      break;
    case "toggle-split":
      toggleSplit();
      break;
    case "split-right":
      splitPane("row");
      break;
    case "split-down":
      splitPane("column");
      break;
    case "toggle-line-numbers":
      toggleLines();
      break;
    case "toggle-word-wrap":
      toggleWordWrap();
      break;
    case "toggle-spell-check":
      toggleSpellCheck();
      break;
    case "toggle-sidebar":
      toggleSidebar();
      break;
    case "toggle-outline":
      toggleWidget("outline");
      break;
    case "toggle-calendar":
      toggleWidget("calendar");
      break;
    case "toggle-timer":
      toggleWidget("timer");
      break;
    case "toggle-calculator":
      toggleWidget("calculator");
      break;
    case "toggle-revisions":
      toggleHistory();
      break;
    case "typography":
      openModal("typography-modal");
      break;
    case "ask-ai":
      openAi();
      break;
    case "ai-settings":
      openAiSettings();
      break;
    case "markdown-help":
      openModal("md-modal");
      break;
    case "keyboard-shortcuts":
      openModal("shortcuts-modal");
      break;
  }
}
function toggleMode() {
  if (guideIsOpen()) {
    toggleGuideMode();
    return;
  }
  mode = mode === "source" ? "live" : "source";
  write("editor-mode", mode);
  panes.forEach(applyPaneMode);
  renderPreviewToggle();
}
/** Puts a pane's editor in its mode: in Preview mode, Live Preview and read-only. */
function applyPaneMode(pane: Pane) {
  pane.editor.setReadOnly(pane.preview);
  pane.editor.setMode(pane.preview ? "live" : mode);
}
/**
 * F3: Preview mode shows the active pane's note rendered and read-only, and an eye on its
 * title, until F3 again. The other panes, and F4's choice for them, stay as they are.
 */
function togglePreviewMode() {
  if (guideIsOpen()) {
    toast("Guide.md is always read-only.");
    return;
  }
  const pane = current();
  const focused = pane.editor.hasFocus();
  pane.preview = !pane.preview;
  // An answer on its way into the note has nowhere to go once it can't be edited.
  if (pane.preview && aiJob?.pane === pane) cancelAi();
  applyPaneMode(pane);
  setPaneTitle(pane, pane.path);
  renderPreviewToggle();
  if (focused) pane.editor.focus();
}
function toggleLines() {
  lineNumbers = !lineNumbers;
  write("line-numbers", String(lineNumbers));
  panes.forEach((p) => p.editor.setLineNumbers(lineNumbers));
  document.body.classList.toggle("linenums-active", lineNumbers);
}
// The spell checker and its dictionary load once the page is idle, or when it is turned on.
let spellChecker: SpellChecker | null = null;
let spellLoading: Promise<void> | null = null;
/** Words added to the dictionary stay on this device, so none from an encrypted note leaves it. */
const ADDED_WORDS_KEY = "turbonotes2-spelling-words";
function addedWords(): string[] {
  try {
    const words: unknown = JSON.parse(pref.getItem(ADDED_WORDS_KEY) ?? "[]");
    return Array.isArray(words) ? words.filter((word): word is string => typeof word === "string") : [];
  } catch {
    return [];
  }
}
function loadSpelling(): Promise<void> {
  spellLoading ??= import("./spelling")
    .then(({ createSpellChecker }) => {
      spellChecker = createSpellChecker(addedWords());
      setSpellChecker(spellChecker);
    })
    .catch(() => {
      spellLoading = null;
    });
  return spellLoading;
}
function changeDictionary(word: string, add: boolean) {
  const words = addedWords().filter((added) => added !== word);
  if (add) words.push(word);
  pref.setItem(ADDED_WORDS_KEY, JSON.stringify(words));
  if (add) spellChecker?.add(word);
  else spellChecker?.remove(word);
  setSpellChecker(spellChecker);
  toast(add ? `Added “${word}” to the dictionary.` : `Removed “${word}” from the dictionary.`);
}
function toggleSpellCheck() {
  spellCheck = !spellCheck;
  write("spell-check", String(spellCheck));
  panes.forEach((p) => p.editor.setSpellCheck(spellCheck));
  document.body.classList.toggle("spellcheck-active", spellCheck);
  if (spellCheck) void loadSpelling();
  toast(spellCheck ? "Spell check on." : "Spell check off: the browser checks spelling instead.");
}
/**
 * The spelling commands that open a note's menu, for the word right-clicked: what it may
 * have been meant to be, and adding it to the dictionary, or taking out one added before.
 */
function spellingMenu(editor: MarkdownEditor, spot: (SpellingWord & { misspelled: boolean }) | null): MenuEntry[] {
  if (!spot || !spellChecker || editor.isReadOnly()) return [];
  const { from, to, word } = spot;
  if (!spot.misspelled)
    return spellChecker.added(word) ? [{ label: `Remove “${word}” from dictionary`, run: () => changeDictionary(word, false) }, "-"] : [];
  const suggestions = spellChecker.suggest(word);
  // The note may change while the menu is open; a suggestion replaces the word only if it is still there.
  const replace = (suggestion: string) => () => {
    if (editor.getValue().slice(from, to) === word) editor.change([{ from, to, insert: suggestion }]);
  };
  return [
    ...(suggestions.length
      ? suggestions.map((suggestion): MenuEntry => ({ label: suggestion, strong: true, run: replace(suggestion) }))
      : [{ label: "No suggestions", disabled: true }]),
    { label: `Add “${word}” to dictionary`, run: () => changeDictionary(word, true) },
    "-",
  ];
}
function toggleWordWrap() {
  wordWrap = !wordWrap;
  write("word-wrap", String(wordWrap));
  panes.forEach((p) => p.editor.setLineWrapping(wordWrap));
  toast(wordWrap ? "Word wrap on." : "Word wrap off: long lines scroll sideways.");
}
function typography() {
  document.documentElement.style.setProperty("--md-body-size", `${bodySize}px`);
  el("markdown-body-size").textContent = `${bodySize}px`;
  for (const level of [1, 2, 3, 4, 5, 6] as const) {
    document.documentElement.style.setProperty(
      `--md-heading-${level}-size`,
      `${headingSizes[level] / 100}em`,
    );
    el(`markdown-heading-size-${level}`).textContent =
      `${headingSizes[level]}%`;
  }
  pref.setItem(MARKDOWN_BODY_SIZE_STORAGE_KEY, String(bodySize));
  pref.setItem(MARKDOWN_HEADING_SIZE_STORAGE_KEY, JSON.stringify(headingSizes));
}
function renderDateTimeSettings() {
  const dateFormat = read("slash-date-format");
  const timeFormat = read("slash-time-format");
  el<HTMLSelectElement>("slash-date-format").value = isDateFormat(dateFormat)
    ? dateFormat
    : "mdy";
  el<HTMLSelectElement>("slash-time-format").value = isTimeFormat(timeFormat)
    ? timeFormat
    : "none";
  el("slash-date-preview").textContent = slashTodayText();
}
function renderTableSettings() {
  document.documentElement.dataset.tablePadding = tableSettings.padding;
  document.documentElement.dataset.tablePosition = tableSettings.position;
  document.documentElement.dataset.tableWidth = tableSettings.width;
  document.documentElement.style.setProperty("--md-table-align", tableSettings.align);
  el<HTMLSelectElement>("table-padding").value = tableSettings.padding;
  el<HTMLSelectElement>("table-position").value = tableSettings.position;
  el<HTMLSelectElement>("table-align").value = tableSettings.align;
  el<HTMLSelectElement>("table-width").value = tableSettings.width;
}
function renderCodeBlockSettings() {
  document.documentElement.dataset.codeWordWrap = String(codeBlockSettings.wordWrap);
  el<HTMLSelectElement>("code-word-wrap").value = String(codeBlockSettings.wordWrap);
  panes.forEach((pane) => {
    pane.editor.refreshPreview();
    pane.editor.requestMeasure();
  });
  guideEditor?.refreshPreview();
  guideEditor?.requestMeasure();
}
function renderImageSettings() {
  document.documentElement.dataset.imageAlign = imageSettings.align;
  document.documentElement.dataset.imageBorder = imageSettings.border;
  el<HTMLSelectElement>("image-align").value = imageSettings.align;
  el<HTMLSelectElement>("image-border").value = imageSettings.border;
  const preview = el("image-settings-preview-image");
  if (imageSettings.border === "double") preview.setAttribute("box-", "double");
  else preview.removeAttribute("box-");
  panes.forEach((pane) => pane.editor.refreshPreview());
}
function renderMermaidSettings(refreshDiagram = true) {
  el<HTMLSelectElement>("mermaid-theme").value = mermaidSettings.theme;
  el<HTMLSelectElement>("mermaid-border").value = mermaidSettings.border;
  el<HTMLSelectElement>("mermaid-align").value = mermaidSettings.align;
  el<HTMLSelectElement>("mermaid-width").value = mermaidSettings.width;
  const diagram = el("mermaid-settings-preview-diagram");
  diagram.dataset.mermaidTheme = mermaidSettings.theme;
  diagram.dataset.mermaidBorder = mermaidSettings.border;
  diagram.dataset.mermaidAlign = mermaidSettings.align;
  diagram.dataset.mermaidWidth = mermaidSettings.width;
  if (mermaidSettings.border === "double") diagram.setAttribute("box-", "double");
  else diagram.removeAttribute("box-");
  if (refreshDiagram && el("mermaid-settings-modal").classList.contains("active"))
    void renderMermaidSettingsPreview();
  panes.forEach((pane) => pane.editor.refreshPreview());
}
async function renderMermaidSettingsPreview() {
  const version = ++mermaidPreviewVersion;
  const canvas = el("mermaid-settings-preview-canvas");
  canvas.classList.remove("md-live-mermaid-error");
  canvas.textContent = "Rendering diagram…";
  try {
    const svg = await renderMermaidSvg(
      "flowchart LR\n  Write[Write Markdown] --> Preview[Live Preview]",
      mermaidSettings,
      readEditorDiagramPalette(),
    );
    if (version === mermaidPreviewVersion && el("mermaid-settings-modal").classList.contains("active"))
      setMermaidSvg(el("mermaid-settings-preview-diagram"), canvas, svg);
  } catch (error) {
    if (version === mermaidPreviewVersion) canvas.textContent = error instanceof Error
      ? `Diagram error: ${error.message}` : "Could not render preview.";
  }
}
function buildThemePicker() {
  const host = el("theme-options");
  for (const [index, group] of themePickerGroups.entries()) {
    const section = document.createElement("section");
    section.className = "theme-group";
    const heading = document.createElement("h3");
    heading.className = "theme-group-heading";
    heading.id = `theme-group-${index}`;
    heading.textContent = group.label;
    section.setAttribute("aria-labelledby", heading.id);
    const grid = document.createElement("div");
    grid.className = "theme-grid";
    for (const [id, name] of group.themes) {
      const button = document.createElement("button");
      button.type = "button";
      button.className = "theme-choice";
      button.dataset.setTheme = id;
      button.setAttribute("aria-pressed", "false");
      const preview = document.createElement("span");
      preview.className = "theme-choice-preview";
      preview.dataset.colorTheme = id;
      preview.setAttribute("aria-hidden", "true");
      preview.innerHTML = `
        <span class="theme-preview-bar"><span>TurboNotes</span><span>File&nbsp; Settings&nbsp; View</span></span>
        <span class="theme-preview-workspace">
          <span class="theme-preview-library"><span>LIBRARY</span><span class="theme-preview-file">Guide.md</span><span>Notes</span></span>
          <span class="theme-preview-editor"><span class="theme-preview-heading">Guide.md</span><span>Write in Markdown</span><span><b>Bold</b> · <i>Italic</i></span><span class="theme-preview-link">[Open note]</span></span>
        </span>`;
      const caption = document.createElement("span");
      caption.className = "theme-choice-caption";
      const label = document.createElement("span");
      label.textContent = name;
      const state = document.createElement("span");
      state.className = "theme-choice-state";
      state.textContent = "SELECTED";
      state.setAttribute("aria-hidden", "true");
      caption.append(label, state);
      button.append(preview, caption);
      grid.append(button);
    }
    section.append(heading, grid);
    host.append(section);
  }
}
function appearance() {
  const a = loadAppearance(pref);
  document.documentElement.dataset.colorTheme = a.theme;
  document.documentElement.dataset.editorFont = a.font;
  document
    .querySelector('meta[name="theme-color"]')
    ?.setAttribute("content", THEME_COLORS[a.theme]);
  el<HTMLSelectElement>("typography-font").value = a.font;
  document
    .querySelectorAll<HTMLElement>("[data-set-theme]")
    .forEach((b) => {
      const selected = b.dataset.setTheme === a.theme;
      b.classList.toggle("selected", selected);
      b.setAttribute("aria-pressed", String(selected));
      if (selected) b.dataset.initialFocus = "";
      else delete b.dataset.initialFocus;
    });
  panes.forEach((pane) => pane.editor.refreshPreview());
  if (el("mermaid-settings-modal").classList.contains("active"))
    void renderMermaidSettingsPreview();
}
async function chooseRepository() {
  if (!api.session.user) {
    location.href = "/api/auth/github/start";
    return;
  }
  await store.flush();
  if (store.state?.attempts.some((a) => a.state === "submitted"))
    throw new Error(
      "Check the pending checkpoint before changing repositories.",
    );
  repositories = await api.request<RepositoryChoice[]>("/repositories");
  const select = el<HTMLSelectElement>("repository-choice");
  select.replaceChildren(
    ...repositories.map((r, i) => {
      const o = document.createElement("option");
      o.value = String(i);
      o.textContent = `${r.owner}/${r.repository}`;
      return o;
    }),
  );
  el<HTMLInputElement>("repository-branch").value =
    repositories[0]?.defaultBranch ?? "";
  el("repository-error").textContent = repositories.length
    ? ""
    : "Choose repositories through Manage GitHub access, then return here.";
  openModal("repository-modal");
}
async function connectRepository() {
  await store.flush();
  const repo =
    repositories[Number(el<HTMLSelectElement>("repository-choice").value)];
  if (!repo) throw new Error("Select a repository first.");
  await accountData?.flush();
  const connection = await api.request<Connection>("/connections", "POST", {
    repositoryId: repo.repositoryId,
    installationId: repo.installationId,
    branch: el<HTMLInputElement>("repository-branch").value,
  });
  await api.loadSession();
  await store.database.connect(connection, api.session.user?.id);
  await store.changed();
  const choice = el<HTMLSelectElement>("repository-import").value as
    | "merge"
    | "local"
    | "remote";
  await sync.refresh(choice);
  await store.database.mutate((s) => {
    s.vault.userId = api.session.user?.id;
  });
  await accountData?.load();
  closeModal("repository-modal");
  await store.changed();
  render();
  if (store.state?.recovery.some((r) => !r.resolved)) openConflict();
}
async function loadHistory() {
  const pane = activePane,
    path = current().path;
  const list = el("revision-list");
  list.replaceChildren();
  if (!path || !store.state?.vault.connection) {
    list.textContent =
      "Connect a repository to browse Git history. Local recovery is available from File.";
    return;
  }
  const entries = await api.request<HistoryEntry[]>(
    `/history?path=${encodeURIComponent(path)}`,
  );
  if (current().path !== path || activePane !== pane) return;
  for (const entry of entries) {
    const button = document.createElement("button");
    button.className = "note-row";
    button.style.display = "block";
    button.textContent = `${entry.message}\n${entry.author} · ${new Date(entry.date).toLocaleString()}`;
    button.onclick = () =>
      run(async () => {
        const file = await api.request<FileState>(
          `/history/${entry.sha}?path=${encodeURIComponent(path)}`,
        );
        let text = file.exists ? file.text : "",
          notice = file.exists ? null : "[File absent at this commit]";
        // An encrypted version is read with the folder's key; restoring keeps it encrypted.
        if (file.exists && isEncryptedFile(path)) {
          text = "";
          if (store.isLocked(path))
            notice = "[Encrypted. Unlock the folder to read this version.]";
          else {
            const readable = await store.readable(path, file).catch(() => absent);
            if (readable.exists) text = readable.text;
            else notice = "[Encrypted with a different key]";
          }
        }
        historical = { path, file, pane };
        el("revision-preview-title").textContent = entry.message;
        el("revision-preview-meta").textContent =
          `${entry.author} · ${entry.date} · ${text.trim() ? text.trim().split(/\s+/).length : 0} words`;
        el("revision-preview").textContent = notice ?? text;
        openModal("revision-modal");
      });
    list.append(button);
  }
}
function openConflict() {
  run(showConflict);
}
async function showConflict() {
  conflictGuard = store.state ? guardOf(store.state) : undefined;
  conflict = store.state?.recovery.find((r) => !r.resolved)?.conflict ?? null;
  if (!conflict) {
    toast("No unresolved conflicts.");
    return;
  }
  // Encrypted versions are shown and edited decrypted, so their folder must be open.
  const shown = structuredClone(conflict);
  for (const path of shown.paths) {
    if (!isEncryptedFile(path)) continue;
    const folder = store.encryptedFolder(path);
    if (folder === null || !(await requestUnlock(folder))) return;
    for (const side of ["base", "local", "remote"] as const)
      shown[side][path] = await store.readable(path, shown[side][path] ?? absent);
  }
  el("conflict-error").textContent = "";
  el("conflict-reason").textContent = conflict.reason;
  const host = el("conflict-versions");
  host.replaceChildren();
  for (const path of conflict.paths) {
    const section = document.createElement("section");
    section.className = "conflict-path";
    const heading = document.createElement("h3");
    heading.textContent = path;
    const columns = document.createElement("div");
    columns.className = "conflict-columns";
    for (const side of ["base", "local", "remote"] as const) {
      const div = document.createElement("div");
      const label = document.createElement("strong");
      label.textContent = side.toUpperCase();
      const pre = document.createElement("pre");
      const f = shown[side][path] ?? absent;
      pre.textContent = f.exists ? f.text || "[Empty file]" : "[Absent file]";
      div.append(label, pre);
      columns.append(div);
    }
    const input = document.createElement("textarea");
    input.className = "conflict-editor";
    input.dataset.path = path;
    const local = shown.local[path];
    input.value = local?.exists ? local.text : "";
    input.setAttribute("aria-label", `Merged content for ${path}`);
    const exists = document.createElement("input");
    exists.type = "checkbox";
    exists.checked = !!local?.exists;
    exists.dataset.exists = path;
    const label = document.createElement("label");
    label.append(exists, " Keep file (uncheck to keep deleted)");
    const destination = document.createElement("input");
    destination.className = "terminal-input";
    destination.value = path;
    destination.dataset.destination = path;
    destination.setAttribute("aria-label", `Save ${path} at another path`);
    section.append(heading, columns, label, input, destination);
    host.append(section);
  }
  openModal("conflict-modal");
}
async function resolveConflict(side: "local" | "remote" | "merged") {
  if (!conflict) return;
  const chosen: Record<string, FileState> =
    side === "merged" ? {} : structuredClone(conflict[side]);
  if (side === "merged")
    for (const path of conflict.paths) {
      const input = [
        ...el("conflict-versions").querySelectorAll<HTMLTextAreaElement>(
          "textarea",
        ),
      ].find((e) => e.dataset.path === path)!;
      const exists = [
        ...el("conflict-versions").querySelectorAll<HTMLInputElement>(
          "[data-exists]",
        ),
      ].find((e) => e.dataset.exists === path)!;
      const dest = [
        ...el("conflict-versions").querySelectorAll<HTMLInputElement>(
          "[data-destination]",
        ),
      ].find((e) => e.dataset.destination === path)!.value;
      chosen[path] =
        dest === path && exists.checked ? await store.sealed(path, present(input.value)) : absent;
      if (dest !== path && exists.checked) {
        notePath(dest);
        assertDestination(dest, paths());
        if (isEncryptedFile(path) && store.encryptedFolder(dest) !== store.encryptedFolder(path))
          throw new Error(`Keep ${dest.split("/").at(-1)} encrypted: save it as a .md.age note in the same folder.`);
        chosen[dest] = await store.sealed(dest, present(input.value));
      }
    }
  // Include any edits made after the resolver opened in durable recovery before choosing a version.
  const draftFiles: Record<string, FileState> = {};
  for (const path of conflict.paths) {
    const draft = store.drafts.get(path);
    if (draft && draftPending(draft)) {
      // Recovery holds what the vault would: encrypted, for an encrypted note.
      draftFiles[path] = await store.sealed(path, present(draft.text));
    }
  }
  await store.database.resolve(conflict.id, chosen, conflictGuard, draftFiles);
  store.discardResolvedDraft(conflict.paths);
  await store.changed();
  closeModal("conflict-modal");
  if (store.state?.recovery.some((r) => !r.resolved)) openConflict();
}
function openRecovery() {
  const list = el("recovery-list");
  list.replaceChildren();
  for (const record of [...(store.state?.recovery ?? [])].reverse()) {
    if (!record.file.exists) continue;
    const button = document.createElement("button");
    button.className = "tui-button";
    button.textContent = `${record.path} · ${record.reason} · ${new Date(record.createdAt).toLocaleString()} — download`;
    button.onclick = () => {
      if (record.file.exists) download(record.path, record.file.text);
    };
    list.append(button);
  }
  if (!list.children.length)
    list.textContent =
      "Recovery copies appear before deletion, moves, restores, and conflict resolution.";
  openModal("recovery-modal");
}
function focusCurrentPane() {
  if (current().path) current().editor.focus();
  else el(`pane-empty-${current().id}`).querySelector("button")!.focus();
}
/** A pane as a reload finds it: its note, whether that was blank, and the place in it. */
interface SavedPane {
  pane: number;
  path: string | null;
  blank: boolean;
  place: EditorPlace | null;
  /** Whether the pane was in Preview mode; views saved before it had one leave it out. */
  preview?: boolean;
}
const VIEW_KEY = "turbonotes2-view";
/**
 * Keeps the tab's panes, notes, and places for a reload. sessionStorage belongs to the
 * tab, so a reload or a reopened tab finds them and a new window starts without them.
 */
function saveView() {
  // A reload while the last one was still starting must not forget the view it was given.
  if (!panesReady) return;
  const saved: SavedPane[] = openPanes().map((index) => {
    const { path, editor, preview } = panes[index]!;
    return { pane: index, path, blank: isBlankNote(path), place: path ? editor.getPlace() : null, preview };
  });
  try {
    sessionStorage.setItem(VIEW_KEY, JSON.stringify({
      layout: paneLayout, active: activePane, guide: guideIsOpen(), panes: saved,
    }));
  } catch {}
}
function savedView(): { layout: PaneLayout; active: number; guide: boolean; panes: SavedPane[] } | null {
  let saved: Record<string, unknown> | null = null;
  try {
    saved = JSON.parse(sessionStorage.getItem(VIEW_KEY) ?? "null");
  } catch {}
  const layout = parsePaneLayout(saved?.layout);
  if (!saved || !layout) return null;
  const open = layoutPanes(layout);
  const isPlace = (place: unknown): place is EditorPlace => typeof place === "object" && place !== null
    && ["anchor", "head", "top", "offset"].every((key) => Number.isFinite((place as Record<string, unknown>)[key]));
  const savedPanes = (Array.isArray(saved.panes) ? saved.panes : []).filter((pane): pane is SavedPane =>
    typeof pane === "object" && pane !== null && open.includes(pane.pane)
    && (pane.path === null || typeof pane.path === "string") && typeof pane.blank === "boolean"
    && (pane.place === null || isPlace(pane.place))
    && (pane.preview === undefined || typeof pane.preview === "boolean"));
  const active = typeof saved.active === "number" && open.includes(saved.active) ? saved.active : open[0]!;
  return { layout, active, guide: saved.guide === true, panes: savedPanes };
}
/**
 * A new window starts on a blank note in one pane, which stays out of the vault until it
 * has content. A reload comes back to the tab's panes, notes, and places in them, except
 * notes since deleted or in a folder the reload locked, whose panes open empty.
 */
function openStartupNote() {
  const untitled = () => store.state ? untitledNotePath(store.state) : notePath("Untitled.md");
  const saved = savedView();
  if (saved) {
    while (panes.length <= Math.max(...layoutPanes(saved.layout))) panes.push(createPane(panes.length));
    setPaneLayout(saved.layout);
    for (const { pane, path, blank, preview } of saved.panes) {
      if (preview) {
        panes[pane]!.preview = true;
        applyPaneMode(panes[pane]!);
      }
      if (!path || store.isLocked(path) || store.damaged.has(path)) continue;
      const exists = store.drafts.has(path) || !!store.state?.documents.some((d) => d.path === path);
      // A blank note whose name another window has since used makes way for a new one.
      if (blank) loadPaneNote(exists ? untitled() : path, pane);
      else if (exists) loadPaneNote(path, pane);
    }
    activate(saved.active);
  } else {
    loadPaneNote(untitled(), 0);
    activate(0);
  }
  panesReady = true;
  renderPaneLayout();
  render();
  for (const { pane, place } of saved?.panes ?? [])
    if (place && panes[pane]!.path && !isBlankNote(panes[pane]!.path)) panes[pane]!.editor.setPlace(place);
  if (saved?.guide) openGuide();
  else focusCurrentPane();
}
/** ⌘⇧Enter: splits the only pane in two side by side, or closes the active pane. */
function toggleSplit(index = activePane) {
  if (openPanes().length > 1) closePane(index);
  else splitPane("row", index);
}
/**
 * Splits a pane, putting a new one beside it ("row") or below it ("column"), and returns
 * the new pane. Null, with a message, when three across or three down is reached.
 */
function addPane(direction: SplitDirection, index = activePane): number | null {
  const free = panes.findIndex((pane) => !pane.open);
  const added = free === -1 ? panes.length : free;
  // The first split gives the first pane the size it had last time.
  const share = "pane" in paneLayout ? clampPanePercentage(Number(read("split-size") ?? 50)) : 50;
  const layout = splitLayout(paneLayout, index, added, direction, share);
  if (!layout) {
    toast(direction === "row"
      ? "That would make four panes side by side; three is the most."
      : "That would stack four panes; three is the most.");
    return null;
  }
  if (added === panes.length) panes.push(createPane(added));
  setPaneLayout(layout);
  return added;
}
/** Panel → Split pane right or down: the new pane opens Find to choose its note. */
function splitPane(direction: SplitDirection, index = activePane) {
  closeGuide(false);
  const added = addPane(direction, index);
  if (added === null) return;
  activate(added);
  openPalette(added);
}
/** Closes a pane, giving its space to the rest of its split. The last pane stays. */
function closePane(index = activePane) {
  closeGuide(false);
  const beside = paneBeside(paneLayout, index);
  const pane = panes[index];
  if (beside === null || !pane) return;
  if (aiJob?.pane === pane) cancelAi();
  const left = pane.path;
  // The pane comes back empty, and editable, if a split uses it again.
  pane.path = null;
  if (pane.preview) {
    pane.preview = false;
    applyPaneMode(pane);
  }
  if (left && !panes.some((p) => p.path === left)) store.discardNewDraft(left);
  const active = index === activePane;
  setPaneLayout(closeLayout(paneLayout, index));
  if (active) activate(beside);
  render();
  pane.editor.clearHistory();
  if (active) focusCurrentPane();
}
function setPaneLayout(layout: PaneLayout) {
  paneLayout = layout;
  const open = layoutPanes(layout);
  panes.forEach((pane, index) => (pane.open = open.includes(index)));
  renderPaneLayout();
}
/** Builds the panes and dividers of the layout into the page. */
function renderPaneLayout() {
  const container = el("editor-panes");
  const scroller = (pane: Pane) => el(`editor-${pane.id}`).querySelector<HTMLElement>(".cm-scroller");
  // Moving a pane in the page loses its scroll position and the focus in it, so both come back.
  const focused = container.contains(document.activeElement) ? (document.activeElement as HTMLElement) : null;
  const scrolled = panes.map((pane) => [scroller(pane)?.scrollTop ?? 0, scroller(pane)?.scrollLeft ?? 0] as const);
  const build = (node: PaneLayout, size: number | null): HTMLElement => {
    const part = "pane" in node ? el(`editor-pane-${panes[node.pane]!.id}`) : document.createElement("div");
    part.style.flexGrow = size === null ? "" : String(size);
    if ("pane" in node) return part;
    // Not plain "row": TuiCSS's grid gives .row a 20px bottom margin, which made the panes
    // overflow, so the editor's scrolling moved them and hid their titles.
    part.className = `pane-split pane-split-${node.split}`;
    node.children.forEach((child, index) => {
      if (index) part.append(paneSplitter(node, index - 1));
      part.append(build(child, node.sizes[index]!));
    });
    return part;
  };
  const split = openPanes().length > 1;
  container.classList.toggle("split", split);
  el("split-view-toggle").setAttribute("aria-pressed", String(split));
  for (const pane of panes) {
    el(`editor-pane-${pane.id}`).hidden = !pane.open;
    el(`pane-action-icon-${pane.id}`).textContent = split ? "×" : "↔";
    el(`pane-action-${pane.id}`).setAttribute(
      "aria-label",
      split ? "Close pane" : "Open split pane",
    );
    el(`pane-action-${pane.id}`).title = `${split ? "Close pane" : "Open split pane"} (⌘⇧Enter / Ctrl+Shift+Enter)`;
  }
  container.replaceChildren(
    build(paneLayout, null),
    ...panes.filter((pane) => !pane.open).map((pane) => el(`editor-pane-${pane.id}`)),
  );
  panes.forEach((pane, index) => {
    const scroll = scroller(pane);
    if (scroll) [scroll.scrollTop, scroll.scrollLeft] = scrolled[index]!;
    if (pane.open) pane.editor.requestMeasure();
  });
  // A pane that just closed keeps no focus; whoever closed it says where the cursor goes.
  if (focused && !focused.closest(".editor-pane[hidden]")) focused.focus({ preventScroll: true });
}
const narrowScreen = () => matchMedia("(max-width:760px)").matches;
/** Panes side by side stack on a phone, so their divider lies across too. */
function orientSplitter(splitter: HTMLElement) {
  splitter.setAttribute(
    "aria-orientation",
    splitter.dataset.split === "row" && !narrowScreen() ? "vertical" : "horizontal",
  );
}
/** The divider after part `index` of a split, which drags, or moves with the arrow keys, to share out their space. */
/** How close, in pixels, a dragged divider comes to a third or the half before it snaps there. */
const SNAP_REACH = 12;
function paneSplitter(node: PaneSplit, index: number): HTMLElement {
  const splitter = document.createElement("div");
  splitter.className = "editor-splitter";
  splitter.dataset.split = node.split;
  splitter.setAttribute("role", "separator");
  splitter.setAttribute("aria-label", "Resize editor panes");
  splitter.title = "Drag to resize: snaps to thirds and the half, or hold ⌥ to place it freely. Double-click to even out.";
  splitter.tabIndex = 0;
  const grip = document.createElement("span");
  grip.setAttribute("aria-hidden", "true");
  grip.textContent = node.split === "row" ? "⋮" : "⋯";
  splitter.append(grip);
  orientSplitter(splitter);
  const describe = () => {
    const start = node.sizes.slice(0, index).reduce((sum, size) => sum + size, 0);
    const pair = node.sizes[index]! + node.sizes[index + 1]!;
    const least = Math.min(minimumPaneShare(node.sizes.length), pair / 2);
    splitter.setAttribute("aria-valuemin", String(Math.round(start + least)));
    splitter.setAttribute("aria-valuemax", String(Math.round(start + pair - least)));
    splitter.setAttribute("aria-valuenow", String(Math.round(start + node.sizes[index]!)));
  };
  describe();
  const resize = (share: number) => {
    node.sizes = resizeSplit(node.sizes, index, share);
    (splitter.previousElementSibling as HTMLElement).style.flexGrow = String(node.sizes[index]);
    (splitter.nextElementSibling as HTMLElement).style.flexGrow = String(node.sizes[index + 1]);
    describe();
    // Two panes remember how they were shared out, for the next time one splits.
    if (node === paneLayout && node.sizes.length === 2) write("split-size", String(Math.round(node.sizes[0]!)));
    for (const pane of panes) if (pane.open) pane.editor.requestMeasure();
  };
  let pointer: number | null = null;
  splitter.onpointerdown = (e) => {
    pointer = e.pointerId;
    splitter.setPointerCapture(e.pointerId);
    splitter.classList.add("dragging");
    e.preventDefault();
  };
  splitter.onpointermove = (e) => {
    if (pointer !== e.pointerId) return;
    const before = (splitter.previousElementSibling as HTMLElement).getBoundingClientRect();
    const after = (splitter.nextElementSibling as HTMLElement).getBoundingClientRect();
    const across = splitter.getAttribute("aria-orientation") === "vertical";
    const length = across ? before.width + after.width : before.height + after.height;
    const gap = across ? after.left - before.right : after.top - before.bottom;
    const position = (across ? e.clientX - before.left : e.clientY - before.top) - gap / 2;
    if (length <= 0) return;
    const pair = node.sizes[index]! + node.sizes[index + 1]!;
    const share = (position / length) * pair;
    // The divider snaps to a third or the half of its split within 12px of one, and says
    // which; Alt drags freely.
    const { share: snapped, snap } = e.altKey
      ? { share, snap: null }
      : snapShare(node.sizes, index, share, (SNAP_REACH * pair) / length);
    showSnap(snap);
    resize(snapped);
  };
  const showSnap = (snap: string | null) => {
    splitter.classList.toggle("snapped", snap !== null);
    grip.textContent = snap ?? (node.split === "row" ? "⋮" : "⋯");
  };
  splitter.onpointerup = splitter.onpointercancel = () => {
    pointer = null;
    splitter.classList.remove("dragging");
    showSnap(null);
  };
  splitter.ondblclick = () => resize((node.sizes[index]! + node.sizes[index + 1]!) / 2);
  splitter.onkeydown = (e) => {
    if (["ArrowLeft", "ArrowUp", "ArrowRight", "ArrowDown"].includes(e.key)) {
      e.preventDefault();
      resize(node.sizes[index]! + (["ArrowLeft", "ArrowUp"].includes(e.key) ? -5 : 5));
    }
  };
  return splitter;
}
function setupPanes() {
  matchMedia("(max-width:760px)").addEventListener("change", () =>
    document.querySelectorAll<HTMLElement>(".editor-splitter").forEach(orientSplitter));
  renderPaneLayout();
}
function draggableWindows() {
  for (const surface of document.querySelectorAll<HTMLElement>(
    ".tui-modal-content > .tui-window, .palette-shell",
  )) {
    const handle = surface.querySelector<HTMLElement>(
      "legend, .palette-title-row",
    );
    if (!handle) continue;
    handle.classList.add("drag-handle");
    let pointer: number | null = null,
      dx = 0,
      dy = 0;
    handle.onpointerdown = (e) => {
      if (e.button !== 0 || (e.target as Element).closest("button,input,a"))
        return;
      const rect = surface.getBoundingClientRect();
      dx = e.clientX - rect.left;
      dy = e.clientY - rect.top;
      pointer = e.pointerId;
      surface.style.position = "fixed";
      surface.style.width = `${rect.width}px`;
      surface.style.left = `${rect.left}px`;
      surface.style.top = `${rect.top}px`;
      surface.style.margin = "0";
      handle.setPointerCapture(pointer);
      e.preventDefault();
    };
    handle.onpointermove = (e) => {
      if (pointer !== e.pointerId) return;
      surface.style.left = `${Math.max(4, Math.min(innerWidth - surface.offsetWidth - 4, e.clientX - dx))}px`;
      surface.style.top = `${Math.max(4, Math.min(innerHeight - surface.offsetHeight - 4, e.clientY - dy))}px`;
    };
    handle.onpointerup = handle.onpointercancel = () => (pointer = null);
    handle.ondblclick = () => surface.removeAttribute("style");
  }
}
// Right-click menus offer what can be done to the thing clicked: a note or folder in the
// Library, the Library itself, a heading in the Outline, a pane, or the text of a note.
// Each command is one the app already has; the Library's show the key that does the same.
/** A note's commands, from its row in the Library; `next` is the row to select once it goes. */
function noteMenu(path: string, next: string | undefined): MenuEntry[] {
  const archived = under(path, ARCHIVE_ROOT);
  return [
    { label: "Open", shortcut: "Enter", run: () => openNote(path) },
    { label: "Open beside", shortcut: "s", run: () => showBeside(path) },
    "-",
    { label: "New note here", shortcut: "n", run: () => openForm(false, folderOf(path)) },
    "-",
    { label: "Rename…", shortcut: "r", run: () => showThen(path, () => openForm(true)) },
    { label: "Move to…", shortcut: "m", run: () => openMove(path, false) },
    !archived && {
      label: settings().pinned.includes(path) ? "Unpin" : "Pin",
      shortcut: "p",
      run: () => run(() => togglePin(path)),
    },
    { label: archived ? "Unarchive" : "Archive", shortcut: "x", run: () => run(() => archiveFromLibrary(path, next)) },
    "-",
    // As [[ writes it, so it opens this note and no other of the same title.
    !archived && { label: "Copy link", run: () => run(() => copyText(`[[${noteStem(path)}]]`, "Link")) },
    "-",
    {
      label: "Delete…",
      shortcut: "Del",
      danger: true,
      run: () => run(async () => {
        if (!(await deleteNote(path)) || next === undefined) return;
        libraryTreeSelection = next;
        if (libraryTreeVisible) renderLibraryTree();
      }),
    },
  ];
}
/** A folder's commands, or the vault root's when `folder` is "". */
function folderMenu(folder: string, row?: FileTreeRow): MenuEntry[] {
  const key = encryptedFolderFor(folder);
  const state = encryption(folder, true);
  // Locking and unlocking go by the encrypted folder at the top, whichever folder in it was clicked.
  const named = key === folder ? "" : ` “${key}”`;
  return [
    { label: "New note here", shortcut: "n", run: () => openForm(false, folder) },
    { label: "New folder here…", shortcut: "⇧N", run: () => openFolderFormIn(folder) },
    // An encrypted folder can't hold another.
    key === null && { label: "New encrypted folder here…", run: () => openFolderFormIn(folder, true) },
    "-",
    row && { label: row.expanded ? "Collapse" : "Expand", shortcut: row.expanded ? "←" : "→", run: () => toggleLibraryTreeFolder(folder) },
    "-",
    !!folder && { label: "Edit folder…", shortcut: "r", run: () => openFolderEdit(folder) },
    !!folder && { label: "Move to…", shortcut: "m", run: () => openMove(folder, true) },
    state === "unlocked" && { label: `Lock${named}`, run: () => run(() => lockFolders([key!])) },
    state === "locked" && { label: `Unlock${named}…`, run: () => void requestUnlock(key!) },
    "-",
    !!folder && {
      label: "Delete folder…",
      shortcut: "Del",
      danger: true,
      run: () => run(async () => {
        if (await deleteFolder(folder)) libraryTreeSelection = folderOf(folder);
        if (libraryTreeVisible) renderLibraryTree();
      }),
    },
  ];
}
/** The Library's own commands, from anywhere in it that isn't a note or folder. */
function libraryMenu(): MenuEntry[] {
  // New things go in the folder on show: the root of the tree, or the note list's folder.
  const filter = el<HTMLSelectElement>("library-folder").value;
  const folder = libraryTreeVisible || filter === "." ? "" : filter;
  return [
    { label: "New note", run: () => openForm(false, folder) },
    { label: "New folder…", run: () => openFolderFormIn(folder) },
    encryptedFolderFor(folder) === null && { label: "New encrypted folder…", run: () => openFolderFormIn(folder, true) },
    "-",
    { label: "Find in Library", shortcut: "/", run: () => { setLibraryFind(true); el("library-search").focus(); } },
    { label: libraryTreeVisible ? "Show note list" : "Show folder tree", run: () => showLibraryTree(!libraryTreeVisible) },
    libraryTreeVisible && libraryOpenFolders.size > 1 && {
      label: "Collapse all folders",
      run: () => {
        libraryOpenFolders.clear();
        libraryOpenFolders.add("");
        libraryTreeSelection = "";
        renderLibraryTree();
      },
    },
  ];
}
/** Where a run of lines starts and ends in `source`, 1-based and inclusive. */
function lineRange(source: string, from: number, to: number): { from: number; to: number } {
  const lines = source.split("\n");
  const start = lines.slice(0, from - 1).reduce((offset, line) => offset + line.length + 1, 0);
  return { from: start, to: start + lines.slice(from - 1, to).join("\n").length };
}
/** A heading's commands, from its row in the Outline: its section is the heading and all under it. */
function outlineMenu(line: number): MenuEntry[] {
  const editor = activeEditor();
  const source = editor.getValue();
  const headings = outlineHeadings(source);
  const heading = headings.find((item) => item.line === line);
  if (!heading) return [];
  const section = headingSection(source, headings, heading);
  const range = lineRange(source, section.from, section.to);
  // The note may change while the menu is open, so a move is worked out when it is chosen.
  const shift = (by: 1 | -1) => () => {
    const now = editor.getValue();
    const all = outlineHeadings(now);
    const at = all.find((item) => item.line === line && item.text === heading.text);
    const changes = at && shiftSection(now, all, at, by);
    if (changes) editor.change(changes);
  };
  const editable = !editor.isReadOnly();
  const promote = shiftSection(source, headings, heading, -1),
    demote = shiftSection(source, headings, heading, 1);
  return [
    { label: "Go to heading", run: () => { editor.goToLine(line); editor.focus(); } },
    { label: "Select section", run: () => editor.select(range.from, range.to) },
    { label: "Copy section", run: () => run(() => copyText(source.slice(range.from, range.to), "Section")) },
    "-",
    editable && { label: promote ? `Promote to H${heading.level - 1}` : "Promote", disabled: !promote, run: shift(-1) },
    editable && { label: demote ? `Demote to H${heading.level + 1}` : "Demote", disabled: !demote, run: shift(1) },
  ];
}
/** A pane's commands, from its title or, when it is empty, from inside it. */
function paneMenu(index: number): MenuEntry[] {
  const path = panes[index]!.path;
  // A blank note isn't kept, or in the Library, until it has text.
  const stored = !!path && !isBlankNote(path);
  const inPane = (action: () => void) => () => {
    activate(index);
    action();
  };
  const fits = (direction: SplitDirection) => splitLayout(paneLayout, index, -1, direction) !== null;
  return [
    !!path && { label: "Rename, move, pin…", run: () => openNoteSettings(index) },
    stored && { label: "Move to…", run: inPane(() => openMove(path, false)) },
    stored && { label: "Show in Library", shortcut: "⌘⇧L", run: inPane(focusLibrary) },
    "-",
    stored && !under(path, ARCHIVE_ROOT) && {
      label: "Copy link",
      run: () => run(() => copyText(`[[${noteStem(path)}]]`, "Link")),
    },
    !!path && { label: "Copy Markdown", run: () => run(() => copyText(textOf(path), "Markdown")) },
    !!path && { label: "Export Markdown…", run: inPane(exportDraft) },
    !path && { label: "Open note…", shortcut: "⌘K", run: () => openPalette(index) },
    "-",
    { label: "Split right", disabled: !fits("row"), run: () => splitPane("row", index) },
    { label: "Split down", disabled: !fits("column"), run: () => splitPane("column", index) },
    openPanes().length > 1 && { label: "Close pane", run: () => closePane(index) },
    "-",
    stored && { label: "Delete…", danger: true, run: inPane(() => run(() => deleteNote(path))) },
  ];
}
/** Pastes what is on the clipboard as ⌘V would, images too; the browser asks the first time. */
async function pasteInto(editor: MarkdownEditor, plain: boolean) {
  try {
    if (plain || typeof navigator.clipboard.read !== "function") {
      editor.paste(await navigator.clipboard.readText(), plain);
      return;
    }
    const items = await navigator.clipboard.read();
    const images: File[] = [];
    for (const item of items) {
      const type = item.types.find((type) => /^image\/(png|jpeg|gif|webp)$/.test(type));
      if (type) images.push(new File([await item.getType(type)], `image.${type.slice(6).replace("jpeg", "jpg")}`, { type }));
    }
    if (images.length) editor.pasteImages(images);
    else {
      const text = items.find((item) => item.types.includes("text/plain"));
      if (text) editor.paste(await (await text.getType("text/plain")).text());
    }
  } catch {
    throw new Error("The browser didn't let TurboNotes read the clipboard. Press ⌘V to paste.");
  }
}
/** Copies the selection, and with `cut` takes it out as well. */
async function copySelection(editor: MarkdownEditor, cut: boolean) {
  const selection = editor.getSelection();
  if (!selection) return;
  await navigator.clipboard.writeText(selection.text);
  if (cut) editor.change([{ from: selection.from, to: selection.to, insert: "" }]);
}
/**
 * The commands for the text of a note, or of the Guide when `index` is null: the link
 * right-clicked, the clipboard, and formatting. `link` is the link's Markdown, if any.
 */
function editorMenu(index: number | null, link: string | undefined, at: { x: number; y: number } | null): MenuEntry[] {
  const editor = index === null ? guideEditor! : panes[index]!.editor;
  const path = index === null ? null : panes[index]!.path;
  const editable = !editor.isReadOnly();
  const selected = editor.getSelection() !== null;
  const target = link ? parseEditorLink(link, location.href) : null;
  const linkedTitle = target?.kind === "wiki" ? target.title : target?.kind === "note" ? titleOf(target.path) : "";
  const format = (name: MarkdownFormat) => () => editor.formatSelection(name);
  return [
    ...spellingMenu(editor, editor.spellingAt(at ?? undefined)),
    target?.kind === "url" && { label: "Open link", run: () => window.open(target.url, "_blank", "noopener,noreferrer") },
    target?.kind === "url" && { label: "Copy link address", run: () => run(() => copyText(target.url, "Link")) },
    target?.kind === "heading" && { label: "Go to heading", run: () => goToHeading(editor, target.heading) },
    !!linkedTitle && index !== null && { label: `Open “${linkedTitle}”`, run: () => activateLink(link!, index) },
    !!linkedTitle && index !== null && { label: "Open beside", run: () => activateLink(link!, index, true) },
    "-",
    editable && { label: "Cut", shortcut: "⌘X", disabled: !selected, run: () => run(() => copySelection(editor, true)) },
    { label: "Copy", shortcut: "⌘C", disabled: !selected, run: () => run(() => copySelection(editor, false)) },
    editable && { label: "Paste", shortcut: "⌘V", run: () => run(() => pasteInto(editor, false)) },
    editable && { label: "Paste as plain text", shortcut: "⌘⇧V", run: () => run(() => pasteInto(editor, true)) },
    { label: "Select all", shortcut: "⌘A", run: () => editor.select(0, editor.getValue().length) },
    "-",
    editable && {
      label: "Format",
      submenu: [
        { label: "Bold", shortcut: "⌘B", run: format("bold") },
        { label: "Italic", shortcut: "⌘I", run: format("italic") },
        { label: "Inline code", shortcut: "`", run: format("inline-code") },
        { label: "Link", shortcut: "⌘⇧K", run: format("link") },
      ],
    },
    editable && {
      label: "Paragraph",
      submenu: [
        { label: "Heading 1", run: format("heading-1") },
        { label: "Heading 2", run: format("heading-2") },
        { label: "Heading 3", run: format("heading-3") },
        "-",
        { label: "Bullet list", run: format("bullet-list") },
        { label: "Numbered list", run: format("numbered-list") },
        { label: "Task list", run: format("task-list") },
        "-",
        { label: "Quote", run: format("quote") },
      ],
    },
    editable && { label: "Insert table", run: () => editor.insertTable() },
    editable && { label: "Insert image…", run: () => insertImage(editor) },
    "-",
    // Ask AI is off in encrypted notes, so it isn't offered there.
    editable && index !== null && !!path && !isEncryptedFile(path) && {
      label: "Ask AI…",
      run: () => {
        activate(index);
        openAi();
      },
    },
  ];
}
/**
 * Opens the menu for what `target` is, at `at`, or from the keyboard below `target` (or the
 * cursor, in a note) when `at` is null. False leaves the browser's own menu to show, as it
 * does in a field, over an image or a table in Live Preview, and on a touch screen in a note.
 */
function openContextMenu(target: Element, at: { x: number; y: number } | null, touch = false): boolean {
  if (target.closest("input, textarea, select, .tui-modal, .editor-command-menu")) return false;
  const keyboard = at === null;
  const focused = document.activeElement instanceof HTMLElement ? document.activeElement : null;
  let entries: MenuEntry[] | null = null;
  let title: string | undefined;
  let row: HTMLElement | null = null;
  let returnFocus = () => {
    if (focused?.isConnected) focused.focus({ preventScroll: true });
  };
  const paneOf = (element: Element) => {
    const id = element.closest<HTMLElement>(".editor-pane")?.dataset.paneId;
    const index = panes.findIndex((pane) => pane.id === id);
    return index === -1 ? null : index;
  };
  const treeRow = target.closest<HTMLElement>("#library-tree .file-tree-row");
  const noteRow = target.closest<HTMLElement>("#library-panel .note-row[data-path]");
  const folderHeading = target.closest<HTMLElement>("#note-list .folder-heading[data-folder]");
  const outlineRow = target.closest<HTMLElement>("#outline-list .outline-row[data-line]");
  const paneHead = target.closest(".pane-filename-button, .pane-empty");
  const text = target.closest(".editor-area");
  if (treeRow) {
    const index = Number(treeRow.id.replace("library-tree-row-", ""));
    const item = libraryTreeRows[index];
    if (!item) return false;
    const next = (libraryTreeRows[index + 1] ?? libraryTreeRows[index - 1])?.path;
    entries = item.folder ? folderMenu(item.path, item) : noteMenu(item.path, next);
    title = item.folder ? (item.path ? `${item.path}/` : "/") : titleOf(item.path);
    row = treeRow;
    // The tree keeps the cursor itself, on the selected row.
    returnFocus = () => el("library-tree").focus({ preventScroll: true });
  } else if (noteRow) {
    const rows = [...noteRow.parentElement!.querySelectorAll<HTMLElement>(".note-row")];
    const at = rows.indexOf(noteRow);
    const path = noteRow.dataset.path!;
    entries = noteMenu(path, (rows[at + 1] ?? rows[at - 1])?.dataset.path);
    title = titleOf(path);
    row = noteRow;
  } else if (folderHeading) {
    const folder = folderHeading.dataset.folder!;
    entries = folderMenu(folder);
    title = folder ? `${folder}/` : "/";
  } else if (target.closest("#library-panel")) {
    entries = libraryMenu();
    title = "Library";
  } else if (outlineRow) {
    const line = Number(outlineRow.dataset.line);
    entries = outlineMenu(line);
    title = outlineRow.getAttribute("aria-label")?.replace(/, heading (\d)$/, (_, level) => ` · H${level}`);
    row = outlineRow;
    returnFocus = () => {
      if (focused?.isConnected) focused.focus({ preventScroll: true });
      else activeEditor().focus();
    };
  } else if (paneHead) {
    const index = paneOf(paneHead);
    if (index === null) return false;
    activate(index);
    entries = paneMenu(index);
    title = panes[index]!.path ? titleOf(panes[index]!.path!) : "Empty pane";
  } else if (text) {
    // The browser's menu copies or saves an image, and a table cell has its own editing.
    if (touch || target.closest("img, .md-live-table")) return false;
    const index = paneOf(text);
    if (index === null && !text.closest("#guide-pane")) return false;
    const editor = index === null ? guideEditor : panes[index]!.editor;
    if (!editor) return false;
    entries = editorMenu(index, target.closest<HTMLElement>("[data-editor-link]")?.dataset.editorLink, at);
    returnFocus = () => editor.focus();
    if (!at) {
      const caret = editor.cursorCoords();
      if (caret) at = { x: caret.left, y: caret.bottom };
    }
  }
  if (!entries) return false;
  if (!at) {
    const box = (row ?? target).getBoundingClientRect();
    at = { x: box.left + 8, y: box.bottom };
  }
  closeMenus();
  row?.classList.add("context-target");
  showContextMenu(entries, {
    ...at,
    title,
    keyboard,
    returnFocus,
    onClose: () => row?.classList.remove("context-target"),
  });
  return true;
}
function bind() {
  const click = (id: string, action: () => void) =>
    el(id).addEventListener("click", action);
  for (const menu of document.querySelectorAll<HTMLElement>(
    ".app-nav .tui-dropdown",
  )) {
    const label = menu.querySelector<HTMLElement>(".nav-menu-label")!;
    const content = menu.querySelector<HTMLElement>(".tui-dropdown-content")!;
    content.id = `${label.id}-content`;
    label.setAttribute("aria-controls", content.id);
    label.setAttribute("aria-haspopup", "menu");
    label.setAttribute("aria-expanded", "false");
    label.onclick = () => {
      if (label.id === "auth-label" && !api.session.user) {
        location.href = "/api/auth/github/start";
        return;
      }
      const open = menu.classList.contains("menu-open");
      closeMenus();
      menu.classList.toggle("menu-open", !open);
      label.setAttribute("aria-expanded", String(!open));
      if (!open) label.focus({ preventScroll: true });
    };
    label.onkeydown = (e) => {
      if (e.key === "ArrowDown") {
        e.preventDefault();
        closeMenus();
        menu.classList.add("menu-open");
        label.setAttribute("aria-expanded", "true");
        menu
          .querySelector<HTMLElement>(
            ".tui-dropdown-content button, .tui-dropdown-content a",
          )
          ?.focus();
      }
    };
    content.addEventListener("click", (e) => {
      if ((e.target as Element).closest("button, a")) closeMenus();
    });
  }
  document.addEventListener("pointerdown", (e) => {
    if (!(e.target as Element).closest(".tui-dropdown")) closeMenus();
  });
  // A right-click opens the app's menu for what was clicked. Shift-right-click keeps the
  // browser's, for its spelling suggestions and the like.
  document.addEventListener("contextmenu", (e) => {
    if (e.shiftKey || e.defaultPrevented || !(e.target instanceof Element) || inContextMenu(e.target)) return;
    const touch = "pointerType" in e && e.pointerType === "touch";
    if (openContextMenu(e.target, { x: e.clientX, y: e.clientY }, touch)) e.preventDefault();
  });
  // The menu key, or Shift+F10, opens it for what has the cursor: the selected row, or the note.
  document.addEventListener("keydown", (e) => {
    if (e.defaultPrevented || !(e.key === "ContextMenu" || (e.key === "F10" && e.shiftKey))) return;
    if (e.metaKey || e.ctrlKey || e.altKey) return;
    const focused = document.activeElement;
    const target = focused?.id === "library-tree" ? focused.querySelector('[aria-selected="true"]') : focused;
    if (target && openContextMenu(target, null)) e.preventDefault();
  });
  document
    .querySelectorAll<HTMLElement>("[data-close-modal]")
    .forEach((b) => (b.onclick = () => closeModal(b.dataset.closeModal!)));
  document
    .querySelectorAll<HTMLElement>("[data-open-modal]")
    .forEach((b) => (b.onclick = () => openModal(b.dataset.openModal!)));
  document.querySelectorAll<HTMLElement>("[data-set-theme]").forEach(
    (b) =>
      (b.onclick = () => {
        if (isThemeId(b.dataset.setTheme ?? null)) {
          pref.setItem(THEME_STORAGE_KEY, b.dataset.setTheme!);
          appearance();
        }
      }),
  );
  el<HTMLSelectElement>("typography-font").onchange = (e) => {
    const value = (e.target as HTMLSelectElement).value;
    if (isFontId(value)) {
      pref.setItem(FONT_STORAGE_KEY, value);
      appearance();
    }
  };
  el<HTMLSelectElement>("slash-date-format").onchange = (e) => {
    const value = (e.target as HTMLSelectElement).value;
    if (isDateFormat(value)) write("slash-date-format", value);
    renderDateTimeSettings();
  };
  el<HTMLSelectElement>("slash-time-format").onchange = (e) => {
    const value = (e.target as HTMLSelectElement).value;
    if (isTimeFormat(value)) write("slash-time-format", value);
    renderDateTimeSettings();
  };
  el<HTMLSelectElement>("code-word-wrap").onchange = (e) => {
    const value = (e.target as HTMLSelectElement).value;
    if (value !== "true" && value !== "false") return;
    codeBlockSettings = { ...codeBlockSettings, wordWrap: value === "true" };
    pref.setItem(CODE_WORD_WRAP_STORAGE_KEY, value);
    renderCodeBlockSettings();
  };
  el<HTMLSelectElement>("table-padding").onchange = (e) => {
    const value = (e.target as HTMLSelectElement).value;
    if (!isTablePadding(value)) return;
    tableSettings = { ...tableSettings, padding: value };
    pref.setItem(TABLE_PADDING_STORAGE_KEY, value);
    renderTableSettings();
  };
  el<HTMLSelectElement>("table-align").onchange = (e) => {
    const value = (e.target as HTMLSelectElement).value;
    if (!isTableAlign(value)) return;
    tableSettings = { ...tableSettings, align: value };
    pref.setItem(TABLE_ALIGN_STORAGE_KEY, value);
    renderTableSettings();
  };
  el<HTMLSelectElement>("table-position").onchange = (e) => {
    const value = (e.target as HTMLSelectElement).value;
    if (!isTablePosition(value)) return;
    tableSettings = { ...tableSettings, position: value };
    pref.setItem(TABLE_POSITION_STORAGE_KEY, value);
    renderTableSettings();
  };
  el<HTMLSelectElement>("table-width").onchange = (e) => {
    const value = (e.target as HTMLSelectElement).value;
    if (!isTableWidth(value)) return;
    tableSettings = { ...tableSettings, width: value };
    pref.setItem(TABLE_WIDTH_STORAGE_KEY, value);
    renderTableSettings();
  };
  el<HTMLSelectElement>("image-align").onchange = (e) => {
    const value = (e.target as HTMLSelectElement).value;
    if (!isImageAlign(value)) return;
    imageSettings = { ...imageSettings, align: value };
    pref.setItem(IMAGE_ALIGN_STORAGE_KEY, value);
    renderImageSettings();
  };
  el<HTMLSelectElement>("image-border").onchange = (e) => {
    const value = (e.target as HTMLSelectElement).value;
    if (!isImageBorder(value)) return;
    imageSettings = { ...imageSettings, border: value };
    pref.setItem(IMAGE_BORDER_STORAGE_KEY, value);
    renderImageSettings();
  };
  el<HTMLSelectElement>("mermaid-theme").onchange = (e) => {
    const value = (e.target as HTMLSelectElement).value;
    if (!isMermaidTheme(value)) return;
    mermaidSettings = { ...mermaidSettings, theme: value };
    pref.setItem(MERMAID_THEME_STORAGE_KEY, value);
    renderMermaidSettings();
  };
  el<HTMLSelectElement>("mermaid-border").onchange = (e) => {
    const value = (e.target as HTMLSelectElement).value;
    if (!isMermaidBorder(value)) return;
    mermaidSettings = { ...mermaidSettings, border: value };
    pref.setItem(MERMAID_BORDER_STORAGE_KEY, value);
    renderMermaidSettings();
  };
  el<HTMLSelectElement>("mermaid-align").onchange = (e) => {
    const value = (e.target as HTMLSelectElement).value;
    if (!isMermaidAlign(value)) return;
    mermaidSettings = { ...mermaidSettings, align: value };
    pref.setItem(MERMAID_ALIGN_STORAGE_KEY, value);
    renderMermaidSettings(false);
  };
  el<HTMLSelectElement>("mermaid-width").onchange = (e) => {
    const value = (e.target as HTMLSelectElement).value;
    if (!isMermaidWidth(value)) return;
    mermaidSettings = { ...mermaidSettings, width: value };
    pref.setItem(MERMAID_WIDTH_STORAGE_KEY, value);
    renderMermaidSettings(false);
  };
  document.querySelectorAll<HTMLElement>("[data-body-size-step]").forEach(
    (b) =>
      (b.onclick = () => {
        bodySize = normalizeMarkdownBodySize(
          bodySize + Number(b.dataset.bodySizeStep),
        );
        typography();
      }),
  );
  document.querySelectorAll<HTMLElement>("[data-heading-size-step]").forEach(
    (b) =>
      (b.onclick = () => {
        const level = Number(b.dataset.headingLevel);
        if (isMarkdownHeadingLevel(level)) {
          headingSizes = setMarkdownHeadingSize(
            headingSizes,
            level,
            headingSizes[level] + Number(b.dataset.headingSizeStep),
          );
          typography();
        }
      }),
  );
  click("typography-reset", () => {
    bodySize = DEFAULT_MARKDOWN_BODY_SIZE;
    headingSizes = defaultMarkdownHeadingSizes();
    pref.setItem(FONT_STORAGE_KEY, DEFAULT_FONT);
    appearance();
    typography();
  });
  click("new-note-button", () => openForm());
  click("open-note-button", openFileTree);
  click("library-tree-toggle", () => {
    showLibraryFolderPicker(false);
    showLibraryTree(!libraryTreeVisible);
  });
  click("library-new-folder", () => openFolderForm());
  el("library-tree").onkeydown = libraryTreeKeydown;
  click("save-note-button", () => run(() => saveNotes()));
  click("save-all-button", () => run(() => saveNotes(true)));
  click("open-selected-note", openSelectedNote);
  el("file-tree").onkeydown = treeKeydown;
  click("status-find", () => openPalette());
  click("status-panel", openCommands);
  click("status-new", () => openForm());
  click("status-checkpoint", openCheckpoint);
  click("note-settings-button", () => openForm(true));
  click("mobile-note-settings", () => openForm(true));
  click("delete-note-button", () => run(() => deleteNote()));
  click("export-note-button", exportDraft);
  click("copy-draft", () => run(copyDraft));
  click("download-draft", exportDraft);
  click("retry-sync", () => run(() => sync.refresh()));
  click("retry-save", () =>
    run(async () => {
      for (const d of store.drafts.values())
        if (d.status === "storage-error") d.status = "unsaved";
      await store.flush();
      await store.changed();
    }),
  );
  click("save-status", () => {
    if (store.state?.recovery.some((r) => !r.resolved)) openConflict();
    else if (store.storageError) run(() => store.flush());
    else if (store.state?.attempts.some((a) => a.state === "submitted"))
      run(() => sync.checkpoint("Recover pending checkpoint"));
  });
  click("new-folder-button", () => openFolderForm());
  click("edit-folder-button", () => openFolderEdit());
  el<HTMLSelectElement>("folder-edit-source").onchange = (event) =>
    showFolderEdit((event.target as HTMLSelectElement).value);
  el("folder-edit-form").onsubmit = (event) => {
    event.preventDefault();
    void submitFolderEdit().catch((error) => {
      el("folder-edit-error").textContent = error.message;
    });
  };
  click("folder-delete-button", () => {
    el("folder-edit-error").textContent = "";
    void deleteFolder(editingFolder)
      .then((deleted) => deleted && closeModal("folder-edit-modal"))
      .catch((error) => {
        el("folder-edit-error").textContent = error.message;
      });
  });
  for (const picker of ["folder-icon-picker", "folder-edit-icon-picker"]) {
    el(picker).onkeydown = (event) => iconPickerKeydown(picker, event);
    el(`${picker}-emoji`).oninput = () => typeEmoji(picker);
    click(`${picker}-upload`, () => {
      iconUploadPicker = picker;
      el("folder-icon-upload").click();
    });
  }
  el("folder-icon-upload").onchange = () => {
    if (!iconUploadPicker) return;
    void uploadIcon().catch(showIconError(iconUploadPicker));
  };
  el<HTMLInputElement>("folder-encrypt").onchange = (event) => {
    showFolderEncryption((event.target as HTMLInputElement).checked);
    if ((event.target as HTMLInputElement).checked) el("folder-passphrase").focus();
  };
  el("unlock-form").onsubmit = (event) => {
    event.preventDefault();
    void submitUnlock().catch((error) => {
      el("unlock-error").textContent = error.message;
      el<HTMLInputElement>("unlock-passphrase").select();
    });
  };
  click("encryption-settings-button", () => {
    renderEncryptionSettings();
    openModal("encryption-settings-modal");
  });
  click("ai-settings-button", openAiSettings);
  el("ai-key-form").addEventListener("submit", (event) => {
    event.preventDefault();
    void saveAiKey();
  });
  click("ai-key-remove", () => void removeAiKey());
  const aiModelInput = el<HTMLInputElement>("ai-model");
  aiModelInput.addEventListener("focus", () => {
    // Every model at first; what's typed narrows them.
    aiModelInput.dataset.query = "";
    aiModelSelection = 0;
    aiModelInput.select();
    renderAiModelOptions("");
    void loadAiModels();
  });
  aiModelInput.addEventListener("input", () => {
    aiModelInput.dataset.query = aiModelInput.value;
    aiModelSelection = 0;
    renderAiModelOptions(aiModelInput.value);
  });
  aiModelInput.addEventListener("blur", commitAiModel);
  aiModelInput.addEventListener("keydown", aiModelKeydown);
  el<HTMLSelectElement>("ai-reasoning").replaceChildren(
    ...AI_REASONING.map((value) => new Option(AI_REASONING_LABELS[value], value)),
  );
  el<HTMLSelectElement>("ai-reasoning").onchange = (event) => {
    const value = (event.target as HTMLSelectElement).value;
    if (isAiReasoning(value)) write("ai-reasoning", value);
    renderAiSettings();
  };
  el<HTMLSelectElement>("ai-apply").onchange = (event) => {
    const value = (event.target as HTMLSelectElement).value;
    if (isAiApplyMode(value)) write("ai-apply", value);
    renderAiSettings();
  };
  el("ai-form").addEventListener("submit", (event) => {
    event.preventDefault();
    void askAi();
  });
  for (const id of ["ai-scope-selection", "ai-scope-cursor", "ai-scope-note"])
    el(id).addEventListener("change", () => {
      // An answer for one scope doesn't fit another.
      if (aiJob && !aiJob.stream) cancelAi();
      markAiScope();
      renderAi();
    });
  click("ai-model-button", openAiSettings);
  click("ai-stop", stopAi);
  click("ai-discard", () => closeModal("ai-modal"));
  click("ai-replace", () => applyAi(false));
  click("ai-below", () => applyAi(true));
  el("ai-modal").addEventListener("keydown", (event) => {
    if (event.key !== "Enter" || event.isComposing) return;
    // ⌘Enter replaces the text with the answer, ⇧Enter keeps it and inserts the answer.
    // Before there is an answer, ⇧Enter asks, as Enter does.
    const replace = event.metaKey || event.ctrlKey;
    if (!replace && !(event.shiftKey && !event.altKey && aiReady())) return;
    event.preventDefault();
    event.stopPropagation();
    applyAi(!replace);
  });
  el<HTMLSelectElement>("encryption-lock-minutes").onchange = (event) => {
    const value = (event.target as HTMLSelectElement).value;
    if (isLockMinutes(value)) pref.setItem(ENCRYPTION_LOCK_STORAGE_KEY, value);
    lockMinutes = loadLockMinutes(pref);
    renderEncryptionSettings();
  };
  click("lock-folders-button", () => run(() => lockFolders()));
  el<HTMLInputElement>("encryption-lock-on-blur").onchange = (event) => {
    pref.setItem(ENCRYPTION_BLUR_LOCK_STORAGE_KEY, String((event.target as HTMLInputElement).checked));
    lockOnBlur = loadLockOnBlur(pref);
    renderEncryptionSettings();
  };
  // Focus moving to another window, app, or the browser's own controls. An element's
  // blur never bubbles here, but a node as target is never the window itself.
  window.addEventListener("blur", (event) => {
    if (!(event.target instanceof Node)) lockOnFocusLoss();
  });
  for (const type of ["keydown", "pointerdown", "wheel"])
    document.addEventListener(type, () => (lastActivity = Date.now()), { capture: true, passive: true });
  el("folder-form").onsubmit = (event) => {
    event.preventDefault();
    void submitFolder().catch((error) => {
      el("folder-error").textContent = error.message;
    });
  };
  click("recovery-button", openRecovery);
  click("open-guide-button", openGuide);
  click("close-guide-button", () => closeGuide());
  click("guide-mode-button", toggleGuideMode);
  el<HTMLSelectElement>("note-folder-input").onchange = () => {
    updateNewFolderField();
    if (!el("note-new-folder-field").hidden)
      el("note-new-folder-input").focus();
  };
  el("note-form").onsubmit = (e) => {
    e.preventDefault();
    void submitForm().catch((e) => {
      el("note-form-error").textContent = e.message;
    });
  };
  click("archive-note-button", () => run(archiveNote));
  click("checkpoint-button", openCheckpoint);
  click("auto-checkpoint-settings", openAutoCheckpointSettings);
  el("auto-checkpoint-form").onsubmit = (e) => {
    e.preventDefault();
    el("auto-checkpoint-error").textContent = "";
    void store.database
      .setAutoCheckpointMinutes(
        el<HTMLInputElement>("auto-checkpoint-minutes").valueAsNumber,
      )
      .then(() => store.changed())
      .then(() => {
        closeModal("auto-checkpoint-modal");
        toast("Auto checkpoint settings saved.");
      })
      .catch((e) => {
        el("auto-checkpoint-error").textContent = e.message;
      });
  };
  el("figlet-form").onsubmit = (e) => {
    e.preventDefault();
    submitFiglet();
  };
  el("figlet-input").oninput = () => {
    el("figlet-error").textContent = "";
    renderFiglet();
  };
  // ↑ and ↓ pick a font without leaving the text.
  el("figlet-input").onkeydown = (e) => {
    if (e.key !== "ArrowDown" && e.key !== "ArrowUp") return;
    e.preventDefault();
    stepFigletFont(e.key === "ArrowDown" ? 1 : -1);
  };
  el("image-form").onsubmit = (e) => {
    e.preventDefault();
    submitImage();
  };
  el("checkpoint-form").onsubmit = (e) => {
    e.preventDefault();
    void sync
      .checkpoint(el<HTMLInputElement>("checkpoint-label").value)
      .then(() => {
        closeModal("checkpoint-modal");
        toast("Checkpoint confirmed.");
      })
      .catch((e) => {
        el("checkpoint-error").textContent = e.message;
      });
  };
  document
    .querySelectorAll<HTMLElement>("[data-sidebar-toggle]")
    .forEach((b) => (b.onclick = toggleSidebar));
  click("library-close", () => setSidebar(false));
  for (const widget of SIDEBAR_WIDGETS) {
    click(widgetToggleId(widget.id), () => toggleWidget(widget.id));
    click(widgetCloseId(widget.id), () => setWidget(widget.id, false));
    const handle = el(widgetHandleId(widget.id));
    handle.onclick = () => {
      if (widgetDragEnded) {
        widgetDragEnded = false;
        return;
      }
      setWidgetFolded(widget.id, !widgetFolded.has(widget.id));
    };
    handle.addEventListener("pointerdown", (e) =>
      startWidgetDrag(widget.id, e as PointerEvent),
    );
    handle.onkeydown = (e) => {
      if (!e.altKey || (e.key !== "ArrowUp" && e.key !== "ArrowDown")) return;
      e.preventDefault();
      nudgeWidget(widget.id, e.key === "ArrowUp" ? -1 : 1);
    };
  }
  el("timer-presets").replaceChildren(
    ...TIMER_PRESETS.map((minutes) => {
      const preset = document.createElement("button");
      preset.type = "button";
      preset.className = "timer-preset";
      preset.dataset.minutes = String(minutes);
      preset.textContent = `[${String(minutes).padStart(2, "0")}]`;
      preset.setAttribute("aria-label", `${minutes} minutes`);
      preset.setAttribute("aria-pressed", String(minutes === timerMinutes));
      preset.onclick = () => {
        timerMinutes = minutes;
        resetTimer();
      };
      return preset;
    }),
  );
  click("timer-run", () => (timerEndsAt === null ? startTimer() : pauseTimer()));
  click("timer-reset", resetTimer);
  click("calendar-previous", () => moveCalendar(-1));
  click("calendar-next", () => moveCalendar(1));
  click("calendar-month", showThisMonth);
  el("calendar-grid").onkeydown = calendarKeydown;
  el("calculator-form").onsubmit = (e) => {
    e.preventDefault();
    submitCalculation();
  };
  el("calculator-input").oninput = showCalculation;
  el("calculator-input").onkeydown = (e) => {
    // Shift+Enter works the sum out and takes the answer with it.
    if (e.key !== "Enter" || !e.shiftKey) return;
    e.preventDefault();
    if (el<HTMLInputElement>("calculator-input").value.trim()) submitCalculation();
    copyAnswer();
  };
  click("calculator-answer", copyAnswer);
  click("revisions-toggle", toggleHistory);
  click("revisions-close", () => setHistory(false));
  click("backlinks-toggle", () => {
    const visible = !!el("backlinks-panel").hidden;
    el("backlinks-panel").hidden = !visible;
    screen.classList.toggle("backlinks-visible", visible);
    el("backlinks-toggle").setAttribute("aria-pressed", String(visible));
    write("backlinks", String(visible));
  });
  click("library-find-toggle", () => {
    const visible = !!el("library-search-controls").hidden;
    setLibraryFind(visible);
    if (visible) el("library-search").focus();
    else {
      el<HTMLInputElement>("library-search").value = "";
      searchOpenFolders.library = new Set();
      renderLibrary();
    }
  });
  el("library-search").oninput = () => {
    const query = el<HTMLInputElement>("library-search").value;
    searchOpenFolders.library = query.trim() ? foldersAbove(sortedPaths(query)) : new Set();
    renderLibrary();
  };
  click("library-folder-toggle", () => {
    if (libraryTreeVisible) showLibraryTree(false);
    showLibraryFolderPicker(el("library-folder").hidden === true);
  });
  el("library-folder").onchange = () => {
    showLibraryFolderPicker(false);
    renderLibrary();
  };
  el("library-folder").onkeydown = (e) => {
    if (e.key !== "Escape") return;
    e.preventDefault();
    e.stopPropagation();
    showLibraryFolderPicker(false);
    el("library-folder-toggle").focus();
  };
  document.addEventListener("pointerdown", (e) => {
    if (!el("library-folder-controls").contains(e.target as Node))
      showLibraryFolderPicker(false);
  });
  el("palette-folder").onchange = () => {
    selection = 0;
    renderPalette();
  };
  const arrowThroughRows = (id: string) => {
    el(id).onkeydown = (e) => {
      if (e.key !== "ArrowDown" && e.key !== "ArrowUp") return;
      const rows = [...el(id).querySelectorAll<HTMLButtonElement>("button")];
      const index = rows.indexOf(document.activeElement as HTMLButtonElement);
      rows[
        Math.max(
          0,
          Math.min(rows.length - 1, index + (e.key === "ArrowDown" ? 1 : -1)),
        )
      ]?.focus();
      e.preventDefault();
    };
  };
  // The note list answers the Library's keys too; arrowing to a note shows it.
  el("note-list").onkeydown = (e) => {
    const rows = [...el("note-list").querySelectorAll<HTMLElement>(".note-row")];
    const index = rows.indexOf(document.activeElement as HTMLElement);
    const path = rows[index]?.dataset.path;
    if (!path) return;
    if (libraryShortcut(e, path, false, (rows[index + 1] ?? rows[index - 1])?.dataset.path)) return;
    const step = { ArrowDown: 1, j: 1, ArrowUp: -1, k: -1 }[e.key];
    if (step === undefined || e.metaKey || e.ctrlKey || e.altKey) return;
    e.preventDefault();
    const next = rows[Math.max(0, Math.min(rows.length - 1, index + step))]!.dataset.path!;
    previewNote(next);
    // Showing it draws the list again, so the cursor goes back to its row.
    focusLibraryRow(next);
  };
  arrowThroughRows("outline-list");
  click("editor-mode-toggle", toggleMode);
  click("preview-mode-toggle", togglePreviewMode);
  click("split-view-toggle", () => toggleSplit());
  click("line-numbers-button", toggleLines);
  click("spell-check-button", toggleSpellCheck);
  for (const [modal, input, results, render] of [
    ["palette-modal", "palette-search", "palette-results", renderPalette],
    [
      "command-palette-modal",
      "command-palette-search",
      "command-palette-results",
      renderCommands,
    ],
    ["move-modal", "move-search", "move-results", renderMoveTargets],
  ] as const) {
    el(input).oninput = () => {
      selection = 0;
      render();
    };
    el(modal).onkeydown = (e) => {
      const target = e.target as HTMLElement;
      if (target !== el(input) && !el(results).contains(target)) return;
      const rows = [
        ...el(results).querySelectorAll<HTMLButtonElement>("button"),
      ];
      const focusedRow = target.closest("button");
      if (focusedRow) selection = rows.indexOf(focusedRow);
      if (e.key === "ArrowDown" || e.key === "ArrowUp") {
        e.preventDefault();
        if (!rows.length) return;
        selection += e.key === "ArrowDown" ? 1 : -1;
        render();
        const selected = el(results).querySelector<HTMLButtonElement>(
          '[aria-selected="true"]',
        );
        if (focusedRow) selected?.focus();
        selected?.scrollIntoView({ block: "nearest" });
      } else if (e.key === "Enter") {
        e.preventDefault();
        if (modal === "palette-modal" && e.shiftKey && !e.metaKey && !e.ctrlKey) {
          run(createPaletteNote);
        } else if (modal === "palette-modal" && (e.metaKey || e.ctrlKey) && !e.shiftKey) {
          const path = rows[selection]?.dataset.path;
          if (path) {
            closeModal("palette-modal");
            openNoteInSplit(path, modalPane);
          }
        } else if (!e.metaKey && !e.ctrlKey && !e.shiftKey) {
          rows[selection]?.click();
        }
      }
    };
  }
  click("choose-repository", () => run(chooseRepository));
  click("refresh-repository", () =>
    run(async () => {
      await sync.refresh();
      if (store.state?.recovery.some((r) => !r.resolved)) openConflict();
    }),
  );
  click("check-checkpoint", () =>
    run(() => sync.checkpoint("Recover pending checkpoint")),
  );
  el<HTMLSelectElement>("repository-choice").onchange = () => {
    el<HTMLInputElement>("repository-branch").value =
      repositories[Number(el<HTMLSelectElement>("repository-choice").value)]
        ?.defaultBranch ?? "";
  };
  el("repository-form").onsubmit = (e) => {
    e.preventDefault();
    void connectRepository().catch(
      (e) => (el("repository-error").textContent = e.message),
    );
  };
  click("disconnect-repository", () =>
    run(async () => {
      await store.flush();
      if (store.state?.attempts.some((a) => a.state === "submitted"))
        throw new Error("Recover the pending checkpoint before disconnecting.");
      if (api.session.user) await api.request("/connections/current", "DELETE");
      await store.database.connect(null);
      await store.changed();
      await api.loadSession();
      await accountData?.load();
      render();
    }),
  );
  click("logout-button", () =>
    run(async () => {
      await accountData?.flush().catch(() => {});
      await api.request("/logout", "POST");
      await api.loadSession();
      await accountData?.load();
      render();
    }),
  );
  click("restore-revision-button", () =>
    run(async () => {
      if (!historical) return;
      const { path, file, pane } = historical;
      await store.flush(path);
      const doc = store.state?.documents.find((d) => d.path === path);
      await store.database.restore(
        path,
        file,
        pathRevision(store.state!, path),
      );
      await store.changed();
      closeModal("revision-modal");
      if (file.exists) openNote(path, pane);
    }),
  );
  click("resolve-conflicts", openConflict);
  for (const side of ["local", "remote", "merged"] as const)
    click(`conflict-${side}`, () => {
      void resolveConflict(side).catch(
        (e) => (el("conflict-error").textContent = e.message),
      );
    });
  // F3 is caught on the way down too, before an editor or the browser takes it to find the
  // next match. ⌘G still finds the next one, and ⇧F3 the one before.
  document.addEventListener(
    "keydown",
    (e) => {
      if (e.key !== "F3" || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.isComposing) return;
      if (document.querySelector(".tui-modal.active")) return;
      e.preventDefault();
      e.stopPropagation();
      if (!e.repeat) togglePreviewMode();
    },
    true,
  );
  // ⌘⇧L is caught on the way down, before an editor takes it to select every match.
  document.addEventListener(
    "keydown",
    (e) => {
      if (e.metaKey === e.ctrlKey || !e.shiftKey || e.altKey || e.isComposing || e.key.toLowerCase() !== "l") return;
      if (document.querySelector(".tui-modal.active")) return;
      e.preventDefault();
      e.stopPropagation();
      if (el("library-panel").contains(document.activeElement)) focusCurrentPane();
      else focusLibrary();
    },
    true,
  );
  // Esc stops an answer streaming into a note. It's caught before the editor, whose own Esc
  // would only collapse the selection the answer is replacing.
  document.addEventListener(
    "keydown",
    (e) => {
      if (e.key !== "Escape" || !aiJob?.stream || document.querySelector(".tui-modal.active")) return;
      e.preventDefault();
      e.stopPropagation();
      cancelAi();
      toast("Stopped Ask AI. The note was left as it was.");
    },
    true,
  );
  document.addEventListener("keydown", (e) => {
    if (e.defaultPrevented) return;
    const command = e.metaKey || e.ctrlKey,
      key = e.key.toLowerCase();
    if (command && e.shiftKey && key === "p") {
      e.preventDefault();
      openCommands();
    } else if (command && e.metaKey !== e.ctrlKey && e.shiftKey && key === "e"
      && !e.altKey && !e.isComposing && !document.querySelector(".tui-modal.active")) {
      e.preventDefault();
      if (!e.repeat) run(createUntitledNote);
    } else if (command && key === "k" && !e.shiftKey) {
      e.preventDefault();
      openPalette();
    } else if (command && e.metaKey !== e.ctrlKey
      && key === "enter" && e.shiftKey && !e.altKey && !e.isComposing
      && !document.querySelector(".tui-modal.active")) {
      e.preventDefault();
      if (!e.repeat) toggleSplit();
    } else if (e.key === "?" && !command && !e.altKey && !typingIn(e.target)
      && !document.querySelector(".tui-modal.active")) {
      e.preventDefault();
      openModal("shortcuts-modal");
    } else if (e.key === "F1") {
      e.preventDefault();
      openModal("md-modal");
    } else if (e.key === "F2") {
      e.preventDefault();
      toggleLines();
    } else if (e.key === "F4") {
      e.preventDefault();
      toggleMode();
    } else if (e.key === "Escape") {
      const modal = [
        ...document.querySelectorAll<HTMLElement>(".tui-modal.active"),
      ].at(-1);
      if (modal) closeModal(modal.id);
      closeMenus();
    } else if (e.key === "Tab") {
      const modal = [
        ...document.querySelectorAll<HTMLElement>(".tui-modal.active"),
      ].at(-1);
      if (!modal) return;
      const focus = [
        ...modal.querySelectorAll<HTMLElement>(
          'input,button,select,textarea,a,[tabindex="0"]',
        ),
      ].filter(
        (e) => !e.hasAttribute("disabled") && e.getClientRects().length > 0,
      );
      const first = focus[0],
        last = focus.at(-1);
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last?.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first?.focus();
      }
    }
  });
  document.addEventListener("visibilitychange", () => {
    if (document.hidden) {
      // A hidden tab can be discarded and later reloaded without a pagehide.
      saveView();
      lastForeground = Date.now();
      lockOnFocusLoss();
      void store.flush().catch(() => {});
    } else {
      // A background tab's timers are slowed; lock before showing anything stale.
      checkAutoLock();
      sync.connectivityChanged();
      run(async () => {
        await store.flush();
        if (
          Date.now() - lastForeground >= 300000 &&
          store.state?.vault.connection &&
          api.session.user
        )
          await sync.refresh();
        await sync.tick();
      });
    }
  });
  window.addEventListener("pagehide", () => {
    saveView();
    void store.flush().catch(() => {});
  });
  window.addEventListener("beforeunload", (e) => {
    if (store.unsaved) {
      e.preventDefault();
      e.returnValue = "";
    }
  });
  window.addEventListener("online", () => {
    sync.connectivityChanged();
    render();
    void accountData?.flush().catch(() => {});
    void sync.tick();
  });
  window.addEventListener("offline", render);
}
async function restoreAccount() {
  await api.loadSession();
  render();
  const user = api.session.user;
  const saved = api.session.connection;
  if (!user) return;
  try {
    if (saved) {
      await store.flush();
      const state = await store.database.load();
      const same = sameTarget(state.vault.connection, saved);
      const differentAccount =
        state.vault.userId !== undefined && state.vault.userId !== user.id;
      if (!same || differentAccount) {
        // Never publish another account/repository's local notes automatically.
        // Remote replacement keeps local versions in the recovery store.
        const hasPreviousRepository =
          !!state.vault.connection || differentAccount;
        await store.database.connect(saved, user.id);
        await store.changed();
        await sync.refresh(hasPreviousRepository ? "remote" : "merge");
      } else await sync.refresh();
      await store.database.mutate((s) => {
        s.vault.userId = user.id;
      });
      await store.changed();
      render();
    }
  } finally {
    await accountData?.load();
    render();
  }
}
async function initialize() {
  buildThemePicker();
  appearance();
  typography();
  renderTableSettings();
  renderImageSettings();
  renderMermaidSettings();
  renderDateTimeSettings();
  renderCodeBlockSettings();
  renderEncryptionSettings();
  bind();
  setupPanes();
  draggableWindows();
  document.body.classList.toggle("linenums-active", lineNumbers);
  document.body.classList.toggle("spellcheck-active", spellCheck);
  // The dictionary is big enough to wait until the page has settled.
  if (spellCheck) {
    if (typeof requestIdleCallback === "function") requestIdleCallback(() => void loadSpelling(), { timeout: 4000 });
    else setTimeout(() => void loadSpelling(), 1500);
  }
  renderPreviewToggle();
  setSidebar(
    !matchMedia("(max-width:760px)").matches &&
      read("library-collapsed") !== "true",
  );
  applyWidgetOrder();
  // The outline crosses between the sidebars as a window narrows to a phone's width and back.
  matchMedia("(max-width:760px)").addEventListener("change", applyWidgetOrder);
  for (const widget of SIDEBAR_WIDGETS) {
    setWidgetFolded(widget.id, widgetFolded.has(widget.id));
    setWidget(widget.id, read(widgetPreferenceKey(widget.id)) === "true");
  }
  el("backlinks-panel").hidden = read("backlinks") !== "true";
  setLibraryFind(read("library-find") === "true");
  showLibraryTree(libraryTreeVisible, false);
  store.addEventListener("change", render);
  sync.addEventListener("change", render);
  try {
    await store.reload();
  } catch (e) {
    store.storageError =
      e instanceof Error ? e.message : "Local storage is unavailable.";
  }
  openStartupNote();
  render();
  accountData = new AccountDataSync(store, api, pref, () => {
    appearance();
    bodySize = loadMarkdownBodySize(pref);
    headingSizes = loadMarkdownHeadingSizes(pref);
    tableSettings = loadTableSettings(pref);
    codeBlockSettings = loadCodeBlockSettings(pref);
    imageSettings = loadImageSettings(pref);
    mermaidSettings = loadMermaidSettings(pref);
    lockMinutes = loadLockMinutes(pref);
    lockOnBlur = loadLockOnBlur(pref);
    typography();
    renderTableSettings();
    renderCodeBlockSettings();
    renderImageSettings();
    renderMermaidSettings();
    renderDateTimeSettings();
    renderEncryptionSettings();
    renderAiSettings();
    render();
  });
  void restoreAccount()
    .then(() => {
      if (!document.hidden) void sync.tick();
    })
    .catch((e) => {
      if (store.state?.vault.connection || api.session.user) {
        sync.error = e.message;
        render();
      }
    });
  setInterval(() => {
    void sync.tick();
    void accountData?.flush().catch(() => {});
  }, 60000);
  setInterval(checkAutoLock, 15000);
  if ("serviceWorker" in navigator)
    void navigator.serviceWorker
      .register("/sw.js")
      .then(async () => {
        const registration = await navigator.serviceWorker.ready;
        if (store.state)
          registration.active?.postMessage({ type: "VAULT_READY" });
      })
      .catch(() => {});
}
void initialize();
